import type {
  LocalizedText,
  TimelineCoverageStatus,
  TimelineTarget,
} from "./data/schema";

export interface TimelineProgrammeSegment {
  storyId: string;
  stepId: string;
  sourceSteps?: Array<{ storyId: string; stepId: string }>;
  sceneId?: string;
  title?: LocalizedText;
  narrative?: {
    setup?: LocalizedText;
    development?: LocalizedText;
    consequence?: LocalizedText;
  };
  nodeIds?: string[];
  edgeIds?: string[];
  primaryFocus?: TimelineTarget;
  focusPath?: TimelineTarget[];
  chronologyNote?: LocalizedText;
}

export interface TimelineProgrammeSubEra {
  id: string;
  order: number;
  title: LocalizedText;
  summary: LocalizedText;
  chronologyMode: "sequence" | "parallel" | "variant";
  coverageStatus: TimelineCoverageStatus;
  coverageNote?: LocalizedText;
  segments: TimelineProgrammeSegment[];
}

export interface TimelineProgrammeEra {
  id: string;
  order: number;
  title: LocalizedText;
  summary: LocalizedText;
  subEras: TimelineProgrammeSubEra[];
}

export interface TimelineProgramme {
  id: string;
  title: LocalizedText;
  description: LocalizedText;
  chronologyDisclaimer: LocalizedText;
  eras: TimelineProgrammeEra[];
}

const allSteps = (
  storyId: string,
  stepIds: string[],
): TimelineProgrammeSegment[] => stepIds.map((stepId) => ({ storyId, stepId }));

const scene = (
  storyId: string,
  stepIds: string[],
  options: Omit<
    TimelineProgrammeSegment,
    "storyId" | "stepId" | "sourceSteps"
  > = {},
): TimelineProgrammeSegment => ({
  storyId,
  stepId: stepIds[0],
  sourceSteps: stepIds.map((stepId) => ({ storyId, stepId })),
  ...options,
});

const sceneGroup = (
  sourceSteps: Array<{ storyId: string; stepId: string }>,
  options: Omit<
    TimelineProgrammeSegment,
    "storyId" | "stepId" | "sourceSteps"
  > = {},
): TimelineProgrammeSegment => ({
  storyId: sourceSteps[0].storyId,
  stepId: sourceSteps[0].stepId,
  sourceSteps,
  ...options,
});

const sceneNarrative = (
  setupZh: string,
  developmentZh: string,
  consequenceZh: string,
  setupEn: string,
  developmentEn: string,
  consequenceEn: string,
): NonNullable<TimelineProgrammeSegment["narrative"]> => ({
  setup: { "zh-Hant": setupZh, en: setupEn },
  development: { "zh-Hant": developmentZh, en: developmentEn },
  consequence: { "zh-Hant": consequenceZh, en: consequenceEn },
});

const hjorvarthssonTimelineSegments: TimelineProgrammeSegment[] = [
  scene("norse-helgi-hjorvarthsson", ["sigrlin-threatened-homeland"], { title: { "zh-Hant": "西格琳失去故地", en: "Sigrlin Loses Her Homeland" }, narrative: sceneNarrative(
    "赫羅斯馬爾求娶西格琳不成，便攻打她父親斯瓦夫尼爾的土地。",
    "斯瓦夫尼爾被殺，斯瓦瓦蘭遭掠奪與焚毀；希約爾瓦德帶走西格琳。",
    "這場毀滅沒有立刻得到回應，卻留給西格琳的兒子一片失去的故地與未雪的舊仇。",
    "Hrothmar seeks Sigrlin’s hand and, when refused, attacks the land of her father Svafnir.",
    "Svafnir is killed and Svavaland is plundered and burned; Hjorvarth takes Sigrlin away.",
    "No answer comes at once, but the devastation leaves Sigrlin’s son a lost homeland and an old wrong still awaiting him.",
  ) }),
  scene("norse-helgi-hjorvarthsson", ["unnamed-son-receives-name"], { title: { "zh-Hant": "山上得到名字與劍", en: "A Name and a Sword on the Hill" }, narrative: sceneNarrative(
    "希約爾瓦德與西格琳的兒子已能出門，卻仍沒有名字。",
    "山上的騎馬女子叫他赫爾吉，又指給他一柄名劍；散文稱她為埃利米之女斯瓦娃。",
    "名字和武器在同一次相遇中來到少年身邊。他先被叫出名字，才被指向後來的戰事。",
    "The son of Hjorvarth and Sigrlin is old enough to travel, yet still has no name.",
    "A riding woman on a hill calls him Helgi and points him to a renowned sword; the prose calls her Svava, Eylimi’s daughter.",
    "Name and weapon arrive in one meeting. The boy is named before he is pointed toward the conflicts to come.",
  ) }),
  scene("norse-helgi-hjorvarthsson", ["hrothmar-avenged"], { title: { "zh-Hant": "赫羅斯馬爾被追討", en: "Helgi Goes against Hrothmar" }, narrative: sceneNarrative(
    "赫爾吉質問父親：母親的土地被毀、外祖父被殺，為何一直無人追討。",
    "希約爾瓦德交給他隨從，赫爾吉帶人出發；散文說他殺死赫羅斯馬爾。",
    "他把出生前留下的傷口變成自己的行動，但這次勝利沒有讓故事變得安穩。海上的敵意、誓言與另一場戰鬥仍在前面。",
    "Helgi asks why no one has answered for his mother’s ruined land and his grandfather’s death.",
    "Hjorvarth gives him followers, and Helgi goes out; the prose says he kills Hrothmar.",
    "He turns a wound from before his birth into his own act, but victory brings no lasting calm. Enmity at sea, vows, and another battle remain ahead.",
  ) }),
  scene("norse-helgi-hjorvarthsson", ["hati-falls-before-the-fleet"], { title: { "zh-Hant": "哈提死後的夜航", en: "Night Sailing after Hati’s Death" }, narrative: sceneNarrative(
    "赫爾吉殺死巨人哈提後，船隊夜泊峽灣。",
    "哈提的女兒赫里姆蓋爾德來到船邊，把父親的死與船隊的危險帶進夜裡。",
    "哈提已死，卻沒有從故事裡消失；女兒的聲音讓他的死亡變成下一場對峙，而不是一個結束。",
    "After Helgi kills the giant Hati, the fleet lies at a fjord by night.",
    "Hati’s daughter Hrimgerth comes to the ships and brings her father’s death into the danger of the night.",
    "Hati is dead but not gone from the story: through his daughter’s voice, his death becomes another confrontation rather than an ending.",
  ) }),
  scene("norse-helgi-hjorvarthsson", ["riders-over-the-sea"], { title: { "zh-Hant": "海上的守護者", en: "Protectors over the Sea" }, narrative: sceneNarrative(
    "赫里姆蓋爾德說，船隊能平安，是因為有女子與戴盔的騎者在海上護持。",
    "詩句在這裡殘缺，卻把她留到日出；天亮時，她被說成化為石頭。",
    "散文把斯瓦娃與赫爾吉的護持相連，但這段海上守護沒有把身分說得毫無疑問。夜色散去後，船隊留下，答案仍帶著缺口。",
    "Hrimgerth says the ships survive because a woman and helmed riders guard them over the sea.",
    "The verses are damaged here, yet keep her until sunrise, when she is said to turn to stone.",
    "The prose joins Svava to Helgi’s protection, but the sea-guard leaves identity uncertain. Night passes, the ships remain, and the answer still has a gap.",
  ) }),
  scene("norse-helgi-hjorvarthsson", ["helgi-and-svava-vows"], { title: { "zh-Hant": "赫爾吉與斯瓦娃立誓", en: "Helgi and Svava Make Their Vows" }, narrative: sceneNarrative(
    "赫爾吉向斯瓦娃求婚，兩人交換誓言，也彼此相愛。",
    "斯瓦娃仍留在父親家中，赫爾吉繼續出征；誓言讓兩人相連，卻沒有讓他們同住。",
    "這份承諾使後來的請求格外沉重。赫爾吉要斯瓦娃轉向赫辛時，她面對的是自己已經說出口的話。",
    "Helgi seeks Svava’s hand, and the two exchange vows and love one another.",
    "Svava remains at her father’s home while Helgi goes to war; the vow joins them without bringing them into one household.",
    "That promise makes the later request weighty. When Helgi asks Svava to turn to Hethin, she faces words she has already spoken.",
  ) }),
  scene("norse-helgi-hjorvarthsson", ["hethin-dangerous-vow"], { title: { "zh-Hant": "赫辛在祝酒中失言", en: "Hethin Speaks at the Toast" }, narrative: sceneNarrative(
    "國王的祝酒開始時，赫辛遇見一名可怕的女子；她預告代價將臨到他身上。",
    "赫辛在酒席上誓言要得到斯瓦娃。話一出口，他便悲痛地去向兄長赫爾吉坦白。",
    "這不是兩人的愛情，也不是已被證實的命運安排；它是一個傷人的要求，使赫辛立刻看見自己會撕裂什麼。",
    "At the king’s toast, Hethin meets a fearsome woman who foretells a cost.",
    "He vows at the feast that he will have Svava, then goes in grief to confess the words to his brother Helgi.",
    "It is neither love between the two nor a proven fate. It is a hurtful demand that makes Hethin immediately see what he may tear apart.",
  ) }),
  scene("norse-helgi-hjorvarthsson", ["battle-at-frekastein"], { title: { "zh-Hant": "弗雷卡斯坦的傷口", en: "The Wound at Frekastein" }, narrative: sceneNarrative(
    "赫羅斯馬爾的兒子阿爾夫指定弗雷卡斯坦作戰場，赫爾吉前去迎戰。",
    "散文沒有細寫每一擊，只說赫爾吉在那裡受了致命傷。",
    "阿爾夫是赫羅斯馬爾之子，詩卻沒有明說他的心意。父親的死與兒子指定的戰場相繼出現，接著赫爾吉走入最後的告別。",
    "Alf, Hrothmar’s son, sets Frekastein as the battle-place, and Helgi goes to meet him.",
    "The prose does not detail every blow. It says only that Helgi receives a mortal wound there.",
    "Alf is Hrothmar’s son, but the poem does not state his motive. The father’s death and the son’s battlefield appear in succession, then Helgi moves toward his final farewell.",
  ) }),
  scene("norse-helgi-hjorvarthsson", ["svava-answers-after-battle"], { title: { "zh-Hant": "斯瓦娃沒有改變誓言", en: "Svava Does Not Alter Her Vow" }, narrative: sceneNarrative(
    "受了致命傷的赫爾吉派西加爾召喚斯瓦娃，並要她為赫辛預備床鋪。",
    "斯瓦娃記起自己與赫爾吉的誓言，說她不會出於自己的意願轉向未具名望的人；赫辛則誓言為兄長復仇。",
    "詩沒有說赫辛是否完成復仇，也沒有把斯瓦娃的回答改寫成順從。最後的再生說法只是餘音，不能把這位赫爾吉和另一位同名英雄合成一人。",
    "Mortally wounded Helgi sends Sigar for Svava and asks her to prepare a bed for Hethin.",
    "Svava recalls her vow with Helgi and says she would not willingly turn to an unrenowned man; Hethin vows vengeance for his brother.",
    "The poem does not say whether Hethin fulfils that vow, nor does it turn Svava’s answer into consent. The final rebirth note is an echo, not a way to make this Helgi the same hero as another namesake.",
  ) }),
];

const hervorAwakeningTimelineSegments: TimelineProgrammeSegment[] = [
  scene("norse-hervor-awakening", ["sword-enters-house"], { title: { "zh-Hant": "提爾鋒進入阿恩格里姆家", en: "Tyrfing Enters Arngrim’s House" }, narrative: sceneNarrative(
    "R 本先讓提爾鋒進入阿恩格里姆的家。阿恩格里姆娶了埃芙拉，兩人有十二個兒子，安甘蒂爾是長子。",
    "這把劍不是懸空的傳說物件，而是在父母、兒子與日後繼承者之間傳遞。它先在家族裡得到位置，才被帶去戰場。",
    "故事的危險因此不是忽然從墓丘冒出。赫爾沃爾後來要求的劍，早已和父系、婚姻與長子的身分纏在一起。",
    "The R text first brings Tyrfing into Arngrim’s household. Arngrim marries Eyfura; they have twelve sons, and Angantyr is the eldest.",
    "The sword is not a floating legend but something carried among parents, sons, and a later inheritor. It gains a place in the family before it goes to war.",
    "The danger does not suddenly emerge from a mound. The sword Hervor later demands is already bound to paternal line, marriage, and the eldest son.",
  ) }),
  scene("norse-hervor-awakening", ["samsey-challenge"], { title: { "zh-Hant": "薩姆瑟島的約戰", en: "The Meeting Set for Samsey" }, narrative: sceneNarrative(
    "一場求婚衝突沒有留在王廳裡。赫亞爾馬爾與阿恩格里姆諸子被約到薩姆瑟島，讓勝負在海島上決定。",
    "約戰先把人從各自的家中拉開，也把提爾鋒推到下一個持有者手上。阿恩格里姆替安甘蒂爾備好武器，卻不能替兒子走進決鬥。",
    "島嶼從這裡開始承受整段故事：它不只是背景，而是比試、埋葬與後來父女相見的同一片土地。",
    "A competing marriage claim does not remain in the king’s hall. Hjalmar and Arngrim’s sons are set to meet on Samsey, where the outcome will be decided on an island.",
    "The arrangement draws people from their homes and puts Tyrfing into its next bearer’s hands. Arngrim arms Angantyr, but cannot enter the duel for him.",
    "From here the island bears the whole story: not merely a backdrop, but the ground of contest, burial, and a later meeting of father and daughter.",
  ) }),
  scene("norse-hervor-awakening", ["samsey-duel"], { title: { "zh-Hant": "赫亞爾馬爾與安甘蒂爾", en: "Hjalmar and Angantyr" }, narrative: sceneNarrative(
    "船隊抵達薩姆瑟島後，赫亞爾馬爾看見安甘蒂爾手中的提爾鋒。他堅持由自己面對這位對手。",
    "奧爾瓦爾・奧德沒有離開同伴，卻轉去接下其餘狂戰士。兩人並肩而來，到了戰鬥時卻各自承擔不同的人數與風險。",
    "決鬥沒有給任何人完整的勝利。安甘蒂爾倒下，赫亞爾馬爾也受了致命傷；劍在此時已經留下無法撤回的痕跡。",
    "When the ships reach Samsey, Hjalmar sees Tyrfing in Angantyr’s hand. He insists on facing that opponent himself.",
    "Orvar-Odd does not abandon his companion, but takes the remaining berserks. They arrive together, then carry different dangers in the fight.",
    "The duel gives no one a whole victory. Angantyr falls, and Hjalmar is mortally wounded; the sword has left a mark that cannot be withdrawn.",
  ) }),
  scene("norse-hervor-awakening", ["sword-leaves-survivor"], { title: { "zh-Hant": "戰後留下的劍傷", en: "The Wound Left after Battle" }, narrative: sceneNarrative(
    "奧爾瓦爾・奧德走到赫亞爾馬爾身邊時，戰場已經安靜下來。赫亞爾馬爾說安甘蒂爾的劍傷到自己心口，之後死去。",
    "安甘蒂爾和兄弟則連同武器被葬在薩姆瑟島。提爾鋒沒有隨著決鬥結束而消失，而是被放進父親的墓丘。",
    "這個埋葬把兩個時間隔開：當下是決鬥的死者，日後卻會有一個女兒專為這把劍回到同一座島。",
    "When Orvar-Odd reaches Hjalmar, the battlefield has grown quiet. Hjalmar says Angantyr’s sword has struck near his heart, and then he dies.",
    "Angantyr and his brothers are buried with their weapons on Samsey. Tyrfing does not vanish when the duel ends; it is placed inside the father’s mound.",
    "The burial separates two times: the immediate dead of battle and a daughter who will later return to the same island for this sword alone.",
  ) }),
  scene("norse-hervor-awakening", ["hervor-learns-father"], { title: { "zh-Hant": "赫爾沃爾得知父親", en: "Hervor Learns Her Father’s Name" }, narrative: sceneNarrative(
    "赫爾沃爾原本不知道自己的父親是誰。養父說出安甘蒂爾的名字，也告訴她他葬在薩姆瑟島。",
    "這個消息沒有讓她安靜留在原處。她決定出行，想見已死的父親，也想取得他留下的財物與劍。",
    "她的旅程由一個被揭開的家族名字開始。墓丘中的相見還未發生，但繼承已不再只是別人替她保管的故事。",
    "Hervor does not know who her father is. Her foster father names Angantyr and tells her he lies buried on Samsey.",
    "The news does not leave her still. She sets out to seek her dead father and the property and sword he has left behind.",
    "Her journey begins with a family name uncovered. The meeting in the mound has not happened, but inheritance is no longer a story held by others.",
  ) }),
  scene("norse-hervor-awakening", ["ship-waits-sunset"], { title: { "zh-Hant": "日落時登上薩姆瑟島", en: "Landing on Samsey at Sunset" }, narrative: sceneNarrative(
    "赫爾沃爾隨船到薩姆瑟島，卻在日落時獨自登岸。牧人勸她不要問墓丘的位置，說島上火焰與亡者使人逃離。",
    "她沒有隨他回去，而是繼續往前。她看見丘火在黑暗中燃起，便走向埋葬安甘蒂爾和兄弟的地方。",
    "恐懼在這一幕不是抽象的考驗，而是每個人都看得見的夜火與空島。赫爾沃爾選擇跨過它，讓父女相見成為可能。",
    "Hervor reaches Samsey by ship, then lands alone at sunset. A herdsman tells her not to ask for the mound, saying fire and the dead drive people from the island.",
    "She does not go back with him. She sees mound-fire rising in the darkness and walks toward the place where Angantyr and his brothers are buried.",
    "Fear is not an abstract trial here, but visible night-fire and an empty island. Hervor crosses it, making the meeting of father and daughter possible.",
  ) }),
  scene("norse-hervor-awakening", ["hervor-wakes-angantyr"], { title: { "zh-Hant": "墓丘前的呼喚", en: "A Call before the Mound" }, narrative: sceneNarrative(
    "赫爾沃爾站在燃燒的墓丘前，叫出安甘蒂爾與他的兄弟。她不是來哀悼，而是要求他們交出提爾鋒。",
    "安甘蒂爾先以父親的聲音回答，叫她不要喚醒死者，也催她趕快回船。赫爾沃爾拒絕離去，說墓火與亡者不足以使她退步。",
    "這段對話讓繼承成為一場抵抗，而不是安靜的交接。父親已死，女兒仍要讓他承認她的要求。",
    "Before the burning mound, Hervor calls Angantyr and his brothers. She comes not to mourn but to demand Tyrfing from them.",
    "Angantyr answers first as a father, telling her not to wake the dead and urging her back to the ships. Hervor refuses, saying neither mound-fire nor the dead can make her retreat.",
    "The dialogue makes inheritance a resistance rather than a quiet handover. The father is dead, yet the daughter still requires him to acknowledge her claim.",
  ) }),
  scene("norse-hervor-awakening", ["inheritance-not-left"], { title: { "zh-Hant": "她帶走提爾鋒", en: "She Takes Tyrfing Away" }, narrative: sceneNarrative(
    "安甘蒂爾說提爾鋒會傷害赫爾沃爾的家族，並要她把它留在墓丘裡。這是 R 本墓中父親給出的警告。",
    "赫爾沃爾仍不肯空手回去。安甘蒂爾最後把劍交出，她接住它，向墓丘中的父親告別。",
    "故事在她離開時停下，沒有替這把劍補完後代的命運。她帶走的是一件遺產，也帶走父親明明說過卻未能阻止的危險。",
    "Angantyr says Tyrfing will harm Hervor’s family and tells her to leave it in the mound. This is the warning given by the father in the R text.",
    "Hervor still refuses to return empty-handed. Angantyr finally gives the sword, and she takes it and bids farewell to her father in the mound.",
    "The story stops as she leaves; it does not complete the fate of later descendants. She takes an inheritance and a danger her father plainly named but could not stop.",
  ) }),
];

const vafthruthnismolTimelineSegments: TimelineProgrammeSegment[] = [
  scene("norse-vafthruthnismol", ["frigg-warning"], { title: { "zh-Hant": "弗麗嘉先提醒奧丁", en: "Frigg Warns Odin First" }, narrative: sceneNarrative(
    "奧丁想去找瓦夫蘇魯德尼爾，比一比誰知道得更多。",
    "弗麗嘉說，在所有巨人當中，沒有人可與這位對手相比；她仍祝奧丁平安來回。",
    "這不是阻止他的命令。她的話先把一場問答的危險放到奧丁面前：比起武力，這次要面對的是一位難以探清見聞的對手。",
    "Odin wants to seek Vafthruthnir and measure what each knows.",
    "Frigg says no giant can match this opponent, yet still wishes Odin a safe return.",
    "She does not forbid the journey. Her words place the danger of a contest before Odin first.",
  ) }),
  scene("norse-vafthruthnismol", ["gagnrath-enters-hall"], { title: { "zh-Hant": "加格拉德坐下來", en: "Gagnrath Takes a Seat" }, narrative: sceneNarrative(
    "奧丁走進巨人的廳堂，沒有報上自己的名字。",
    "他稱自己為加格拉德；瓦夫蘇魯德尼爾讓他坐下，說兩人很快就會知道誰更有智慧。",
    "力量沒有立刻交鋒。來客能否離開，要看他能否回答；巨人已說明，若不比他更有智慧，這趟拜訪就沒有出口。",
    "Odin enters the giant’s hall without giving his own name.",
    "He calls himself Gagnrath. Vafthruthnir gives him a seat and says they will soon know who is wiser.",
    "Strength does not clash at once. Whether the guest leaves depends on his answers.",
  ) }),
  scene("norse-vafthruthnismol", ["four-questions-at-doorway"], { title: { "zh-Hant": "先由巨人發問", en: "The Giant Asks First" }, narrative: sceneNarrative(
    "瓦夫蘇魯德尼爾一連問起白日、夜晚、河流和將來的戰場。",
    "奧丁答出伊芬河，說它隔在諸神與巨人之間，永遠不結冰。",
    "四題答完，巨人把座位與提問權讓給來客；較量才真正翻面。奧丁也答出蘇爾特與諸神將在維格里德相遇，證明他知道的不只是眼前的河。",
    "Vafthruthnir asks in turn about day, night, a river, and a battlefield to come.",
    "Odin names Ifing, the river said to lie between gods and giants and never freeze.",
    "After four answers, the giant yields the seat and the questioning turn. The contest finally reverses.",
  ) }),
  scene("norse-vafthruthnismol", ["world-in-an-answer"], { title: { "zh-Hant": "世界來自誰的身體", en: "Whose Body Made the World" }, narrative: sceneNarrative(
    "輪到奧丁問：大地和天空最初從哪裡來。",
    "瓦夫蘇魯德尼爾回答，土地、山、天空和海都出自伊米爾的身體。",
    "這是他在競賽中的答覆，不是把所有北歐故事壓成同一種世界起源。問答從馬與河的名字，推進到整個世界用什麼材料構成。",
    "Odin’s turn begins with a question: where did earth and sky first come from?",
    "Vafthruthnir answers that land, mountains, sky, and sea came from Ymir’s body.",
    "It is his answer in this contest, not a rule that flattens every Norse account into one origin.",
  ) }),
  scene("norse-vafthruthnismol", ["what-the-giant-remembers"], { title: { "zh-Hant": "巨人最遠的記憶", en: "The Giant’s Farthest Memory" }, narrative: sceneNarrative(
    "奧丁把問題推得更遠，問瓦夫蘇魯德尼爾究竟記得什麼。",
    "巨人說起貝爾格爾米爾，說他曾被船載著，也把他放進一條古老的巨人血脈。",
    "詩讓這段往昔以記憶的形式留下，而不是把它展成一段可補全的歷史。",
    "Odin presses farther and asks what Vafthruthnir remembers.",
    "The giant speaks of Bergelmir borne in a boat and places him in an ancient giant lineage.",
    "The poem keeps this past as memory, not as a history we can safely fill in.",
  ) }),
  scene("norse-vafthruthnismol", ["wind-from-eagles-wings"], { title: { "zh-Hant": "看不見的風從哪裡來", en: "Where Unseen Wind Comes From" }, narrative: sceneNarrative(
    "奧丁再問，吹過海面的風來自何處，明明看不見它本身。",
    "瓦夫蘇魯德尼爾說，在天邊以鷹形坐著的赫瑞斯維爾格，風從他的翅膀而出。",
    "這幅畫面留在巨人的話裡：它為風添上形狀，卻沒有要求讀者把它當成自然學。",
    "Odin asks again: where does wind over the sea come from when it cannot itself be seen?",
    "Vafthruthnir says Hraesvelg sits in eagle form at heaven’s end and wind comes from his wings.",
    "The image remains in the giant’s speech: it gives wind a shape without asking readers to treat it as natural science.",
  ) }),
  scene("norse-vafthruthnismol", ["nine-worlds-claim"], { title: { "zh-Hant": "他說自己走過九界", en: "He Says He Has Crossed Nine Worlds" }, narrative: sceneNarrative(
    "奧丁問巨人是否真能說出神與巨人的一切知識。",
    "瓦夫蘇魯德尼爾答說，他走過九界，也到過尼福爾海之下死者居住的地方。",
    "這一句把他的見聞推到很遠，卻沒有替這段問答另造一條旅行故事。奧丁沒有追問每一段旅程，因為他正把問題收束到最後一件誰也替不了的事。",
    "Odin asks whether the giant can truly speak of all the knowledge of gods and giants.",
    "Vafthruthnir says he has crossed nine worlds and gone beneath to the place where the dead dwell.",
    "The claim reaches far, yet the poem does not turn it into another travel story.",
  ) }),
  scene("norse-vafthruthnismol", ["question-no-guest-could-ask"], { title: { "zh-Hant": "只有奧丁能問的一句話", en: "A Question Only Odin Can Ask" }, narrative: sceneNarrative(
    "最後，奧丁問自己在兒子火葬前耳語了什麼。",
    "瓦夫蘇魯德尼爾沒有揭開秘密。他說世上沒有人能知道，並在這一刻認出面前的人。",
    "競賽不以殺戮收場；巨人承認奧丁更有智慧，秘密仍留在兩人之間。他已講完自己所知的古事，卻仍越不過奧丁對兒子說過的那一句耳語。",
    "At last Odin asks what he himself whispered to his son before the pyre.",
    "Vafthruthnir does not expose the secret. He says no person can know it, and in that instant recognizes who stands before him.",
    "The contest ends without a killing. The giant concedes Odin’s greater wisdom, and the secret stays between them.",
  ) }),
];

const theogonySiblingEdges = [
  "cronus-consort-rhea",
  "rhea-parent-hestia",
  "cronus-parent-hestia",
  "rhea-parent-demeter",
  "cronus-parent-demeter",
  "rhea-parent-hera",
  "cronus-parent-hera",
  "rhea-parent-hades",
  "cronus-parent-hades",
  "rhea-parent-poseidon",
  "cronus-parent-poseidon",
  "rhea-parent-zeus",
  "cronus-parent-zeus",
  "zeus-sibling-hera",
];

const theogonySwallowingEdges = [
  "cronus-swallows-children",
  "hestia-participates-swallowing",
  "demeter-participates-swallowing",
  "hera-participates-swallowing",
  "hades-participates-swallowing",
  "poseidon-participates-swallowing",
];

export const greekCoreEightAgesProgramme: TimelineProgramme = {
  id: "greek-core-eight-ages" as const,
  title: {
    "zh-Hant": "希臘神話時間線",
    en: "Greek Myth Timeline",
  },
  description: {
    "zh-Hant":
      "以八個按來源編排的神話時代，串連希臘神話的人物、地點、事件與關係。",
    en: "Eight source-led ages trace Greek myth figures, places, events, and relationships across the atlas.",
  },
  chronologyDisclaimer: {
    "zh-Hant":
      "這是按古代來源編排的導覽路線，不是實際年份或唯一正史。平行故事及異說會保留其版本標籤。",
    en: "This is a source-led route, not a calendar or a single canon. Parallel stories and variants retain their tradition labels.",
  },
  eras: [
    {
      id: "age-of-chaos",
      order: 1,
      title: { "zh-Hant": "混沌時代", en: "Age of Chaos" },
      summary: {
        "zh-Hant": "由卡俄斯 (Chaos) 開始，世界最初的根源逐一出現。",
        en: "The route begins with Chaos and the first roots of the world.",
      },
      subEras: [
        {
          id: "first-powers",
          order: 1,
          title: { "zh-Hant": "最初的力量", en: "The First Powers" },
          summary: {
            "zh-Hant":
              "卡俄斯 (Chaos)、蓋亞 (Gaia)、塔耳塔羅斯 (Tartarus)、厄洛斯 (Eros)、倪克斯 (Nyx) 與厄瑞玻斯 (Erebus) 構成同一個原初開端。",
            en: "Chaos, Gaia, Tartarus, Eros, Nyx and Erebus form one primordial opening.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            {
              storyId: "theogony",
              stepId: "theogony-first-powers",
              nodeIds: ["chaos", "gaia", "tartarus", "eros", "nyx", "erebus"],
              edgeIds: [
                "tartarus-within-gaia",
                "chaos-generates-nyx",
                "chaos-generates-erebus",
                "nyx-unites-with-erebus",
              ],
              primaryFocus: { kind: "node", nodeId: "chaos" },
              focusPath: [
                { kind: "node", nodeId: "gaia" },
                { kind: "node", nodeId: "tartarus" },
                { kind: "node", nodeId: "eros" },
                { kind: "edge", edgeId: "nyx-unites-with-erebus" },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "primordial-gods",
      order: 2,
      title: { "zh-Hant": "原始神時代", en: "Primordial Gods" },
      summary: {
        "zh-Hant":
          "蓋亞 (Gaia) 生出天空與海，天地子代與第一次權力更替隨之而來。",
        en: "Gaia brings forth sky and sea, followed by the first divine generations and transfer of power.",
      },
      subEras: [
        {
          id: "earth-sky-mountains-sea",
          order: 1,
          title: {
            "zh-Hant": "大地、天空、群山與海",
            en: "Earth, Sky, Mountains, and Sea",
          },
          summary: {
            "zh-Hant":
              "蓋亞 (Gaia) 生出烏拉諾斯 (Uranus) 與蓬托斯 (Pontus)；群山神 (Ourea) 在此作來源提及。",
            en: "Gaia brings forth Uranus and Pontus; Ourea remains a source-mentioned part of this scene.",
          },
          chronologyMode: "sequence",
          coverageStatus: "partial",
          coverageNote: {
            "zh-Hant":
              "群山神 (Ourea) 與這段生成同源；此處先以蓋亞 (Gaia)、烏拉諾斯 (Uranus) 與蓬托斯 (Pontus) 承接後續家系。",
            en: "Ourea belongs to the same generative passage; this sequence carries the next genealogy through Gaia, Uranus and Pontus.",
          },
          segments: [
            scene("theogony", ["theogony-earth-sky-sea"], {
              narrative: {
                setup: {
                  "zh-Hant":
                    "原初力量出現之後，故事把目光放回蓋亞 (Gaia) 身上。她不是先進入婚配，而是從自身生出能覆蓋她的烏拉諾斯 (Uranus)，也生出無邊的蓬托斯 (Pontus)。",
                  en: "After the first powers appear, the story returns to Gaia. Before any pairing, she brings forth Uranus to cover her and Pontus as the sea.",
                },
                development: {
                  "zh-Hant":
                    "此處還不是權力鬥爭，而是世界開始有形狀：大地在下，天空在上，海域在邊界之外展開。群山神 (Ourea) 同屬這段生成；敘事先集中在蓋亞、烏拉諾斯與蓬托斯三個會連向後續家系的力量。",
                  en: "This is not yet a struggle for power. It is the moment when the world gains shape: earth below, sky above, and sea spreading at the edges. The Ourea belong to this generative passage; the narration remains with Gaia, Uranus and Pontus, the powers that carry into the next family line.",
                },
                consequence: {
                  "zh-Hant":
                    "當天空覆在大地之上，下一代家族便有了舞台；之後的子女、壓迫與政變，都會在天地相連之間展開。",
                  en: "Once sky covers earth, the next family line has a stage; children, confinement, and revolt will unfold between earth and sky.",
                },
              },
              primaryFocus: { kind: "edge", edgeId: "gaia-generates-uranus" },
              focusPath: [{ kind: "edge", edgeId: "gaia-generates-pontus" }],
            }),
          ],
        },
        {
          id: "gaia-uranus-children",
          order: 2,
          title: {
            "zh-Hant": "蓋亞與烏拉諾斯的子代",
            en: "Children of Gaia and Uranus",
          },
          summary: {
            "zh-Hant":
              "泰坦神族 (Titans)、獨眼巨人 (Cyclopes) 與百臂巨人 (Hundred-Handers) 進入世界。",
            en: "Titans, Cyclopes, and Hundred-Handers enter the world.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene(
              "theogony",
              ["theogony-children-earth-sky", "theogony-hidden-within-earth"],
              {
                sceneId: "gaia-uranus-children-and-confinement",
                title: {
                  "zh-Hant": "天地子代與被藏起的痛苦",
                  en: "The Children of Earth and the Pain Hidden Within",
                },
                narrative: {
                  setup: {
                    "zh-Hant":
                      "蓋亞 (Gaia) 與烏拉諾斯 (Uranus) 結合後，世界不再只有原初力量，而開始變成家族。",
                    en: "When Gaia and Uranus unite, the world becomes more than primordial powers: it becomes a family.",
                  },
                  development: {
                    "zh-Hant":
                      "泰坦神族 (Titans)、獨眼巨人 (Cyclopes) 與百臂巨人 (Hundred-Handers) 相繼出生，但烏拉諾斯把可怕的子女藏進蓋亞深處。",
                    en: "Titans, Cyclopes, and Hundred-Handers are born, but Uranus hides the terrifying children deep within Gaia.",
                  },
                  consequence: {
                    "zh-Hant":
                      "大地承受不了這份痛苦，鐮刀與反抗的念頭也因此出現。",
                    en: "The earth cannot bear that pain, and the sickle and the thought of rebellion enter the story.",
                  },
                },
                primaryFocus: { kind: "edge", edgeId: "uranus-hides-children" },
                focusPath: [
                  { kind: "edge", edgeId: "gaia-parent-cronus" },
                  { kind: "node", nodeId: "cyclopes" },
                  { kind: "node", nodeId: "hecatoncheires" },
                ],
              },
            ),
          ],
        },
        {
          id: "uranus-overthrown",
          order: 3,
          title: { "zh-Hant": "烏拉諾斯被推翻", en: "Uranus Overthrown" },
          summary: {
            "zh-Hant":
              "蓋亞 (Gaia) 武裝克羅諾斯 (Cronus)，第一場神族政變改變統治秩序。",
            en: "Gaia arms Cronus, and the first divine coup changes the order of rule.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene("theogony", ["theogony-cronus-takes-sickle"], {
              narrative: {
                setup: {
                  "zh-Hant":
                    "蓋亞 (Gaia) 的痛苦不再只是沉默承受。她在大地深處造出灰色鐮刀，向自己的子女提出一個危險問題：誰願意向烏拉諾斯 (Uranus) 出手，終止這種把孩子藏回母體的統治？",
                  en: "Gaia’s pain no longer remains silent. Deep within the earth she makes the grey sickle and asks her children a dangerous question: who will strike Uranus and end this rule that hides children back inside their mother?",
                },
                development: {
                  "zh-Hant":
                    "眾子畏縮時，克羅諾斯 (Cronus) 接過母親的計劃。他不是突然成為純粹的英雄，也不是一開始就被寫成永恆惡王；赫西俄德 (Hesiod) 先讓他成為願意回應蓋亞的人。當天空再度覆向大地，他從伏藏處揮下鐮刀。",
                  en: "When the others shrink back, Cronus takes up his mother’s plan. He is not made a pure hero, nor introduced as a timeless villain; Hesiod first makes him the child who answers Gaia. When sky comes down over earth again, he strikes from ambush.",
                },
                consequence: {
                  "zh-Hant":
                    "這一擊切開第一個神族秩序。烏拉諾斯失去統治位置，克羅諾斯站上承繼的入口；而被割下的部分投入海中，又把新的誕生推向下一章。",
                  en: "The blow cuts open the first divine order. Uranus loses his ruling place and Cronus steps into succession; the severed part cast into the sea will carry the next birth forward.",
                },
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "cronus-overthrows-uranus",
              },
              focusPath: [{ kind: "edge", edgeId: "gaia-enlists-cronus" }],
            }),
            { storyId: "theogony", stepId: "theogony-aphrodite-from-foam" },
          ],
        },
      ],
    },
    {
      id: "age-of-titans",
      order: 3,
      title: { "zh-Hant": "泰坦時代", en: "Age of Titans" },
      summary: {
        "zh-Hant":
          "克羅諾斯 (Cronus) 統治後，夜、海與較早的神明仍在世界各處延續，繼承預言也令家庭秩序崩裂。",
        en: "After Cronus takes power, night, sea, and older powers continue to act, while a succession prophecy fractures the family order.",
      },
      subEras: [
        {
          id: "cronus-rule",
          order: 1,
          title: { "zh-Hant": "克羅諾斯統治", en: "Cronus Rules" },
          summary: {
            "zh-Hant":
              "夜與海的家系、俄刻阿諾斯 (Oceanus) 與忒堤斯 (Tethys) 的後代，以及未來神職逐步成形。",
            en: "The houses of Night and Sea, the descendants of Oceanus and Tethys, and future honours take shape.",
          },
          chronologyMode: "parallel",
          coverageStatus: "complete",
          segments: [
            scene(
              "theogony",
              ["theogony-houses-night-sea", "theogony-older-branches-honours"],
              {
                sceneId: "old-divine-branches",
                title: {
                  "zh-Hant": "夜、海與舊神仍在場",
                  en: "Night, Sea, and the Older Powers",
                },
                narrative: {
                  setup: {
                    "zh-Hant":
                      "這段統治不是空白年代；克羅諾斯 (Cronus) 掌權後，夜、海與更古老的神明仍在世界各處延續。",
                    en: "This age of rule is not empty; after Cronus takes power, night, sea, and older powers continue to act across the world.",
                  },
                  development: {
                    "zh-Hant":
                      "倪克斯 (Nyx) 生出的子女帶來睡眠、死亡、命運與紛爭；蓬托斯 (Pontus)、俄刻阿諾斯 (Oceanus) 與忒堤斯 (Tethys) 這一邊，則把故事帶向海域與未來盟友。",
                    en: "Nyx’s children bring sleep, death, fate, and strife, while Pontus, Oceanus, and Tethys carry the story toward sea powers and future allies.",
                  },
                  consequence: {
                    "zh-Hant":
                      "這些名字不是路過的細節；之後的誓言、榮位、亡者世界與英雄故事，都會再次回到這些較早出現的神明與力量，尤其是斯堤克斯的誓言與赫卡忒的榮位。",
                    en: "These names are not passing details; later oaths, honours, the realm of the dead, and heroic stories return to these older powers.",
                  },
                },
                primaryFocus: { kind: "node", nodeId: "nyx" },
                focusPath: [
                  { kind: "node", nodeId: "pontus" },
                  { kind: "node", nodeId: "oceanus" },
                  { kind: "node", nodeId: "styx" },
                ],
              },
            ),
          ],
        },
        {
          id: "rheas-children",
          order: 2,
          title: {
            "zh-Hant": "瑞亞的子女與吞子恐懼",
            en: "Rhea's Children and Cronus's Fear",
          },
          summary: {
            "zh-Hant":
              "瑞亞 (Rhea) 生下六名子女，克羅諾斯 (Cronus) 因承繼預言吞下其中五名。",
            en: "Rhea bears six children, and Cronus swallows five of them because of a succession prophecy.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            {
              storyId: "theogony",
              stepId: "theogony-swallowed-children",
              sceneId: "rheas-children-and-cronus-fear",
              title: {
                "zh-Hant": "瑞亞的子女與吞子恐懼",
                en: "Rhea's Children and Cronus's Fear",
              },
              narrative: {
                setup: {
                  "zh-Hant":
                    "克羅諾斯 (Cronus) 接過王權後，並沒有真正擺脫承繼的陰影。蓋亞 (Gaia) 與烏拉諾斯 (Uranus) 的預言指出，他也會被自己的孩子奪去榮位。",
                  en: "After Cronus takes power, he does not escape the shadow of succession. Gaia and Uranus foretell that one of his own children will take his honour.",
                },
                development: {
                  "zh-Hant":
                    "瑞亞 (Rhea) 先後生下赫斯提亞 (Hestia)、得墨忒耳 (Demeter)、赫拉 (Hera)、哈得斯 (Hades) 與波塞冬 (Poseidon)。每一個孩子剛出生，克羅諾斯便把他吞下，彷彿只要把下一代困在自己體內，預言就無法抵達現實。",
                  en: "Rhea bears Hestia, Demeter, Hera, Hades, and Poseidon. Each time a child is born, Cronus swallows the child, as if trapping the next generation inside himself can keep the prophecy from becoming real.",
                },
                consequence: {
                  "zh-Hant":
                    "這不是普通家族名錄，而是第二次承繼危機的開場。五名未來奧林匹斯神被父親吞沒，瑞亞的悲痛也把故事推向最後一個孩子：宙斯 (Zeus)。",
                  en: "This is not a simple family list, but the opening of the second succession crisis. Five future Olympians disappear into their father, and Rhea's grief turns the story toward the last child: Zeus.",
                },
              },
              nodeIds: [
                "cronus",
                "rhea",
                "hestia",
                "demeter",
                "hera",
                "hades",
                "poseidon",
                "zeus",
                "gaia",
                "uranus",
                "swallowing-children",
              ],
              edgeIds: [...theogonySiblingEdges, ...theogonySwallowingEdges],
              primaryFocus: { kind: "node", nodeId: "swallowing-children" },
              focusPath: [
                { kind: "node", nodeId: "rhea" },
                { kind: "node", nodeId: "hestia" },
                { kind: "node", nodeId: "poseidon" },
                { kind: "node", nodeId: "zeus" },
              ],
            },
          ],
        },
        {
          id: "swallowing-and-hidden-zeus",
          order: 3,
          title: { "zh-Hant": "宙斯藏匿", en: "The Hidden Zeus" },
          summary: {
            "zh-Hant":
              "瑞亞 (Rhea) 求助蓋亞 (Gaia)，以裹布石頭欺瞞克羅諾斯 (Cronus)，讓宙斯 (Zeus) 藏了起來。",
            en: "Rhea turns to Gaia, deceives Cronus with a swaddled stone, and preserves Zeus in hiding.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene("theogony", ["theogony-hidden-child"], {
              title: { "zh-Hant": "被藏起的宙斯", en: "The Hidden Zeus" },
              narrative: {
                setup: {
                  "zh-Hant":
                    "到了最幼的孩子將要出生時，瑞亞 (Rhea) 不再只是哀傷。她向蓋亞 (Gaia) 與烏拉諾斯 (Uranus) 求助，尋找一個能讓孩子活下來的辦法。",
                  en: "When the youngest child is about to be born, Rhea is no longer only grieving. She asks Gaia and Uranus for help, seeking a way for the child to live.",
                },
                development: {
                  "zh-Hant":
                    "蓋亞接收新生的宙斯 (Zeus)，把他帶到克里特 (Crete) 藏起。瑞亞則把一塊石頭包在襁褓裏，交給克羅諾斯 (Cronus)；害怕預言的王吞下石頭，以為又一次把未來關回自己體內。",
                  en: "Gaia receives newborn Zeus and hides him in Crete. Rhea wraps a stone in swaddling cloth and gives it to Cronus; the fearful king swallows it, thinking he has shut the future inside himself again.",
                },
                consequence: {
                  "zh-Hant":
                    "這個欺瞞沒有立刻推翻克羅諾斯，卻保存了能推翻他的孩子。從此，王權表面仍在泰坦手中，真正的下一次更替已在隱藏處長大。",
                  en: "The deception does not overthrow Cronus at once, but it preserves the child who will. From here, kingship still appears Titan, while the next succession grows in hiding.",
                },
              },
              primaryFocus: { kind: "edge", edgeId: "gaia-hides-zeus" },
              focusPath: [
                { kind: "node", nodeId: "rhea" },
                { kind: "edge", edgeId: "rhea-deceives-cronus-stone" },
              ],
              chronologyNote: {
                "zh-Hant":
                  "此處依赫西俄德 (Hesiod)《神譜》468–491 行；庫瑞忒斯 (Curetes) 與阿瑪爾忒亞 (Amalthea) 等細節屬其他版本。",
                en: "This scene follows Hesiod, Theogony 468–491; details such as the Curetes and Amalthea belong to other versions.",
              },
            }),
          ],
        },
      ],
    },
    {
      id: "titanomachy",
      order: 4,
      title: { "zh-Hant": "泰坦戰爭", en: "Titanomachy" },
      summary: {
        "zh-Hant":
          "宙斯 (Zeus) 召集盟友，奧林匹斯眾神 (Olympians) 與泰坦神族 (Titans) 的衝突重組宇宙權力。",
        en: "Zeus gathers allies and the conflict between Olympians and Titans remakes cosmic power.",
      },
      subEras: [
        {
          id: "zeus-returns",
          order: 1,
          title: { "zh-Hant": "宙斯回歸", en: "Zeus Returns" },
          summary: {
            "zh-Hant":
              "宙斯 (Zeus) 的兄弟姊妹與獨眼巨人 (Cyclopes) 重獲自由，繼承衝突成為戰爭。",
            en: "The siblings and Cyclopes are freed and succession becomes war.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          segments: [
            sceneGroup(
              [
                {
                  storyId: "theogony",
                  stepId: "theogony-stone-siblings-thunder",
                },
                {
                  storyId: "titanomachy",
                  stepId: "titanomachy-succession-war",
                },
              ],
              {
                sceneId: "zeus-return-and-succession-war",
                title: {
                  "zh-Hant": "石頭、手足與戰爭開端",
                  en: "Stone, Siblings, and the War Begins",
                },
                narrative: {
                  setup: {
                    "zh-Hant":
                      "被藏起的宙斯 (Zeus) 長大後，克羅諾斯 (Cronus) 的吞子策略終於反轉。",
                    en: "When the hidden Zeus grows up, Cronus's strategy of swallowing his children finally turns back on him.",
                  },
                  development: {
                    "zh-Hant":
                      "克羅諾斯吐出石頭與被吞下的手足；獨眼巨人 (Cyclopes) 重獲自由，雷霆也進入宙斯手中。",
                    en: "Cronus disgorges the stone and the swallowed siblings; the Cyclopes are freed, and thunder enters Zeus’s hands.",
                  },
                  consequence: {
                    "zh-Hant":
                      "家庭內部的繼承危機不再只是預言，而變成奧林匹斯與泰坦之間的戰爭。",
                    en: "The family’s succession crisis is no longer only prophecy: it becomes war between Olympians and Titans.",
                  },
                },
                primaryFocus: {
                  kind: "edge",
                  edgeId: "zeus-forces-release-siblings",
                },
                focusPath: [
                  { kind: "edge", edgeId: "zeus-frees-cyclopes" },
                  { kind: "edge", edgeId: "cyclopes-give-zeus-thunder" },
                ],
                chronologyNote: {
                  "zh-Hant":
                    "墨提斯 (Metis) 令克羅諾斯 (Cronus) 吐回子女，屬偽阿波羅多洛斯 (Pseudo-Apollodorus) 的敘事。",
                  en: "Metis causing Cronus to disgorge the children appears in Pseudo-Apollodorus.",
                },
              },
            ),
          ],
        },
        {
          id: "alliances-and-ten-years",
          order: 2,
          title: {
            "zh-Hant": "結盟與十年戰爭",
            en: "Alliances and the Ten-Year War",
          },
          summary: {
            "zh-Hant":
              "奧林匹斯山 (Mount Olympus) 與俄特律斯山 (Mount Othrys) 對峙，蓋亞 (Gaia) 的條件、斯堤克斯 (Styx) 的盟誓及神兵逐一改變戰局。",
            en: "Olympus faces Othrys as Gaia’s condition, Styx’s alliance, and divine weapons alter the war.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          segments: [
            scene(
              "titanomachy",
              ["titanomachy-olympus-othrys", "titanomachy-ten-year-deadlock"],
              {
                sceneId: "olympus-othrys-deadlock",
                title: {
                  "zh-Hant": "奧林匹斯、俄特律斯與十年僵局",
                  en: "Olympus, Othrys, and the Ten-Year Deadlock",
                },
                narrative: {
                  setup: {
                    "zh-Hant":
                      "戰爭有了兩個地標：宙斯一方據奧林匹斯山 (Mount Olympus)，泰坦神族 (Titans) 從俄特律斯山 (Mount Othrys) 迎戰。",
                    en: "The war receives two landmarks: Zeus’s side fights from Mount Olympus, while the Titans answer from Mount Othrys.",
                  },
                  development: {
                    "zh-Hant":
                      "雙方長久相持，克羅諾斯 (Cronus) 與宙斯的父子衝突被放大成兩代神祇的僵局。",
                    en: "The sides hold each other for years, and the conflict between Cronus and Zeus widens into a deadlock between divine generations.",
                  },
                  consequence: {
                    "zh-Hant": "若沒有新的盟友，戰爭只會繼續停在兩座山之間。",
                    en: "Without new allies, the war will remain suspended between the two mountains.",
                  },
                },
                primaryFocus: { kind: "edge", edgeId: "zeus-opposes-cronus" },
                focusPath: [
                  { kind: "edge", edgeId: "titanomachy-at-olympus" },
                  { kind: "edge", edgeId: "titanomachy-at-othrys" },
                ],
                chronologyNote: {
                  "zh-Hant":
                    "十年戰爭在赫西俄德 (Hesiod) 與偽阿波羅多洛斯 (Pseudo-Apollodorus) 路線中都出現；個別參戰細節仍按來源標示。",
                  en: "The ten-year war appears in both Hesiod and Pseudo-Apollodorus; individual participation is labelled by source.",
                },
              },
            ),
            scene(
              "titanomachy",
              [
                "titanomachy-gaia-condition",
                "titanomachy-hundred-handers-covenant",
              ],
              {
                sceneId: "gaia-condition-and-hundred-handers",
                title: {
                  "zh-Hant": "蓋亞的條件與百臂者盟約",
                  en: "Gaia’s Condition and the Hundred-Handers’ Covenant",
                },
                narrative: {
                  setup: {
                    "zh-Hant": "僵局要被打破，宙斯必須先回到更深的家族傷口。",
                    en: "To break the deadlock, Zeus must first return to an older wound in the family.",
                  },
                  development: {
                    "zh-Hant":
                      "蓋亞 (Gaia) 指出塔耳塔羅斯 (Tartarus) 中的百臂巨人 (Hundred-Handers) 可以改變戰局；宙斯釋放他們，並以盟約換得援助。",
                    en: "Gaia points to the Hundred-Handers in Tartarus as the force that can change the war; Zeus frees them and gains their aid through covenant.",
                  },
                  consequence: {
                    "zh-Hant":
                      "被上一代壓下去的力量，現在回到戰場，幫宙斯推翻泰坦統治。",
                    en: "The powers suppressed by the previous generation now return to the battlefield and help Zeus overthrow Titan rule.",
                  },
                },
                primaryFocus: {
                  kind: "edge",
                  edgeId: "gaia-advises-zeus-free-hecatoncheires",
                },
                focusPath: [
                  { kind: "edge", edgeId: "zeus-frees-hecatoncheires" },
                  { kind: "edge", edgeId: "hecatoncheires-aid-zeus" },
                ],
              },
            ),
            { storyId: "titanomachy", stepId: "titanomachy-styx-first" },
            {
              storyId: "titanomachy",
              stepId: "titanomachy-three-weapons",
              chronologyNote: {
                "zh-Hant":
                  "獨眼巨人 (Cyclopes) 提供三件武器，採用偽阿波羅多洛斯 (Pseudo-Apollodorus) 的敘事。",
                en: "The three-weapon account is presented through Pseudo-Apollodorus.",
              },
            },
            { storyId: "titanomachy", stepId: "titanomachy-heaven-earth-boil" },
          ],
        },
        {
          id: "victory-punishment-division",
          order: 3,
          title: {
            "zh-Hant": "勝利、懲罰與分封",
            en: "Victory, Punishment, and Settlement",
          },
          summary: {
            "zh-Hant":
              "泰坦神族 (Titans) 戰敗，阿特拉斯 (Atlas) 受罰，塔耳塔羅斯 (Tartarus) 的看守秩序建立。",
            en: "The Titans fall, Atlas is punished, and the guardianship of Tartarus is established.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene(
              "titanomachy",
              ["titanomachy-titans-fall", "titanomachy-gates-tartarus"],
              {
                sceneId: "titans-fall-and-tartarus-guarded",
                title: {
                  "zh-Hant": "泰坦陷落與塔耳塔羅斯之門",
                  en: "The Titans Fall and the Gates of Tartarus",
                },
                narrative: {
                  setup: {
                    "zh-Hant":
                      "雷霆與巨石打破僵局後，泰坦神族 (Titans) 的統治走到盡頭。",
                    en: "Once thunder and hurled stones break the deadlock, Titan rule reaches its end.",
                  },
                  development: {
                    "zh-Hant":
                      "泰坦被捆縛並送入塔耳塔羅斯；墨諾提俄斯 (Menoetius) 與阿特拉斯 (Atlas) 的個別命運也在戰後被安排。",
                    en: "The Titans are bound and sent into Tartarus; the individual fates of Menoetius and Atlas are also assigned after the war.",
                  },
                  consequence: {
                    "zh-Hant":
                      "百臂巨人守在門前，勝利不只是擊敗敵人，也是在深處建立新的封鎖秩序。",
                    en: "The Hundred-Handers stand guard at the gates: victory is not only defeat of enemies but a new order of confinement below.",
                  },
                },
                primaryFocus: {
                  kind: "edge",
                  edgeId: "titans-confined-tartarus",
                },
                focusPath: [
                  { kind: "edge", edgeId: "hecatoncheires-bind-titans" },
                  { kind: "edge", edgeId: "hecatoncheires-guard-titans" },
                ],
              },
            ),
          ],
        },
      ],
    },
    {
      id: "early-olympian-age",
      order: 5,
      title: { "zh-Hant": "奧林匹斯建立", en: "Early Olympian Age" },
      summary: {
        "zh-Hant": "宙斯 (Zeus) 的王權、三界分權及神職網絡形成新的宇宙秩序。",
        en: "Zeus’s kingship, the division of realms, and divine offices form a new cosmic order.",
      },
      subEras: [
        {
          id: "division-of-realms",
          order: 1,
          title: { "zh-Hant": "三界分權", en: "Division of the Realms" },
          summary: {
            "zh-Hant":
              "戰後挑戰結束，眾神承認宙斯 (Zeus)，天空、海與冥界以抽籤分配。",
            en: "After the final challenge, the gods recognise Zeus and allot sky, sea, and underworld.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene(
              "olympians",
              [
                "olympians-after-victory",
                "olympians-earth-last-challenge",
                "olympians-thunder-secures-throne",
              ],
              {
                sceneId: "typhoeus-and-secured-throne",
                title: {
                  "zh-Hant": "最後挑戰與王權確立",
                  en: "The Last Challenge and the Secured Throne",
                },
                narrative: {
                  setup: {
                    "zh-Hant":
                      "泰坦神族 (Titans) 已被囚禁，但宙斯 (Zeus) 的勝利還未完全變成穩定秩序。",
                    en: "The Titans are imprisoned, but Zeus’s victory has not yet become stable order.",
                  },
                  development: {
                    "zh-Hant":
                      "蓋亞 (Gaia) 與塔耳塔羅斯 (Tartarus) 生下堤豐 (Typhoeus)，把最後的威脅推向天地；宙斯以雷霆擊敗他。",
                    en: "Gaia and Tartarus bring forth Typhoeus, raising one last threat across heaven and earth; Zeus defeats him with thunder.",
                  },
                  consequence: {
                    "zh-Hant":
                      "最後挑戰被壓入深處後，宙斯才真正站到新秩序的中心。",
                    en: "Only after this last challenge is driven below can Zeus stand at the centre of the new order.",
                  },
                },
                primaryFocus: { kind: "edge", edgeId: "zeus-defeats-typhoeus" },
                focusPath: [
                  { kind: "edge", edgeId: "gaia-parent-typhoeus" },
                  { kind: "edge", edgeId: "typhoeus-confined-tartarus" },
                ],
              },
            ),
            scene(
              "olympians",
              ["olympians-gods-choose-zeus", "olympians-realms-by-lot"],
              {
                sceneId: "zeus-kingship-and-realms",
                title: {
                  "zh-Hant": "眾神推舉與三界分權",
                  en: "The Gods Choose Zeus and Divide the Realms",
                },
                narrative: {
                  setup: {
                    "zh-Hant":
                      "擊敗敵人仍不足以成王，眾神還要承認誰來分配榮位。",
                    en: "Defeating enemies is not enough to make a king; the gods must recognise who distributes honour.",
                  },
                  development: {
                    "zh-Hant":
                      "眾神依蓋亞提示推舉宙斯統治；偽阿波羅多洛斯 (Pseudo-Apollodorus) 另以抽籤說明宙斯、波塞冬 (Poseidon) 與哈得斯 (Hades) 分得天空、海洋與冥界。",
                    en: "The gods, prompted by Gaia, ask Zeus to rule; Pseudo-Apollodorus also explains the sky, sea, and underworld through lots drawn by Zeus, Poseidon, and Hades.",
                  },
                  consequence: {
                    "zh-Hant":
                      "戰後世界因此有了王權中心，也有了三界的基本邊界。",
                    en: "The post-war world gains both a centre of kingship and the basic borders of three realms.",
                  },
                },
                primaryFocus: {
                  kind: "edge",
                  edgeId: "olympian-council-makes-zeus-king",
                },
                focusPath: [
                  { kind: "edge", edgeId: "zeus-receives-sky-by-lot" },
                  { kind: "edge", edgeId: "poseidon-receives-sea-by-lot" },
                  { kind: "edge", edgeId: "hades-receives-underworld-by-lot" },
                ],
                chronologyNote: {
                  "zh-Hant":
                    "三界抽籤屬偽阿波羅多洛斯 (Pseudo-Apollodorus) 路線，不取代赫西俄德 (Hesiod) 的權位分配說法。",
                  en: "The division by lot belongs to the Pseudo-Apollodorus route and does not replace Hesiod’s broader distribution of honours.",
                },
              },
            ),
          ],
        },
        {
          id: "offices-family-honours",
          order: 2,
          title: {
            "zh-Hant": "神職、家族與榮譽",
            en: "Offices, Family, and Honours",
          },
          summary: {
            "zh-Hant":
              "雅典娜 (Athena)、時序女神 (Horae)、摩伊賴 (Fates)、繆斯 (Muses)、阿波羅 (Apollo)、阿耳忒彌斯 (Artemis) 等進入新秩序。",
            en: "Athena, the Horae, Moirai, Muses, Apollo, Artemis, and others enter the new order.",
          },
          chronologyMode: "parallel",
          coverageStatus: "complete",
          segments: [
            { storyId: "olympians", stepId: "olympians-swallowing-succession" },
            sceneGroup(
              [
                { storyId: "theogony", stepId: "theogony-muses-commission" },
                {
                  storyId: "olympians",
                  stepId: "olympians-order-justice-fate",
                },
              ],
              {
                sceneId: "song-justice-and-fate",
                title: {
                  "zh-Hant": "歌聲、正義與命運",
                  en: "Song, Justice, and Fate",
                },
                narrative: {
                  setup: {
                    "zh-Hant": "新秩序需要被唱出，也需要可以管理神與人的原則。",
                    en: "The new order must be sung into memory, and it needs principles that can govern gods and mortals.",
                  },
                  development: {
                    "zh-Hant":
                      "繆斯 (Muses) 的召命把眾神起源變成可傳述的詩；宙斯與忒彌斯 (Themis) 的家系則把良序、正義、和平與命運放入王權之中。",
                    en: "The Muses’ commission turns divine origins into a song that can be handed down; Zeus and Themis bring order, justice, peace, and fate into kingship.",
                  },
                  consequence: {
                    "zh-Hant":
                      "奧林匹斯不只是勝利者的山，也成為記憶、法律與生命分配的中心。",
                    en: "Olympus becomes more than the mountain of victors: it becomes a centre of memory, law, and the allotment of life.",
                  },
                },
                primaryFocus: { kind: "edge", edgeId: "zeus-consort-themis" },
                focusPath: [
                  { kind: "node", nodeId: "zeus" },
                  { kind: "edge", edgeId: "zeus-parent-moirai" },
                ],
              },
            ),
            { storyId: "olympians", stepId: "olympians-grace-memory-seasons" },
            scene(
              "olympians",
              ["olympians-leto-twins", "olympians-hera-house-athena"],
              {
                sceneId: "olympian-children-and-households",
                title: {
                  "zh-Hant": "雙生神、赫拉之家與雅典娜",
                  en: "The Twins, Hera’s House, and Athena",
                },
                narrative: {
                  setup: {
                    "zh-Hant":
                      "王權穩定後，奧林匹斯的下一步是家族、職能與宮殿秩序。",
                    en: "Once kingship is stable, Olympus turns toward family, offices, and palace order.",
                  },
                  development: {
                    "zh-Hant":
                      "勒托 (Leto) 生下阿波羅 (Apollo) 與阿耳忒彌斯 (Artemis)；赫拉 (Hera) 的子女與披甲誕生的雅典娜 (Athena) 也把戰爭、工藝、生育與智慧帶入神族家庭。",
                    en: "Leto bears Apollo and Artemis; Hera’s children and armed Athena bring war, craft, birth, and wisdom into the divine household.",
                  },
                  consequence: {
                    "zh-Hant":
                      "眾神不再只是世代名錄，而開始有了日後故事會辨認的職能與位置。",
                    en: "The gods cease to be only genealogy and begin to take the roles later stories will recognise.",
                  },
                },
                primaryFocus: { kind: "edge", edgeId: "zeus-parent-apollo" },
                focusPath: [
                  { kind: "edge", edgeId: "zeus-parent-artemis" },
                  { kind: "edge", edgeId: "hera-parent-ares" },
                  { kind: "node", nodeId: "athena" },
                ],
              },
            ),
            { storyId: "olympians", stepId: "olympians-hearth-centre" },
          ],
        },
        {
          id: "gods-and-humans-new-order",
          order: 3,
          title: {
            "zh-Hant": "神與人類的新秩序",
            en: "A New Order for Gods and Humans",
          },
          summary: {
            "zh-Hant":
              "奧林匹斯山 (Mount Olympus) 的秩序在議事、冥界及凡人故事中實際運作。",
            en: "Olympian order operates through council, underworld, and mortal-facing stories.",
          },
          chronologyMode: "parallel",
          coverageStatus: "partial",
          coverageNote: {
            "zh-Hant":
              "伊俄 (Io)、歐羅巴 (Europa)、卡利斯托 (Callisto)、塞墨勒 (Semele) 等會留到後面的平行故事；此處先處理普羅米修斯 (Prometheus)、潘多拉 (Pandora) 與冥界秩序。",
            en: "Io, Europa, Callisto, Semele and related parallel myths remain future story packs; this section follows Prometheus, Pandora and the order below.",
          },
          segments: [
            {
              storyId: "olympians",
              stepId: "olympians-prometheus-human-order",
              sceneId: "prometheus-fire-and-pandora",
              primaryFocus: { kind: "edge", edgeId: "prometheus-steals-fire" },
              focusPath: [
                { kind: "edge", edgeId: "prometheus-stages-mecone-division" },
                { kind: "edge", edgeId: "zeus-orders-pandora" },
                { kind: "edge", edgeId: "pandora-opens-jar" },
              ],
            },
            { storyId: "olympians", stepId: "olympians-order-in-practice" },
            scene(
              "underworld",
              [
                "underworld-tartarus-distinction",
                "underworld-persephone-divided-year",
              ],
              {
                sceneId: "underworld-enters-olympian-order",
                title: {
                  "zh-Hant": "塔耳塔羅斯、冥后與地下秩序",
                  en: "Tartarus, the Queen, and the Order Below",
                },
                narrative: {
                  setup: {
                    "zh-Hant":
                      "奧林匹斯建立後，世界下方也需要被理解：塔耳塔羅斯不是整個冥界。",
                    en: "After Olympus is established, the world below must also be understood: Tartarus is not the whole underworld.",
                  },
                  development: {
                    "zh-Hant":
                      "泰坦囚在深淵，黑帝斯 (Hades) 統治亡者之境；珀耳塞福涅 (Persephone) 被帶入地下，又因石榴籽把一年分成兩段。",
                    en: "The Titans are imprisoned in the abyss, Hades rules the realm of the dead, and Persephone is taken below, with the pomegranate dividing the year.",
                  },
                  consequence: {
                    "zh-Hant":
                      "地下世界因此不只是懲罰之處，也成為季節、婚姻與死亡秩序的一部分。",
                    en: "The world below is therefore not only a place of punishment but part of seasonal, marital, and mortal order.",
                  },
                },
                primaryFocus: {
                  kind: "edge",
                  edgeId: "persephone-divides-year-with-underworld",
                },
                focusPath: [
                  { kind: "edge", edgeId: "titans-confined-tartarus" },
                  { kind: "edge", edgeId: "hades-rules-underworld" },
                  { kind: "edge", edgeId: "hades-takes-persephone" },
                ],
              },
            ),
          ],
        },
      ],
    },
    {
      id: "age-of-heroes",
      order: 6,
      title: { "zh-Hant": "英雄時代", en: "Age of Heroes" },
      summary: {
        "zh-Hant": "不同英雄傳統平行展開，不被強行壓成一條唯一時間線。",
        en: "Different heroic traditions unfold in parallel rather than being forced into one chronology.",
      },
      subEras: [
        {
          id: "founders-and-monster-slayers",
          order: 1,
          title: {
            "zh-Hant": "建城者與怪物征服者",
            en: "Founders and Monster-Slayers",
          },
          summary: {
            "zh-Hant": "這個入口保留給尚待研究及製作的英雄傳統。",
            en: "This entry is reserved for heroic traditions that still require research and production.",
          },
          chronologyMode: "parallel",
          coverageStatus: "editorial-only",
          coverageNote: {
            "zh-Hant":
              "珀耳修斯 (Perseus)、卡德摩斯 (Cadmus)、美杜莎 (Medusa)、彌諾陶洛斯 (Minotaur) 等屬後續平行英雄故事；本段保留為導覽位置。",
            en: "Perseus, Cadmus, Medusa, the Minotaur and related cycles remain future parallel hero stories; this section stays as an orientation point.",
          },
          segments: [],
        },
        {
          id: "argonaut-generation",
          order: 2,
          title: { "zh-Hant": "阿爾戈英雄世代", en: "The Argonaut Generation" },
          summary: {
            "zh-Hant":
              "伊阿宋 (Jason)、美狄亞 (Medea)、阿爾戈英雄 (Argonauts) 與奧菲斯 (Orpheus) 的故事平行展開。",
            en: "Parallel traditions of Jason, Medea, the Argonauts, and Orpheus.",
          },
          chronologyMode: "parallel",
          coverageStatus: "complete",
          segments: [
            scene("argonauts", ["argonauts-01", "argonauts-02"], {
              sceneId: "argonauts-one-sandal-command",
              title: {
                "zh-Hant": "單鞋人的命令與阿爾戈號",
                en: "The One-Sandal Command and the Argo",
              },
              narrative: {
                setup: {
                  "zh-Hant":
                    "珀利阿斯 (Pelias) 害怕神諭所說「只穿一隻鞋的人」，而伊阿宋 (Jason) 正以這個樣子來到伊奧爾科斯 (Iolcus)。",
                  en: "Pelias fears the oracle’s one-sandaled man, and Jason arrives at Iolcus in exactly that form.",
                },
                development: {
                  "zh-Hant":
                    "金羊毛 (Golden Fleece) 被設成幾乎不能回來的任務；阿爾戈斯 (Argus) 在雅典娜 (Athena) 協助下造船，英雄們登上阿爾戈號 (Argo)。",
                  en: "The Golden Fleece becomes an almost impossible command; Argus builds the Argo with Athena’s help, and the heroes gather aboard.",
                },
                consequence: {
                  "zh-Hant":
                    "一個王權危機因此變成海上遠征，伊阿宋也不再是孤身上路。",
                  en: "A crisis of kingship becomes a sea expedition, and Jason no longer travels alone.",
                },
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "pelias-commands-jason-fleece",
              },
              focusPath: [
                { kind: "edge", edgeId: "argus-builder-builds-argo" },
                { kind: "edge", edgeId: "jason-arrives-iolcus-one-sandal" },
                {
                  kind: "edge",
                  edgeId: "golden-fleece-objective-argonaut-expedition",
                },
              ],
            }),
            scene("argonauts", ["argonauts-03", "argonauts-04"], {
              sceneId: "argonauts-lemnos-and-cyzicus",
              narrative: {
                setup: {
                  "zh-Hant":
                    "阿爾戈號 (Argo) 離開故鄉海岸後，遠征不再只是一道艱難命令，而是一連串會考驗整隊英雄的海上遭遇。",
                  en: "After the Argo leaves its home shore, the expedition is no longer only a difficult command; it becomes a chain of sea encounters that tests the whole company.",
                },
                development: {
                  "zh-Hant":
                    "在雷姆諾斯 (Lemnos)，英雄們因島上的女人而停留；赫拉克勒斯 (Heracles) 催促眾人再次啟航。到庫齊科斯 (Cyzicus) 一帶，黑夜與誤認把賓主之誼變成殺戮。",
                  en: "On Lemnos the heroes delay among the island women until Heracles urges departure. Near Cyzicus, darkness and misrecognition turn hospitality into killing.",
                },
                consequence: {
                  "zh-Hant":
                    "這段航程令遠征帶上悲劇色彩：阿爾戈英雄 (Argonauts) 可以前進，但每一次登岸都可能留下新的損失與哀悼。",
                  en: "The voyage gains a tragic edge: the Argonauts can move forward, but every landing may leave loss and mourning behind.",
                },
              },
              title: {
                "zh-Hant": "利姆諾斯停留與黑夜誤殺",
                en: "Lemnos and the Killing in the Dark",
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "argonaut-expedition-reaches-lemnos",
              },
              focusPath: [
                { kind: "edge", edgeId: "heracles-urges-departure-lemnos" },
                { kind: "edge", edgeId: "argonaut-expedition-kills-cyzicus" },
                { kind: "edge", edgeId: "argonaut-expedition-mourns-cyzicus" },
              ],
            }),
            scene("argonauts", ["argonauts-05", "argonauts-06"], {
              sceneId: "argonauts-hylas-and-amycus",
              narrative: {
                setup: {
                  "zh-Hant":
                    "航程再向前推進時，英雄隊伍先在密西亞 (Mysia) 失去許拉斯 (Hylas)，連赫拉克勒斯 (Heracles) 也被留在尋找他的悲傷之中。",
                  en: "As the voyage advances, the company loses Hylas in Mysia, and even Heracles is left behind in the search and grief.",
                },
                development: {
                  "zh-Hant":
                    "接着，阿米科斯 (Amycus) 以拳鬥攔路；波呂丟刻斯 (Polydeuces) 接下挑戰並擊敗他，為阿爾戈英雄 (Argonauts) 打開通道。",
                  en: "Then Amycus blocks the passage with a boxing challenge. Polydeuces accepts it, defeats him, and opens the way for the Argonauts.",
                },
                consequence: {
                  "zh-Hant":
                    "遠征因此同時失去最強同行者之一，又證明隊伍仍能靠不同英雄的本領繼續前行。",
                  en: "The expedition loses one of its strongest companions, yet proves that other heroic skills can still carry it onward.",
                },
              },
              title: {
                "zh-Hant": "赫拉斯失蹤與拳鬥開路",
                en: "Hylas Lost and a Boxing Passage Opened",
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "hylas-disappears-from-argonaut-expedition",
              },
              focusPath: [
                { kind: "edge", edgeId: "argo-leaves-heracles-behind" },
                { kind: "edge", edgeId: "amycus-challenges-polydeuces" },
                { kind: "edge", edgeId: "polydeuces-defeats-amycus" },
              ],
            }),
            scene("argonauts", ["argonauts-07", "argonauts-08"], {
              sceneId: "argonauts-phineus-and-clashing-rocks",
              narrative: {
                setup: {
                  "zh-Hant":
                    "阿爾戈英雄 (Argonauts) 到達菲紐斯 (Phineus) 面前時，看見一個知道前路、卻被哈耳庇厄 (Harpies) 折磨的先知。",
                  en: "When the Argonauts reach Phineus, they find a prophet who knows the road ahead but is tormented by the Harpies.",
                },
                development: {
                  "zh-Hant":
                    "卡萊斯 (Calais) 與仄忒斯 (Zetes) 趕走哈耳庇厄，菲紐斯便說出撞擊岩 (Symplegades) 的危險；阿爾戈號 (Argo) 依照指示闖過會合攏的岩門。",
                  en: "Calais and Zetes drive the Harpies away, and Phineus warns them about the Symplegades. Following the warning, the Argo passes the rocks that crash together.",
                },
                consequence: {
                  "zh-Hant":
                    "這段航程把遠征從普通航行推到神話邊界：只有先知、援助與準確行動同時到位，英雄們才可抵達真正的目的地。",
                  en: "This scene moves the quest to the edge of mythic geography: prophecy, help, and precise action must align before the heroes can reach the quest’s destination.",
                },
              },
              title: {
                "zh-Hant": "菲紐斯的指引與撞擊岩",
                en: "Phineus’ Guidance and the Clashing Rocks",
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "phineus-guides-argonaut-expedition",
              },
              focusPath: [
                { kind: "edge", edgeId: "harpies-torment-phineus" },
                { kind: "edge", edgeId: "phineus-warns-of-symplegades" },
                { kind: "edge", edgeId: "argo-passes-symplegades" },
              ],
            }),
            { storyId: "argonauts", stepId: "argonauts-09" },
            { storyId: "argonauts", stepId: "argonauts-10" },
            { storyId: "argonauts", stepId: "argonauts-11" },
            { storyId: "argonauts", stepId: "argonauts-12" },
            { storyId: "underworld", stepId: "underworld-do-not-look-back" },
          ],
        },
        {
          id: "heracles-and-parallel-heroes",
          order: 3,
          title: {
            "zh-Hant": "赫拉克勒斯與平行英雄故事",
            en: "Heracles and Parallel Hero Stories",
          },
          summary: {
            "zh-Hant": "十二苦差與冥界取犬作為平行英雄傳統展開。",
            en: "The Twelve Labours and the descent for Cerberus unfold as parallel heroic traditions.",
          },
          chronologyMode: "parallel",
          coverageStatus: "partial",
          coverageNote: {
            "zh-Hant":
              "忒修斯 (Theseus) 與阿塔蘭忒 (Atalanta) 已收錄為人物介紹；珀耳修斯 (Perseus) 等完整英雄故事留待後續擴展。",
            en: "Theseus and Atalanta have character profiles; complete hero cycles such as Perseus remain future story expansions.",
          },
          segments: [
            scene("heracles", ["heracles-labour-01", "heracles-labour-02"], {
              sceneId: "heracles-lion-and-hydra",
              narrative: {
                setup: {
                  "zh-Hant":
                    "歐律斯透斯 (Eurystheus) 的勞役由兩隻近乎不可殺死的怪物開始：尼米亞獅 (Nemean Lion) 與勒耳那九頭蛇 (Lernaean Hydra)。",
                  en: "Eurystheus begins the labours with two monsters that seem almost impossible to kill: the Nemean Lion and the Lernaean Hydra.",
                },
                development: {
                  "zh-Hant":
                    "赫拉克勒斯 (Heracles) 先以近身力量殺死獅子，取得獅皮；再在伊俄拉俄斯 (Iolaus) 協助下焚燒蛇頸，壓住九頭蛇再生的力量。",
                  en: "Heracles first kills the lion through close force and takes its skin. Then, with Iolaus helping, he cauterizes the Hydra’s necks and checks its power to grow back.",
                },
                consequence: {
                  "zh-Hant":
                    "最初兩項勞役確立了赫拉克勒斯的模式：他要面對的不只是野獸，而是會改寫英雄身分的戰利品、毒液與神意阻力。",
                  en: "The first two labours establish Heracles’ pattern: he faces not just beasts, but trophies, venom, and divine resistance that reshape his heroic identity.",
                },
              },
              title: {
                "zh-Hant": "獅皮與九頭蛇毒",
                en: "The Lion Skin and the Hydra’s Venom",
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "heracles-kills-nemean-lion",
              },
              focusPath: [
                { kind: "edge", edgeId: "heracles-kills-lernaean-hydra" },
                { kind: "edge", edgeId: "iolaus-helps-heracles-hydra" },
              ],
            }),
            scene("heracles", ["heracles-labour-03", "heracles-labour-04"], {
              sceneId: "heracles-sacred-hind-and-boar",
              narrative: {
                setup: {
                  "zh-Hant":
                    "怪物之後，勞役轉向捕捉：刻律涅牝鹿 (Ceryneian Hind) 屬於阿耳忒彌斯 (Artemis)，厄律曼托斯野豬 (Erymanthian Boar) 則盤踞山野。",
                  en: "After the monsters, the labours turn to capture: the Ceryneian Hind belongs to Artemis, and the Erymanthian Boar haunts the mountain wilds.",
                },
                development: {
                  "zh-Hant":
                    "赫拉克勒斯 (Heracles) 追逐聖鹿並得到阿耳忒彌斯容許交付；他再把野豬趕入雪中捕獲，讓狩獵變成受命、克制與呈交的考驗。",
                  en: "Heracles pursues the sacred hind and obtains Artemis’ permission to deliver it. He then drives the boar into the snow and captures it, turning hunting into a test of command, restraint, and presentation.",
                },
                consequence: {
                  "zh-Hant":
                    "這兩項勞役顯示英雄不能只靠殺戮完成任務；他也要穿過神聖所有權與王命驗收。",
                  en: "These labours show that the hero cannot complete his tasks by killing alone; he must also navigate sacred ownership and royal inspection.",
                },
              },
              title: {
                "zh-Hant": "聖鹿追逐與雪地野豬",
                en: "The Sacred Hind and the Boar in Snow",
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "heracles-captures-ceryneian-hind",
              },
              focusPath: [
                { kind: "edge", edgeId: "artemis-permits-hind-delivery" },
                { kind: "edge", edgeId: "heracles-captures-erymanthian-boar" },
              ],
            }),
            scene("heracles", ["heracles-labour-05", "heracles-labour-06"], {
              sceneId: "heracles-stables-and-birds",
              narrative: {
                setup: {
                  "zh-Hant":
                    "歐律斯透斯 (Eurystheus) 接着把赫拉克勒斯 (Heracles) 推向兩種不同的清除：奧革阿斯馬廄 (Augean Stables) 的污穢，以及斯廷法利斯湖鳥 (Stymphalian Birds) 的威脅。",
                  en: "Eurystheus next drives Heracles toward two kinds of clearing: the filth of the Augean Stables and the danger of the Stymphalian Birds.",
                },
                development: {
                  "zh-Hant":
                    "赫拉克勒斯引水洗淨馬廄，卻因報酬與命令邊界被拒算；到斯廷法利斯湖，他藉雅典娜 (Athena) 的銅響器驚起群鳥再射殺牠們。",
                  en: "Heracles diverts water to cleanse the stables, but the labour is rejected over the boundary of payment and command. At Stymphalus, Athena’s bronze clappers startle the birds so he can shoot them down.",
                },
                consequence: {
                  "zh-Hant":
                    "這段故事把勞役從純粹力量推向制度與工具：完成了事不一定被承認，而神助也可以改變戰鬥方式。",
                  en: "This scene shifts the labours from raw strength to systems and tools: a task may be completed yet denied, and divine help can change the mode of combat.",
                },
              },
              title: {
                "zh-Hant": "一日洗淨與青銅驚鳥",
                en: "A Day’s Cleansing and Bronze against the Birds",
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "heracles-cleans-augean-stables",
              },
              focusPath: [
                { kind: "edge", edgeId: "eurystheus-rejects-augean-labour" },
                { kind: "edge", edgeId: "athena-gives-heracles-clappers" },
                { kind: "edge", edgeId: "heracles-drives-stymphalian-birds" },
              ],
            }),
            scene(
              "heracles",
              [
                "heracles-labour-07",
                "heracles-labour-08",
                "heracles-labour-09",
              ],
              {
                sceneId: "heracles-bull-mares-and-belt",
                narrative: {
                  setup: {
                    "zh-Hant":
                      "中段勞役把赫拉克勒斯 (Heracles) 帶到更遠的勢力範圍：克里特公牛 (Cretan Bull)、狄俄墨得斯的牝馬 (Mares of Diomedes)，以及希波呂忒 (Hippolyta) 的腰帶。",
                    en: "The middle labours carry Heracles into wider realms of power: the Cretan Bull, the Mares of Diomedes, and the belt of Hippolyta.",
                  },
                  development: {
                    "zh-Hant":
                      "他捕回公牛，又制伏會食人的馬；到亞馬遜人 (Amazons) 那裏，原本可能和平交出的腰帶因赫拉 (Hera) 的欺騙變成衝突。",
                    en: "He captures the bull and overcomes the man-eating mares. Among the Amazons, a belt that might have been given peacefully becomes conflict through Hera’s deception.",
                  },
                  consequence: {
                    "zh-Hant":
                      "三項勞役連成一段外征：赫拉克勒斯的勝利越走越遠，也越來越受神與人群誤會牽動。",
                    en: "The three labours form a sequence of outward expeditions: Heracles’ victories reach farther, and increasingly depend on divine and human misunderstanding.",
                  },
                },
                title: {
                  "zh-Hant": "公牛、牝馬與本可交出的腰帶",
                  en: "The Bull, the Mares, and the Belt That Might Have Been Given",
                },
                primaryFocus: {
                  kind: "edge",
                  edgeId: "heracles-captures-cretan-bull",
                },
                focusPath: [
                  { kind: "edge", edgeId: "heracles-captures-mares-diomedes" },
                  { kind: "edge", edgeId: "hera-deceives-amazons" },
                  { kind: "edge", edgeId: "heracles-takes-belt-hippolyta" },
                ],
              },
            ),
            scene("heracles", ["heracles-labour-10", "heracles-labour-11"], {
              sceneId: "heracles-cattle-and-apples",
              narrative: {
                setup: {
                  "zh-Hant":
                    "後段勞役把赫拉克勒斯 (Heracles) 推到世界邊界：革律翁 (Geryon) 的牛群在遠方，赫斯珀里得斯 (Hesperides) 的金蘋果則牽涉阿特拉斯 (Atlas) 的負天之罰。",
                  en: "The later labours push Heracles to the world’s edge: Geryon’s cattle lie far away, and the Golden Apples of the Hesperides involve Atlas and his burden of the sky.",
                },
                development: {
                  "zh-Hant":
                    "赫利俄斯 (Helios) 借出金杯，讓赫拉克勒斯渡過大洋；普羅米修斯 (Prometheus) 指點他利用阿特拉斯取得金蘋果，再設法讓阿特拉斯重新托天。",
                  en: "Helios lends the golden cup so Heracles can cross Ocean. Prometheus advises him to use Atlas for the apples, and Heracles then contrives to make Atlas take the sky back.",
                },
                consequence: {
                  "zh-Hant":
                    "這段故事把勞役推成宇宙尺度的智力與耐力考驗：英雄不只征服敵人，也要短暫承接世界秩序的重量。",
                  en: "This scene turns the labours into a cosmic test of endurance and wit: the hero does not merely defeat opponents, but briefly bears the weight of the world order.",
                },
              },
              title: {
                "zh-Hant": "越過大洋的牛群與金蘋果",
                en: "Cattle beyond Ocean and the Golden Apples",
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "heracles-takes-cattle-geryon",
              },
              focusPath: [
                { kind: "edge", edgeId: "helios-lends-heracles-golden-cup" },
                { kind: "edge", edgeId: "prometheus-advises-heracles-atlas" },
                { kind: "edge", edgeId: "heracles-tricks-atlas-retake-sky" },
              ],
            }),
            sceneGroup(
              [
                { storyId: "heracles", stepId: "heracles-labour-12" },
                {
                  storyId: "underworld",
                  stepId: "underworld-hound-bare-handed",
                },
              ],
              {
                sceneId: "heracles-cerberus-and-return",
                narrative: {
                  setup: {
                    "zh-Hant":
                      "最後一項勞役要求赫拉克勒斯 (Heracles) 走進冥界 (Underworld)，把刻耳柏洛斯 (Cerberus) 帶到活人世界。",
                    en: "The final labour requires Heracles to enter the Underworld and bring Cerberus into the world of the living.",
                  },
                  development: {
                    "zh-Hant":
                      "他從塔伊那戎 (Taenarum) 下行，得到哈得斯 (Hades) 在不得用武器條件下的許可，徒手制伏冥犬並把牠帶回歐律斯透斯 (Eurystheus) 面前。",
                    en: "He descends through Taenarum, receives Hades’ permission on the condition that he use no weapons, subdues the hound bare-handed, and brings it before Eurystheus.",
                  },
                  consequence: {
                    "zh-Hant":
                      "刻耳柏洛斯最後被送回冥界；勞役以越界又復位收束，證明英雄能進入死亡領域，卻不能把冥界秩序永久帶走。",
                    en: "Cerberus is finally returned below. The labours end through boundary-crossing and restoration: the hero can enter death’s realm, but cannot permanently remove its order.",
                  },
                },
                title: {
                  "zh-Hant": "徒手帶回冥府之犬",
                  en: "The Hound Taken Bare-Handed",
                },
                primaryFocus: {
                  kind: "edge",
                  edgeId: "heracles-subdues-cerberus",
                },
                focusPath: [
                  {
                    kind: "edge",
                    edgeId: "heracles-enters-underworld-through-taenarum",
                  },
                  {
                    kind: "edge",
                    edgeId: "hades-permits-heracles-take-cerberus",
                  },
                  {
                    kind: "edge",
                    edgeId: "heracles-returns-cerberus-underworld",
                  },
                ],
              },
            ),
          ],
        },
      ],
    },
    {
      id: "trojan-cycle",
      order: 7,
      title: { "zh-Hant": "特洛伊時代", en: "Trojan Cycle" },
      summary: {
        "zh-Hant":
          "帕里斯在三位女神之間作選擇，《伊利亞特》(Iliad) 危機與特洛伊 (Troy) 陷落構成完整戰爭週期。",
        en: "Paris chooses among the goddesses, the Iliadic crisis follows, and Troy eventually falls.",
      },
      subEras: [
        {
          id: "judgement-and-oaths",
          order: 1,
          title: { "zh-Hant": "選擇與誓盟", en: "Choice and Oaths" },
          summary: {
            "zh-Hant":
              "帕里斯 (Paris) 選擇阿佛洛狄忒 (Aphrodite) 的承諾，海倫 (Helen) 離開斯巴達 (Sparta)，希臘聯軍隨之集結。",
            en: "Paris chooses Aphrodite's offer, Helen leaves Sparta, and the Achaean coalition gathers.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: allSteps("trojan-war", [
            "judgement-of-paris",
            "helen-leaves-sparta",
            "achaean-coalition",
          ]),
        },
        {
          id: "aulis-and-war",
          order: 2,
          title: {
            "zh-Hant": "奧利斯與開戰",
            en: "Aulis and the Opening of War",
          },
          summary: {
            "zh-Hant": "遠征軍在奧利斯 (Aulis) 付出代價後啟航。",
            en: "The expedition sails after paying its cost at Aulis.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: allSteps("trojan-war", ["aulis-and-crossing"]),
        },
        {
          id: "iliad-crisis",
          order: 3,
          title: { "zh-Hant": "《伊利亞特》危機", en: "The Iliadic Crisis" },
          summary: {
            "zh-Hant":
              "瘟疫、阿基里斯 (Achilles) 的憤怒、神明介入及赫克托耳 (Hector) 的死亡。",
            en: "Plague, the wrath of Achilles, divine intervention, and Hector’s death.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: allSteps("trojan-war", [
            "plague-and-wrath",
            "helen-on-walls",
            "gods-on-battlefield",
            "hector-and-city",
            "death-of-patroclus",
            "achilles-and-hector",
          ]),
        },
        {
          id: "deaths-horse-fall",
          order: 4,
          title: {
            "zh-Hant": "英雄之死、木馬與陷城",
            en: "Heroic Deaths, the Horse, and the Fall",
          },
          summary: {
            "zh-Hant":
              "阿基里斯 (Achilles) 死亡，木馬進城，特洛伊 (Troy) 陷落，倖存者離散。",
            en: "Achilles dies, the horse enters the city, Troy falls, and survivors scatter.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          segments: allSteps("trojan-war", [
            "death-of-achilles",
            "horse-sack-returns",
          ]),
        },
      ],
    },
    {
      id: "returns-and-decline",
      order: 8,
      title: { "zh-Hant": "後英雄時代", en: "Returns and Decline" },
      summary: {
        "zh-Hant":
          "戰後歸途、《奧德賽》(Odyssey) 與倖存者記憶構成英雄時代的收束。",
        en: "Post-war returns, the Odyssey, and survivor memory close the heroic age.",
      },
      subEras: [
        {
          id: "greek-returns",
          order: 1,
          title: { "zh-Hant": "希臘諸將歸途", en: "Greek Returns" },
          summary: {
            "zh-Hant":
              "目前只保留已有原典來源的歸途，以及埃涅阿斯 (Aeneas) 逃離特洛伊 (Troy) 的線索。",
            en: "This route retains only sourced return material and Aeneas’s escape already present in the Trojan pack.",
          },
          chronologyMode: "variant",
          coverageStatus: "partial",
          coverageNote: {
            "zh-Hant":
              "Aeneas 的意大利旅程屬後來 Roman／Virgil 路線，不在 Greek core 默認續篇。",
            en: "Aeneas’s Italian journey belongs to the later Roman/Virgilian route and is not the default sequel to the Greek core.",
          },
          segments: [
            {
              storyId: "trojan-war",
              stepId: "horse-sack-returns",
              sceneId: "aeneas-escape-troy",
              title: {
                "zh-Hant": "埃涅阿斯逃出特洛伊",
                en: "Aeneas Escapes Troy",
              },
              narrative: {
                setup: {
                  "zh-Hant":
                    "特洛伊 (Troy) 陷落之夜不只留下勝利者的記憶，也留下倖存者的出口。當木馬之計引出焚城與屠殺，埃涅阿斯 (Aeneas) 的段落從同一場災難中分出來。",
                  en: "The night Troy falls leaves more than the victors’ memory; it also leaves the path of survivors. As the horse leads to fire and slaughter, Aeneas’s moment separates from the same disaster.",
                },
                development: {
                  "zh-Hant":
                    "這裏只聚焦目前希臘核心故事已收錄的一點：埃涅阿斯沒有被寫成戰後遠征的新主角，而是在陷城中逃出，帶着特洛伊殘餘離開毀滅現場。",
                  en: "This scene focuses only on the point already present in the Greek core stories: Aeneas is not turned into the protagonist of a later expedition here, but escapes the sack and leaves the ruin with Trojan survival attached to him.",
                },
                consequence: {
                  "zh-Hant":
                    "因此，後英雄時代的開端不是另一個完整特洛伊章節，而是同一段敘事的後段凝視：城已失去，英雄世代開始散落，只剩少數人把戰爭的餘波帶往別處。",
                  en: "The opening of the post-heroic age is therefore not another full Troy chapter, but a later-facing view within the same narrative beat: the city is gone, the heroic generation scatters, and a few survivors carry the war’s aftermath elsewhere.",
                },
              },
              nodeIds: ["aeneas", "sack-troy", "troy"],
              edgeIds: ["aeneas-escapes-sack", "sack-destroys-troy"],
              primaryFocus: { kind: "edge", edgeId: "aeneas-escapes-sack" },
              chronologyNote: {
                "zh-Hant":
                  "本段只重訪埃涅阿斯 (Aeneas) 逃離特洛伊 (Troy) 的現有記錄，不延伸至維吉爾 (Virgil) 筆下的意大利旅程。",
                en: "This revisits only Aeneas’s sourced escape already in the Greek core; it does not continue into Virgil’s Italian journey.",
              },
            },
          ],
        },
        {
          id: "odyssey",
          order: 2,
          title: { "zh-Hant": "《奧德賽》", en: "Odyssey" },
          summary: {
            "zh-Hant":
              "奧德修斯 (Odysseus) 由特洛伊 (Troy) 歸返伊薩卡 (Ithaca)，並在冥界取得回家所需的知識。",
            en: "Odysseus returns from Troy to Ithaca and gains the knowledge needed for home in the underworld.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene(
              "odyssey",
              ["troy-departure", "bag-of-winds", "fleet-destroyed"],
              {
                sceneId: "odyssey-war-to-lost-fleet",
                narrative: {
                  setup: {
                    "zh-Hant":
                      "奧德修斯 (Odysseus) 離開特洛伊 (Troy) 時，戰爭已結束，但回鄉路還沒有真正開始服從他的意志。",
                    en: "When Odysseus leaves Troy, the war is over, but the road home has not yet begun to obey him.",
                  },
                  development: {
                    "zh-Hant":
                      "埃俄羅斯 (Aeolus) 一度以風袋相助，卻因同伴打開風袋而失去歸程；之後萊斯特律戈涅斯人 (Laestrygonians) 摧毀船隊，使航行從艦隊故事縮成一人的漂流。",
                    en: "Aeolus once helps him with the bag of winds, but the crew opens it and loses the homeward chance. Then the Laestrygonians destroy the fleet, shrinking the voyage from a fleet’s return to one man’s survival.",
                  },
                  consequence: {
                    "zh-Hant":
                      "從這裏開始，歸途不再是勝利者返航，而是奧德修斯在神意、錯誤與損失中保存身份的故事。",
                    en: "From here, the return is no longer a victorious sailing home, but the story of Odysseus preserving himself through divine forces, error, and loss.",
                  },
                },
                title: {
                  "zh-Hant": "從特洛伊到失去艦隊",
                  en: "From Troy to the Lost Fleet",
                },
                primaryFocus: { kind: "edge", edgeId: "odysseus-departs-troy" },
                focusPath: [
                  { kind: "edge", edgeId: "aeolus-helps-odysseus" },
                  { kind: "edge", edgeId: "laestrygonians-destroy-fleet" },
                ],
              },
            ),
            { storyId: "odyssey", stepId: "cyclops-cave" },
            sceneGroup(
              [
                { storyId: "odyssey", stepId: "circes-island" },
                { storyId: "underworld", stepId: "underworld-circe-command" },
              ],
              {
                sceneId: "circe-and-the-command-to-seek-the-dead",
                narrative: {
                  setup: {
                    "zh-Hant":
                      "埃阿埃亞 (Aeaea) 上，喀耳刻 (Circe) 先使奧德修斯 (Odysseus) 的同伴陷入變形危機。",
                    en: "On Aeaea, Circe first brings Odysseus’ companions into the crisis of transformation.",
                  },
                  development: {
                    "zh-Hant":
                      "赫耳墨斯 (Hermes) 幫助奧德修斯抵抗女神的法術；但當喀耳刻由敵意轉為指引時，她給出的下一步不是直接回鄉，而是前往死者所在之處。",
                    en: "Hermes helps Odysseus resist her magic. Yet when Circe turns from threat to guide, the next command is not to sail straight home, but to seek the dead.",
                  },
                  consequence: {
                    "zh-Hant":
                      "喀耳刻把歸途轉向亡者的知識：奧德修斯必須先聽見死者所知道的路，才可能理解如何回家。",
                    en: "Circe bends the homecoming toward the knowledge of the dead: Odysseus must first hear the road known by the dead before he can understand how to get home.",
                  },
                },
                title: {
                  "zh-Hant": "喀耳刻與通往亡者的命令",
                  en: "Circe and the Command toward the Dead",
                },
                primaryFocus: { kind: "edge", edgeId: "circe-causes-descent" },
                focusPath: [
                  { kind: "edge", edgeId: "hermes-helps-odysseus" },
                  {
                    kind: "edge",
                    edgeId: "descent-underworld-reaches-acheron-route",
                  },
                ],
              },
            ),
            sceneGroup(
              [
                { storyId: "odyssey", stepId: "underworld-prophecy" },
                { storyId: "underworld", stepId: "underworld-ocean-edge-rite" },
                { storyId: "underworld", stepId: "underworld-debt-burial" },
                {
                  storyId: "underworld",
                  stepId: "underworld-knowledge-beyond-death",
                },
                {
                  storyId: "underworld",
                  stepId: "underworld-mother-cannot-hold",
                },
                {
                  storyId: "underworld",
                  stepId: "underworld-war-dead-remember",
                },
                {
                  storyId: "underworld",
                  stepId: "underworld-judgement-punishment",
                },
              ],
              {
                sceneId: "odysseus-consults-the-dead",
                title: {
                  "zh-Hant": "向亡者問路",
                  en: "Asking the Dead for the Way Home",
                },
                narrative: {
                  setup: {
                    "zh-Hant":
                      "奧德修斯 (Odysseus) 不能直接回家；他必須在世界邊界召喚亡魂，先聽見死者知道的路。",
                    en: "Odysseus cannot simply sail home; at the world’s edge he must call the dead and hear the road that the dead know.",
                  },
                  development: {
                    "zh-Hant":
                      "埃爾佩諾耳 (Elpenor) 要求安葬，忒瑞西阿斯 (Tiresias) 說出禁忌，安提克勒亞 (Anticlea) 說明死亡的距離；特洛伊亡者與受罰者則把戰爭、榮耀與懲罰留在同一場視野中。",
                    en: "Elpenor asks for burial, Tiresias gives the taboo, Anticlea explains the distance of death, and the Trojan dead and punished figures keep war, glory, and punishment in one vision.",
                  },
                  consequence: {
                    "zh-Hant":
                      "冥界不是順路插曲，而是奧德修斯理解返鄉代價的核心段落。",
                    en: "The underworld is not a passing detour; it is where Odysseus learns the cost of return.",
                  },
                },
                primaryFocus: {
                  kind: "edge",
                  edgeId: "tiresias-helps-odysseus",
                },
                focusPath: [
                  { kind: "edge", edgeId: "odysseus-performs-underworld-rite" },
                  { kind: "edge", edgeId: "elpenor-asks-odysseus-for-burial" },
                  { kind: "edge", edgeId: "ajax-refuses-odysseus-in-hades" },
                ],
              },
            ),
            scene("odyssey", ["sirens-song", "between-monsters"], {
              sceneId: "sirens-scylla-and-charybdis",
              narrative: {
                setup: {
                  "zh-Hant":
                    "從死者那裏得到路線後，奧德修斯 (Odysseus) 仍要穿過兩種不能靠勇氣直接解決的危險：塞壬 (Sirens) 與斯庫拉 (Scylla)、卡律布狄斯 (Charybdis)。",
                  en: "After receiving the route from the dead, Odysseus still has to pass dangers that courage alone cannot solve: the Sirens, then Scylla and Charybdis.",
                },
                development: {
                  "zh-Hant":
                    "他用綁縛與蠟封耳朵的方法聽過塞壬歌聲；再於兩怪之間作痛苦選擇，避開吞船的漩渦，承受斯庫拉奪人的損失。",
                  en: "He hears the Sirens by being bound while the crew’s ears are stopped with wax. Then he chooses between the monsters, avoiding the ship-swallowing whirlpool while accepting Scylla’s human cost.",
                },
                consequence: {
                  "zh-Hant":
                    "這段航程把英雄智慧推到殘酷處：最好的選擇仍然會犧牲人命，而回鄉只是不斷穿過較少毀滅的路。",
                  en: "This scene pushes heroic intelligence into cruelty: the best choice still costs lives, and homecoming means passing through the less destructive path.",
                },
              },
              title: {
                "zh-Hant": "塞壬之歌與兩害之間",
                en: "The Sirens and the Choice between Monsters",
              },
              primaryFocus: { kind: "edge", edgeId: "sirens-tempt-odysseus" },
              focusPath: [
                { kind: "edge", edgeId: "scylla-attacks-odysseus" },
                { kind: "edge", edgeId: "charybdis-threatens-odysseus" },
              ],
            }),
            { storyId: "odyssey", stepId: "cattle-of-sun" },
            scene("odyssey", ["calypsos-captive", "phaeacian-shore"], {
              sceneId: "calypso-storm-and-phaeacians",
              narrative: {
                setup: {
                  "zh-Hant":
                    "奧德修斯 (Odysseus) 在俄癸癸亞 (Ogygia) 被卡呂普索 (Calypso) 留住時，回鄉被不朽誘惑與神的分歧暫時截停。",
                  en: "On Ogygia, Calypso holds Odysseus, and homecoming is stalled by the temptation of immortality and disagreement among the gods.",
                },
                development: {
                  "zh-Hant":
                    "宙斯 (Zeus) 准許他離開；但波塞冬 (Poseidon) 再以風暴打碎筏子。奧德修斯漂到斯刻里亞 (Scheria)，由瑙西卡 (Nausicaa) 與費阿刻斯人 (Phaeacians) 接入最後的歸程。",
                  en: "Zeus permits him to leave, but Poseidon smashes the raft with a storm. Odysseus washes up on Scheria, where Nausicaa and the Phaeacians receive him into the final stage of return.",
                },
                consequence: {
                  "zh-Hant":
                    "他拒絕永生、熬過風暴，終於從孤身漂流回到有人聽他講述的世界；歸鄉即將重新變成社群與身份的問題。",
                  en: "He refuses immortality, survives the storm, and moves from solitary drifting back into a world that can hear his story; homecoming is about to become a matter of community and identity again.",
                },
              },
              title: {
                "zh-Hant": "不朽、最後風暴與費埃克斯人",
                en: "Immortality, the Final Storm, and the Phaeacians",
              },
              primaryFocus: { kind: "edge", edgeId: "zeus-permits-homecoming" },
              focusPath: [
                { kind: "edge", edgeId: "calypso-desires-odysseus" },
                { kind: "edge", edgeId: "poseidon-causes-storm" },
                { kind: "edge", edgeId: "nausicaa-helps-odysseus" },
              ],
            }),
            { storyId: "odyssey", stepId: "home-and-recognition" },
          ],
        },
        {
          id: "atreus-and-survivors",
          order: 3,
          title: {
            "zh-Hant": "阿特柔斯家族與倖存者",
            en: "The House of Atreus and Survivors",
          },
          summary: {
            "zh-Hant": "亡者與倖存者在冥界記憶戰爭後果。",
            en: "The dead and survivors remember the consequences of war in the underworld.",
          },
          chronologyMode: "parallel",
          coverageStatus: "partial",
          coverageNote: {
            "zh-Hant":
              "Orestes、Electra、Neoptolemus 與完整 Oresteia 仍需要新 story pack。",
            en: "Orestes, Electra, Neoptolemus, and a complete Oresteia still require a new story pack.",
          },
          segments: [
            { storyId: "underworld", stepId: "underworld-hermes-leads-shades" },
          ],
        },
        {
          id: "end-of-heroic-age",
          order: 4,
          title: {
            "zh-Hant": "英雄時代終章",
            en: "Epilogue to the Heroic Age",
          },
          summary: {
            "zh-Hant":
              "「神逐漸退場、人類歷史開始」只作收束語，不寫成具體神話關係。",
            en: "“The gods recede and human history begins” remains an epilogue, not a sourced relationship between figures.",
          },
          chronologyMode: "parallel",
          coverageStatus: "editorial-only",
          segments: [],
        },
      ],
    },
  ],
};

export const norseFenrirBindingProgramme: TimelineProgramme = {
  id: "norse-fenrir-binding",
  title: {
    "zh-Hant": "北歐神話時間線",
    en: "Norse Myth Timeline",
  },
  description: {
    "zh-Hant":
      "先以芬里爾受縛串起預言、誓言、神器與諸神黃昏的前影；其他北歐故事會在日後補上。",
    en: "The first route follows Fenrir through prophecy, pledge, artifact and the shadow of Ragnarok; later Norse stories will extend it.",
  },
  chronologyDisclaimer: {
    "zh-Hant":
      "這條北歐路線目前只整理芬里爾受縛，不把整個北歐神話或完整諸神黃昏寫成單一時間表。",
    en: "This Norse route currently follows only the binding of Fenrir. It does not turn all Norse myth or the full Ragnarok cycle into one timetable.",
  },
  eras: [
    {
      id: "before-the-worlds",
      order: 1,
      title: { "zh-Hant": "世界之前", en: "Before the Worlds" },
      summary: {
        "zh-Hant": "尤彌爾、金倫加鴻溝與創世材料仍待日後宇宙起源故事補上。",
        en: "Ymir, Ginnungagap and creation material wait for a later origins story.",
      },
      subEras: [
        {
          id: "first-beings-reserved",
          order: 1,
          title: {
            "zh-Hant": "最初存在仍待整理",
            en: "First Beings Still Reserved",
          },
          summary: {
            "zh-Hant": "芬里爾故事不從創世開始；這裡先保留位置。",
            en: "The Fenrir story does not begin with creation, so this place is reserved.",
          },
          chronologyMode: "parallel",
          coverageStatus: "editorial-only",
          coverageNote: {
            "zh-Hant": "完整創世與世界樹故事需等待 `norse-cosmology`。",
            en: "Creation and world-tree scenes wait for `norse-cosmology`.",
          },
          segments: [],
        },
      ],
    },
    {
      id: "world-tree-and-realms",
      order: 2,
      title: { "zh-Hant": "世界樹與諸界", en: "World Tree and Realms" },
      summary: {
        "zh-Hant": "九界、井泉與世界樹結構仍待日後故事展開。",
        en: "The worlds, wells and world-tree structure remain for later stories.",
      },
      subEras: [
        {
          id: "realms-reserved",
          order: 1,
          title: { "zh-Hant": "諸界位置仍待展開", en: "Realms Still Reserved" },
          summary: {
            "zh-Hant":
              "本路線只使用神族、約頓親族、黑精靈之地與 Lyngvi 這些芬里爾故事需要的地點。",
            en: "This route uses only the divine, giant-kindred, Black Elves and Lyngvi places needed by the Fenrir story.",
          },
          chronologyMode: "parallel",
          coverageStatus: "partial",
          coverageNote: {
            "zh-Hant": "世界樹全貌需等待宇宙起源與諸界故事。",
            en: "The full world-tree structure waits for the origins-and-realms story.",
          },
          segments: [],
        },
      ],
    },
    {
      id: "divine-order-and-dangerous-kin",
      order: 3,
      title: {
        "zh-Hant": "神族秩序與危險血脈",
        en: "Divine Order and Dangerous Kin",
      },
      summary: {
        "zh-Hant": "洛基的子女被預言標記，神族把危機帶入自己的掌控。",
        en: "Loki's children are marked by prophecy, and the gods bring the danger into their own control.",
      },
      subEras: [
        {
          id: "prophecy-and-control",
          order: 1,
          title: { "zh-Hant": "預言與控制", en: "Prophecy and Control" },
          summary: {
            "zh-Hant":
              "芬里爾先被作為預言中的危險子女帶到神前，再在神族眼下長大。",
            en: "Fenrir is first brought before the gods as a prophesied danger and then grows under their eyes.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene("binding-of-fenrir", ["fenrir-dangerous-kindred"], {
              narrative: {
                setup: {
                  "zh-Hant":
                    "故事不是從芬里爾咬人開始，而是從諸神聽見預言開始。洛基與安格爾波達的三名子女在約頓海姆長大，預言卻說災禍會由這一族而來。",
                  en: "The story does not begin with Fenrir biting anyone. It begins when the gods hear a prophecy: Loki and Angrboda's three children are growing in Jotunheim, and disaster is expected from that kin.",
                },
                development: {
                  "zh-Hant":
                    "奧丁沒有把危險留在邊界外，而是命神明把孩子帶到自己面前。芬里爾因此從遠方巨人親族，變成被阿斯神族親眼看管的狼；恐懼也從傳聞轉為治理問題。",
                  en: "Odin does not leave the danger beyond the border. He orders the children brought before him, turning Fenrir from distant giant-kindred into a wolf watched inside Aesir order. Fear becomes a matter of rule, not rumour.",
                },
                consequence: {
                  "zh-Hant":
                    "這一帶回行動沒有解除預言，只是把預言放進神族秩序中心。後來的餵養、試鏈與受縛，都從這個先把危機收進家中的決定展開。誰被帶到神前、誰被視為邊界血脈、誰被末日牽引，已經在第一幕定下壓力。這讓開場不是血統表，而是治理危機；父母、神王、狼與末日同時進場。壓力已無法退回邊界。",
                  en: "The seizure does not undo the prophecy. It places prophecy at the centre of divine order, and the feeding, fetter-tests and final binding all unfold from that first decision to house the danger. Who is brought before the gods, who is marked as border-lineage, and who is pulled toward doom are fixed from the first scene. The opening is therefore not a genealogy table, but a crisis of rule: parents, king of the gods, wolf and doom enter together. The pressure cannot retreat to the border.",
                },
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "odin-seizes-loki-children",
              },
              focusPath: [
                { kind: "edge", edgeId: "loki-father-fenrir" },
                { kind: "edge", edgeId: "angrboda-mother-fenrir" },
                { kind: "node", nodeId: "ragnarok" },
              ],
            }),
            scene("binding-of-fenrir", ["tyr-feeds-growing-wolf"], {
              narrative: {
                setup: {
                  "zh-Hant":
                    "芬里爾被帶入神族住所後，危機沒有立刻爆發，而是每天在眾神眼前長大。牠仍是洛基的子嗣，也是預言中的威脅；但此時牠需要餵養，神明也必須與牠共處。",
                  en: "After Fenrir is brought into the gods’ home, the danger does not erupt at once. It grows day by day before them. He is still Loki’s child and the prophesied threat, but he also has to be fed.",
                },
                development: {
                  "zh-Hant":
                    "其他神明越來越怕靠近，只有提爾敢走到狼身邊，把食物給牠。這不是大戰中的英勇一擊，而是一種日常勇氣：明知牠將來可怕，仍在今天承擔靠近牠的風險。",
                  en: "The other gods become afraid to approach, while Tyr alone dares to go near and feed him. This is not a heroic blow in battle, but a daily courage: knowing the wolf may become terrible and still stepping close today.",
                },
                consequence: {
                  "zh-Hant":
                    "因此，提爾與芬里爾之間先有一段信任痕跡。到 Lyngvi 島上需要有人把手放入狼口時，這份曾經靠近的關係會變成最沉重的保證。牠在此還不是單純敵人，而是被神族餵養、恐懼又依賴的存在。這使後來的背信不只是計策，也是一段關係的斷裂，並讓提爾之勇先於戰鬥被看見。",
                  en: "A trace of trust is therefore built between Tyr and Fenrir. When a hand later has to enter the wolf’s mouth on Lyngvi, that earlier closeness becomes the pledge with the heaviest cost. At this point Fenrir is not a simple enemy, but a being the gods feed, fear and depend on managing. That makes the later betrayal more than a tactic: it breaks a relationship, and it lets Tyr’s courage appear before battle.",
                },
              },
              primaryFocus: { kind: "edge", edgeId: "tyr-feeds-fenrir" },
              focusPath: [
                { kind: "node", nodeId: "tyr" },
                { kind: "node", nodeId: "fenrir" },
              ],
            }),
          ],
        },
      ],
    },
    {
      id: "broken-oaths-and-bindings",
      order: 4,
      title: { "zh-Hant": "破裂誓言與束縛", en: "Broken Oaths and Bindings" },
      summary: {
        "zh-Hant": "芬里爾掙斷兩條鐵鏈，Gleipnir 出現，提爾以手承擔保證代價。",
        en: "Fenrir breaks two iron fetters, Gleipnir appears, and Tyr bears the cost of the pledge.",
      },
      subEras: [
        {
          id: "failed-fetters",
          order: 1,
          title: { "zh-Hant": "鐵鏈失敗", en: "Failed Fetters" },
          summary: {
            "zh-Hant": "Lædingr 與 Drómi 先後失效，神族知道普通力量不足。",
            en: "Laedingr and Dromi fail in turn, showing the gods that ordinary force is not enough.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene("binding-of-fenrir", ["laeding-breaks"], {
              narrative: {
                setup: {
                  "zh-Hant":
                    "當芬里爾的力量變得不可忽視，諸神把第一條鐵鏈 Lædingr 帶到牠面前，說這只是試力。",
                  en: "When Fenrir's strength can no longer be ignored, the gods bring the first iron fetter, Laedingr, and present it as a test of power.",
                },
                development: {
                  "zh-Hant":
                    "芬里爾讓他們綁上，隨即一伸展便把鐵鏈崩開。第一次試探沒有控制牠，反而公開證明普通束縛不夠。",
                  en: "Fenrir lets them bind him, then stretches and snaps the fetter apart. The first attempt does not control him; it proves openly that an ordinary binding is not enough.",
                },
                consequence: {
                  "zh-Hant":
                    "神族仍未放棄，但這一刻已把故事從疑懼推向升級：下一條束縛必須更重，也必須更像真正陷阱。",
                  en: "The gods do not stop, but the scene escalates the fear. The next fetter will have to be heavier, and it will feel more like a real trap.",
                },
              },
              primaryFocus: { kind: "edge", edgeId: "fenrir-breaks-laeding" },
              focusPath: [{ kind: "node", nodeId: "binding-of-fenrir-event" }],
            }),
            scene("binding-of-fenrir", ["dromi-breaks"], {
              narrative: {
                setup: {
                  "zh-Hant":
                    "第二條束縛 Drómi 比 Lædingr 更強，諸神再次把試驗說成榮耀與名聲的挑戰。",
                  en: "The second fetter, Dromi, is stronger than Laedingr, and the gods again frame the test as a challenge of honour and fame.",
                },
                development: {
                  "zh-Hant":
                    "芬里爾明白難度提高，仍接受試驗；牠猛烈掙動，Drómi 也被扯斷。第二次失敗使問題更加清楚：力量只會暴露牠更大的力量。",
                  en: "Fenrir knows the challenge has grown harder, yet accepts it. He strains violently and Dromi also breaks. The second failure clarifies the problem: force only reveals greater force.",
                },
                consequence: {
                  "zh-Hant":
                    "從此，諸神不能再靠更厚重的鐵鏈。他們要找的不是更大的重量，而是芬里爾無法用同一種力量理解的束縛。",
                  en: "From here the gods cannot merely seek heavier iron. They need a binding Fenrir cannot answer by strength alone.",
                },
              },
              primaryFocus: { kind: "edge", edgeId: "fenrir-breaks-dromi" },
              focusPath: [{ kind: "edge", edgeId: "fenrir-breaks-laeding" }],
            }),
          ],
        },
        {
          id: "gleipnir-and-pledge",
          order: 2,
          title: {
            "zh-Hant": "Gleipnir 與保證",
            en: "Gleipnir and the Pledge",
          },
          summary: {
            "zh-Hant":
              "神族轉向黑精靈之地的工藝，最後在 Lyngvi 島以提爾的手作保。",
            en: "The gods turn to craft from the Black Elves' region and then rely on Tyr's hand at Lyngvi.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene("binding-of-fenrir", ["gleipnir-from-dark-elves"], {
              narrative: {
                setup: {
                  "zh-Hant":
                    "兩條鐵鏈失敗後，奧丁把問題交給另一種力量：隱秘工藝。Skirnir 被派往黑精靈之地，請侏儒製成名為 Gleipnir 的束帶。",
                  en: "After two iron fetters fail, Odin turns to a different kind of power: hidden craft. Skirnir is sent to the region of the Black Elves so dwarves can make the binding called Gleipnir.",
                },
                development: {
                  "zh-Hant":
                    "這條束帶不像武器，反而像柔軟細絲；它由貓足聲、女人鬍鬚、石根、熊筋、魚氣與鳥唾等不可能之物構成。外表越無害，來歷越不透明。",
                  en: "The ribbon does not look like a weapon. It is soft and slender, made from impossible things such as a cat’s footfall, a woman’s beard, a rock’s roots, bear sinews, fish breath and bird spittle.",
                },
                consequence: {
                  "zh-Hant":
                    "受縛故事由此離開蠻力競賽。諸神不再證明自己更強，而是準備以看似微弱、其實無法破解的工藝逼芬里爾進入更危險的誓信局面。",
                  en: "The story leaves the contest of brute force. The gods are no longer proving they are stronger; they are preparing a fragile-looking craft that will force Fenrir into a more dangerous problem of trust.",
                },
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "odin-commissions-gleipnir",
              },
              focusPath: [
                { kind: "edge", edgeId: "gleipnir-made-in-svartalfheim" },
                { kind: "node", nodeId: "gleipnir" },
              ],
            }),
            scene("binding-of-fenrir", ["pledge-at-lyngvi"], {
              narrative: {
                setup: {
                  "zh-Hant":
                    "諸神把芬里爾帶到阿姆斯瓦特尼爾湖中的 Lyngvi 島，展示那條看似輕柔的 Gleipnir。地點從神族住所移到受縛舞台，氣氛也變得更像正式試驗。",
                  en: "The gods bring Fenrir to Lyngvi, the island in Lake Amsvartnir, and show him the soft-looking Gleipnir. The place moves the story from the gods’ home into a binding stage.",
                },
                development: {
                  "zh-Hant":
                    "芬里爾看出束帶可疑：若它真的微弱，掙脫它沒有名聲；若它藏有陷阱，神族可能不會釋放牠。於是牠要求有人把手放進自己口中作保。",
                  en: "Fenrir sees the trap. If the ribbon is weak, breaking it brings no fame; if it hides danger, the gods may not release him. He therefore demands that someone place a hand in his mouth as pledge.",
                },
                consequence: {
                  "zh-Hant":
                    "提爾把手放進芬里爾口中，試驗才真正開始。Gleipnir 套上後，束縛已不再只是比力氣；束帶綁住狼，提爾的手則把神族的信用押了上去。",
                  en: "Tyr places his hand in Fenrir's mouth, and the trial finally begins. Once Gleipnir is placed, the binding is no longer only a contest of strength: the band holds the wolf, while Tyr's hand puts the gods' honour at stake.",
                },
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "tyr-pledges-hand-to-fenrir",
              },
              focusPath: [
                { kind: "edge", edgeId: "binding-at-lyngvi" },
                { kind: "node", nodeId: "amsvartnir-lyngvi" },
              ],
            }),
            scene("binding-of-fenrir", ["tyr-loses-hand"], {
              narrative: {
                setup: {
                  "zh-Hant":
                    "Gleipnir 套上後，芬里爾立即用全力掙扎。前兩條鐵鏈都因牠的力量而破裂；這一次，束帶卻不按鐵鏈的規則反應。",
                  en: "Once Gleipnir is placed on him, Fenrir strains with all his power. The first two fetters broke because of his strength; this ribbon does not answer by the rules of iron.",
                },
                development: {
                  "zh-Hant":
                    "牠越掙，Gleipnir 反而越收越緊。諸神看見束縛成功，沒有依約釋放牠；芬里爾也立刻明白保證已被背棄，便咬下仍在口中的提爾之手。",
                  en: "The more he struggles, the tighter Gleipnir becomes. The gods see the binding work and do not release him. Fenrir understands that the pledge has been broken and bites off Tyr’s hand.",
                },
                consequence: {
                  "zh-Hant":
                    "這一幕讓勝利帶著傷口。神族暫時控制了預言中的狼，卻把欺瞞寫進自己的秩序；提爾的失手成為受縛成立所付出的可見代價。它也改變了提爾與芬里爾的關係：原先敢餵養狼的神，現在成了神族策略中被犧牲的保證。芬里爾的反咬不是隨意暴力，而是對破約的即時回答；從此勝利永遠帶著一隻缺席的手，代價被看見。",
                  en: "The victory is visibly wounded. The gods have controlled the prophesied wolf for now, but deception enters their order, and Tyr’s lost hand becomes the cost that made the binding possible. It also changes Tyr and Fenrir’s relationship: the god who once dared feed the wolf now becomes the sacrificed pledge in the gods’ strategy. Fenrir’s bite is not random violence, but an immediate answer to the broken pledge; from now on the victory carries an absent hand, and the cost is visible.",
                },
              },
              primaryFocus: { kind: "edge", edgeId: "fenrir-bites-tyr" },
              focusPath: [
                { kind: "edge", edgeId: "gleipnir-binds-fenrir" },
                { kind: "edge", edgeId: "tyr-pledges-hand-to-fenrir" },
              ],
            }),
          ],
        },
      ],
    },
    {
      id: "ragnarok-shadow",
      order: 5,
      title: { "zh-Hant": "諸神黃昏前影", en: "Shadow of Ragnarok" },
      summary: {
        "zh-Hant": "受縛暫時阻止芬里爾，卻把牠留到神族命運之時。",
        en: "The binding restrains Fenrir for now, yet keeps him until the fate of the gods.",
      },
      subEras: [
        {
          id: "fastened-until-doom",
          order: 1,
          title: { "zh-Hant": "留到命運之時", en: "Fastened Until Doom" },
          summary: {
            "zh-Hant": "芬里爾被固定到諸神黃昏，受縛成為延遲而非終結。",
            en: "Fenrir is fastened until Ragnarok, making the binding a delay rather than an ending.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene("binding-of-fenrir", ["bound-until-ragnarok"], {
              narrative: {
                setup: {
                  "zh-Hant":
                    "芬里爾咬斷提爾之手後，故事沒有以單純得勝結束。諸神把 Gleipnir 與石、鏈等固定方式連在一起，又把劍塞入狼口，使牠不能自由咬噬。",
                  en: "After Fenrir bites off Tyr’s hand, the story does not end as a simple triumph. The gods fasten Gleipnir with stone and chain, and place a sword in the wolf’s mouth so he cannot bite freely.",
                },
                development: {
                  "zh-Hant":
                    "這些固定動作把危機鎖在 Lyngvi 的場景中，但文本隨即指出期限：芬里爾會留在那裡，直到諸神黃昏。受縛不是消滅，只是把危險推向命運指定的時間。",
                  en: "Those fastenings lock the crisis into the scene at Lyngvi, but the text immediately names the limit: Fenrir will remain there until Ragnarok. The binding is not destruction, only postponement.",
                },
                consequence: {
                  "zh-Hant":
                    "預言因此仍然有效。諸神不殺芬里爾，因為牠已被說成奧丁之禍；受縛之後留下的不是安心，而是一條被束住、卻仍通往末日的命運線。故事在這裡沒有真正完成，只是等待諸神黃昏日後接回這個危機。受縛只買到時間，沒有改寫結局。危機仍在。",
                  en: "The prophecy therefore remains alive. The gods do not kill Fenrir because he has been named as Odin’s bane; the binding leaves behind a bound danger that still points toward doom. The story is not truly solved here. It waits for Ragnarok to take the crisis up again. The binding buys time; it does not rewrite the ending. The threat remains.",
                },
              },
              primaryFocus: {
                kind: "edge",
                edgeId: "fenrir-remains-until-ragnarok",
              },
              focusPath: [
                { kind: "edge", edgeId: "gleipnir-binds-fenrir" },
                { kind: "edge", edgeId: "binding-at-lyngvi" },
                { kind: "node", nodeId: "odin" },
              ],
              chronologyNote: {
                "zh-Hant":
                  "這裡只保留芬里爾受縛故事中的未來期限，完整諸神黃昏需等待後續故事。",
                en: "This keeps only the future horizon inside the Fenrir story; the full Ragnarok sequence waits for a later story.",
              },
            }),
          ],
        },
      ],
    },
    {
      id: "heroic-lays-and-saga-branch",
      order: 6,
      title: {
        "zh-Hant": "英雄詩與傳奇支線",
        en: "Heroic Lays and Saga Branch",
      },
      summary: {
        "zh-Hant": "西格爾德與沃爾松族等英雄材料會作為日後分支處理。",
        en: "Sigurd, the Volsungs and heroic material belong to a later heroic cycle.",
      },
      subEras: [
        {
          id: "volsungs-reserved",
          order: 1,
          title: {
            "zh-Hant": "沃爾松族仍待整理",
            en: "Volsungs Still Reserved",
          },
          summary: {
            "zh-Hant":
              "這些故事尚未放進芬里爾受縛這段故事；此處先保留它們在北歐神話中的位置。",
            en: "These stories do not belong to the binding of Fenrir, so this position is reserved.",
          },
          chronologyMode: "parallel",
          coverageStatus: "editorial-only",
          coverageNote: {
            "zh-Hant": "英雄詩與 saga 材料需等待 `norse-volsungs`。",
            en: "Heroic lays and saga material wait for `norse-volsungs`.",
          },
          segments: [],
        },
      ],
    },
  ],
};

export const norseStoryModeProgramme: TimelineProgramme = {
  id: "norse-story-mode-route",
  title: {
    "zh-Hant": "北歐神話時間線",
    en: "Norse Myth Timeline",
  },
  description: {
    "zh-Hant":
      "以目前已開放的北歐故事模式章節，串連創世、神族秩序、巨人試煉、束縛、死亡、末日與英雄支線。",
    en: "The current Norse Story Mode route links creation, divine order, giant-world tests, bindings, death, doom, and the heroic cycle.",
  },
  chronologyDisclaimer: {
    "zh-Hant":
      "這是按來源與故事閱讀順序整理的導覽路線，不是單一年表。平行片段、saga 支線與不同來源的排序仍保留彈性。",
    en: "This is a source-led reading route, not a single calendar. Parallel fragments, saga episodes, and source-specific ordering remain flexible.",
  },
  eras: [
    {
      id: "norse-origins-and-worlds",
      order: 1,
      title: { "zh-Hant": "創世與諸界", en: "Origins and Worlds" },
      summary: {
        "zh-Hant":
          "由金倫加鴻溝、第一生命、世界創造、人類起源與世界樹建立北歐宇宙框架。",
        en: "Ginnungagap, first life, world-making, human origins, and Yggdrasil establish the Norse cosmic frame.",
      },
      subEras: [
        {
          id: "norse-cosmology-route",
          order: 1,
          title: {
            "zh-Hant": "世界之前、世界樹與命運",
            en: "Before Worlds, Yggdrasil, and Fate",
          },
          summary: {
            "zh-Hant":
              "創世不是靜態地圖，而是寒熱、生命、親族、世界材料與命運逐步成形。",
            en: "Creation unfolds through heat and cold, first life, kinship, world matter, and fate.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene(
              "norse-cosmology",
              ["gap-before-worlds", "ymir-from-rime", "audhumla-and-buri"],
              {
                title: {
                  "zh-Hant": "裂隙中出現第一生命",
                  en: "First Life in the Gap",
                },
                narrative: sceneNarrative(
                  "故事先回到世界尚未成形之處。大地、海、天空和神宮都還不存在，只有金倫加鴻溝隔開寒冷的尼福爾海姆與火熱的穆斯貝爾；寒霜、熱氣與深處泉源先佔住創世的兩端。",
                  "寒與熱在裂隙中相遇，滴落物被賦予生命，尤彌爾由此出現。接着歐德姆布拉以乳汁滋養他，又從鹽冰中舔出布里，讓巨人生命與神族祖先在同一個開端裏並列。",
                  "這段開場把北歐創世寫成一種張力，而不是從空白中突然完成的世界。後來的神族秩序、巨人血脈和世界材料，都從這個寒熱相觸、生命先後浮現的場面開始。神明、巨人與世界本身因此先共享同一個不穩定源頭；這份壓力也會在後來每次衝突中不斷回返。",
                  "The route begins before earth, sea, sky, or divine halls exist. Ginnungagap separates cold Niflheim from fiery Muspell, and the first conditions of creation are already under pressure.",
                  "Rime and heat meet in the gap. Ymir comes to life, Audhumla feeds him with milk, and she licks Buri from salty ice. Giant life and divine ancestry therefore appear side by side.",
                  "Creation begins as tension, not as a blank page. Divine order, giant kinship, and the material of the world all grow from this first contact of cold, heat, and life; that pressure keeps returning in later conflict.",
                ),
              },
            ),
            scene(
              "norse-cosmology",
              ["borr-bestla-brothers", "ymir-becomes-world"],
              {
                title: {
                  "zh-Hant": "三兄弟把尤彌爾變成世界",
                  en: "The Brothers Make a World from Ymir",
                },
                narrative: sceneNarrative(
                  "布里的後代把創世從生命開端帶進親族秩序。布里生博爾；博爾與巨人之女貝絲特拉生下奧丁、維利與維。三兄弟既連着神族祖先，也帶着巨人血脈。",
                  "故事隨即轉向更激烈的創造：三兄弟殺死尤彌爾，把他的身體改造成世界。血成海，肉成大地，骨骼成山，頭骨成天，世界不是平靜建成，而是由一個古老生命的身體重新分配而來。",
                  "因此，北歐宇宙從一開始就帶着親族混合與暴力改造。神明成為世界創造者，但他們創造的材料來自巨人；之後神與巨人的衝突，也不可能被說成兩個完全無關的族群。世界的每一層形狀都保留着被殺巨人的痕跡，創造與殺戮從此成為諸界共同而難解的記憶。",
                  "Buri’s line carries creation into kinship. Buri begets Borr, and Borr with Bestla has Odin, Vili, and Ve. The world-makers are tied to both divine ancestry and giant blood.",
                  "The story then turns creation into a violent remaking. The brothers kill Ymir and fashion the world from his body: sea from blood, earth from flesh, mountains from bone, and sky from skull.",
                  "The cosmos begins with mixed kinship and transformed violence. The gods make the world, but their material comes from a giant being, so later divine-giant conflict is never cleanly separate. Creation and killing remain part of the same memory.",
                ),
              },
            ),
            scene("norse-cosmology", ["ask-and-embla"], {
              title: {
                "zh-Hant": "阿斯克與恩布拉得到生命",
                en: "Ask and Embla Receive Life",
              },
              narrative: sceneNarrative(
                "世界被造成後，故事沒有停在山海與天空。奧丁、維利與維在海邊遇見兩段木頭，阿斯克與恩布拉還沒有生命、呼吸、理智、面貌或說話能力。",
                "三兄弟分別給予他們生命氣息、理解與感官形態，使木頭成為第一對人。人類不是從神族宮廷裏誕生，也不是自然長成，而是在世界邊緣被神明賦予能活、能想、能感受的條件。",
                "這一幕把創世從宇宙材料推到人間。阿斯克與恩布拉使中庭不只是世界的一塊地方，也成為人類故事的起點；後面的神族命運、巨人威脅與末日，都會牽動這個被創造出來的人間。人的脆弱與尊嚴也在這裏同時開始，中庭從此有了需要被守護、也會見證神明失敗的居民。",
                "After the world is shaped, the story does not stop with land, sea, and sky. Odin, Vili, and Ve find Ask and Embla by the shore as lifeless wood without breath, sense, or speech.",
                "The brothers give them life, understanding, and bodily perception. Humanity is not born in a palace or grown without agency; it is formed when the gods give wood the conditions of life.",
                "The scene moves creation toward the human world. Midgard becomes not only a place but the beginning of human story, later caught between divine order, giant threat, and doom. It now has inhabitants to protect, and witnesses to divine failure.",
              ),
            }),
            scene("norse-cosmology", ["sol-and-mani-courses"], {
              title: {
                "zh-Hant": "日月開始運行",
                en: "Sun and Moon Begin Their Courses",
              },
              narrative: sceneNarrative(
                "創世造出大地、天空與人間後，北歐故事還要回答一件很具體的事：日和月如何在這個世界裏運行。這不是純粹的天文背景，而是新世界開始有節奏的時刻。",
                "諸神把蘇爾與瑪尼置於天上。蘇爾駕着為照亮世界而造的日車；瑪尼掌管月亮的行程與盈虧。於是光不再只是從穆斯貝爾飛出的火花，而成為每天都會走過天空的安排。",
                "這個安排讓宇宙有了可辨認的日子和月份，也讓後來的末日威脅更具體：狼追逐日月時，危險的不只是光暗，而是整個已被安排好的時間秩序。世界開始運作，卻從一開始就不是不受威脅的。",
                "After earth, sky, and humanity are made, the myth asks a practical question: how do sun and moon move through this new world? This is not mere astronomical scenery, but the moment the world gains rhythm.",
                "The gods set Sol and Mani in the heavens. Sol drives the chariot made to light the world, while Mani directs the moon’s course and phases. Light becomes not only a spark from Muspell but an arrangement that crosses the sky each day.",
                "The arrangement makes days and months recognisable, and gives later doom a sharper meaning. When wolves pursue sun and moon, the threat is not only darkness but the ordered time that has been set in motion.",
              ),
            }),
            scene("norse-cosmology", ["wolves-pursue-sun-and-moon"], {
              title: {
                "zh-Hant": "日月並非安穩運行",
                en: "Sun and Moon Do Not Travel Securely",
              },
              narrative: sceneNarrative(
                "日車和月亮一旦開始運行，天空立刻有了壓力。北歐神話沒有把日月寫成永遠安穩的背景：一頭狼緊隨日車，另一頭在月道前方等候。",
                "日與月仍照常穿過天際，可是它們的疾行不再只是規律的移動。這裡還不是末日；危險只以追趕的姿態存在，讓每一次白晝和夜晚都帶著尚未落下的陰影。",
                "到了諸神黃昏，天空的崩壞便不是突如其來的災變，而是漫長追逐終於得逞。光亮、月相和可辨認的日子，從創世開始就必須不停向前。",
                "As soon as the sun’s chariot and the moon begin to move, the sky comes under pressure. Norse myth does not treat them as permanently secure scenery: one wolf follows the daylight path, while another waits ahead on the moon’s way.",
                "Sun and moon continue across the heavens, yet their speed is no longer mere regular motion. This is not doom itself; danger appears only as pursuit, leaving every day and night under a shadow that has not yet fallen.",
                "At Ragnarok, the sky’s collapse is therefore not a sudden disaster but the success of a very long chase. Light, lunar change, and recognisable days have had to keep moving since the world began.",
              ),
            }),
            scene(
              "norse-cosmology",
              ["yggdrasil-three-roots", "norns-at-urd-well"],
              {
                title: {
                  "zh-Hant": "世界樹連接諸界與命運",
                  en: "Yggdrasil Connects Worlds and Fate",
                },
                narrative: sceneNarrative(
                  "人類出現後，故事把視野拉回整個宇宙的支架。世界樹不是裝飾性的背景樹，而是把神族、巨人、死者與人間連在一起的結構；它的根伸向不同方向，也把不同力量拉到同一棵樹下。",
                  "兀兒德之泉旁，諾倫替人類分配壽命。她們不只是旁觀者，而是在世界樹旁把生命長短、命運次序與宇宙穩定連起來。世界的存在因此不只靠空間，也靠時間與命數維持。",
                  "這段場景讓北歐宇宙變成可被閱讀的整體：上有神明，下有井泉與根，旁有巨人與亡者，人類壽命也被放入其中。之後任何受縛、死亡或末日，都會回到這棵樹承受壓力。命運不在世界之外，而是在世界支架旁運作；連續的事件也因此先顯出命運的形狀。",
                  "After humanity appears, the route widens again to the structure of the cosmos. Yggdrasil is not a decorative tree; it connects gods, giants, the dead, and the human world.",
                  "At the Well of Urd, the Norns allot human lives. They connect lifespan, fate, and cosmic order beside the tree, so the world is held by time as well as space.",
                  "This scene makes the Norse cosmos readable as one structure. Gods, wells, roots, giants, the dead, and mortal lives all stand within a frame that later bindings, deaths, and doom will strain. The timeline first becomes a line of fate.",
                ),
              },
            ),
            scene("norse-cosmology", ["nidhogg-bifrost-alfheim"], {
              title: {
                "zh-Hant": "樹根、橋與光明國度",
                en: "Roots, Bridge, and Bright Realm",
              },
              narrative: sceneNarrative(
                "世界樹建立後，故事補上它周圍並不安穩的一面。尼德霍格在根部啃咬，彩虹橋成為通往神明世界的道路，亞爾夫海姆則在神族分配中被標示出來。",
                "這三個元素把世界樹從單純的宇宙柱變成有損耗、有通道、有分配的結構。樹根會受傷，橋可以連接也可能斷裂，光明精靈之地則顯示諸界不是只有神宮、巨人世界和人間。",
                "創世故事因此收在一個動態宇宙裏：世界已經有形狀和路徑，卻不是永遠穩定的完成品。後來彩虹橋在諸神黃昏斷裂、世界樹震動，都不是突然出現的新問題，而是這裏早已埋下的壓力。宇宙從成立那刻起，就已包含磨損與通行的兩面，穩定從來都只是暫時維持的狀態。",
                "Once Yggdrasil is established, the story adds its vulnerable surroundings. Nidhogg gnaws below, Bifrost serves as a road to the gods, and Alfheim is marked within divine distribution.",
                "The tree becomes more than a pillar. Its roots can be harmed, its bridge can connect or break, and the worlds include more than Asgard, Jotunheim, and Midgard.",
                "The creation route ends with a living, pressured cosmos. Later scenes where Bifrost breaks or Yggdrasil trembles grow naturally from this earlier structure. Stability is something maintained, not assumed.",
              ),
            }),
          ],
        },
        {
          id: "norse-rigsthula-route",
          order: 2,
          title: { "zh-Hant": "里格之歌：三個家戶", en: "Rigsthula: Three Households" },
          summary: {
            "zh-Hant": "里格先後走進三個家戶，殘篇最後把青年康推向尚未寫出的遠方；這是詩的來處敘說，不是一部社會史。",
            en: "Rig enters three households in turn, and the fragment ends by sending Young Kon toward an unwritten distance. This is the poem’s origin account, not a social history.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "依開頭散文與《里格之歌》1–45 節安排。手稿殘缺，里格與海姆達爾的識別只保留為開頭版本說法；烏鴉的邀請後不添加王位結局。",
            en: "Ordered by the opening prose and Rigsthula stanzas 1–45. The manuscript is damaged; Rig’s identification with Heimdall remains only the opening version’s claim, and no royal ending is added after the crow’s invitation.",
          },
          segments: [
            scene("norse-rigsthula", ["first-household", "thrall-born"], {
              title: { "zh-Hant": "第一個爐邊與特雷爾", en: "The First Hearth and Thrall" },
              narrative: sceneNarrative(
                "里格走進艾與埃達的家戶，與兩人同席，又在床上留了三夜。九月後，埃達生下特雷爾。",
                "出生的一節殘缺，開頭對里格身分的說法也不穩固。時間線因此只跟隨這首詩如何把一名訪客和一個家戶放進同一段來處敘事。",
                "特雷爾不是被用來概括任何真實人群的標籤。這裡讀的是詩先把一段家戶故事說成起點，再讓它成為第一支的由來。",
                "Rig enters the household of Ai and Edda, shares their table, and stays three nights in bed. Nine months later, Edda bears Thrall.",
                "The birth stanza is damaged, and the opening’s account of Rig’s identity is uncertain. The route therefore follows only how this poem places a visitor and a household inside one origin sequence.",
                "Thrall is not a label used to summarize any real population. Here we read how the poem first makes a household story into the origin of its first branch.",
              ),
            }),
            scene("norse-rigsthula", ["thrall-and-thir", "karl-born"], {
              title: { "zh-Hant": "西爾的到來與第二家戶", en: "Thir’s Arrival and the Second Household" },
              narrative: sceneNarrative(
                "西爾走進特雷爾的家，兩人同住，後面接著一串子女名字。里格隨後到第二家戶，九月後阿瑪生下卡爾。",
                "兩段都把家、伴侶與後代連在一起，但並不要求讀者把長長的名字全變成永久人物，或把勞務描述當成天生價值。",
                "詩透過重複的來訪結構換到下一支；時間線保留這個節奏，卻不替它補出一套真實社會的固定階序。",
                "Thir enters Thrall’s home, the two live together, and a long list of children follows. Rig then reaches the second household, and nine months later Amma bears Karl.",
                "Both passages join home, partners, and descendants, yet neither requires us to make every listed name permanent or turn work descriptions into inborn worth.",
                "The poem uses a repeated visit structure to move to its next branch. The route retains that rhythm without supplying a fixed social hierarchy beyond the poem.",
              ),
            }),
            scene("norse-rigsthula", ["karl-and-snor", "jarl-born"], {
              title: { "zh-Hant": "鑰匙、戒指與第三家戶", en: "Keys, Rings, and the Third Household" },
              narrative: sceneNarrative(
                "斯諾爾帶著鑰匙和面紗來到卡爾家，兩人交換戒指、整理床鋪、建立居所。第三段接著讓莫希爾生下雅爾。",
                "詩把每一段家戶寫得不同：這一段把耕作、家產與婚配放在一起，下一段則逐步轉向武器、土地與王權。",
                "這不是一張把人排好位置的社會圖。它是一首在殘缺手稿中保存下來的文化詩，讓家戶敘事一層一層走向它想稱頌的王權。",
                "Snor arrives at Karl’s home with keys and veil; the two exchange rings, prepare a bed, and make a dwelling. The third sequence then has Mothir bear Jarl.",
                "The poem writes each household differently: this one joins cultivation, property, and marriage, while the next moves toward weapons, land, and kingship.",
                "This is not a chart that puts people in their places. It is a cultural poem, preserved in a damaged manuscript, that layers household stories toward the kingship it wants to praise.",
              ),
            }),
            scene("norse-rigsthula", ["rig-teaches-jarl", "kon-learns-runes"], {
              title: { "zh-Hant": "符文教給雅爾，斷頁留給康", en: "Runes for Jarl; a Break for Kon" },
              narrative: sceneNarrative(
                "里格回來教雅爾符文、稱他為子；雅爾作戰、獲地，並迎娶艾爾娜。康在兩人的子嗣中最年幼，接著學會符文。",
                "烏鴉建議康前往丹與丹普的廳堂，手稿就在邀請之後中斷。它沒有說他出征、勝利或登位。",
                "因此這條線停在一個被推向遠方的年輕人身上：王權的語言已經出現，結局卻沒有留下。這份未完成正是文本的邊界。",
                "Rig returns to teach Jarl runes and call him son; Jarl fights, gains land, and marries Erna. Kon, their youngest child, then learns runes.",
                "A crow advises Kon to go to the halls of Dan and Danp, and the manuscript breaks after the invitation. It does not say that he rides out, wins, or becomes king.",
                "The route therefore stops with a young person sent toward the distance: royal language has appeared, but its outcome has not survived. That incompletion is the text’s boundary.",
              ),
            }),
          ],
        },
        {
          id: "norse-vafthruthnismol-route",
          order: 3,
          title: {
            "zh-Hant": "瓦夫蘇魯德尼爾之歌：一場問答",
            en: "Vafthruthnismol: A Contest of Questions",
          },
          summary: {
            "zh-Hant": "奧丁在巨人的廳堂裡先作客、再提問；詩中的創世答覆屬於這場較量，而不是唯一宇宙年表。",
            en: "Odin enters a giant’s hall first as guest and then as questioner; the poem’s creation answers belong to this contest, not to a single cosmic calendar.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "依問答展開的順序安排，並與既有諸神黃昏路線分開：第44至53節不在本線重述。",
            en: "Ordered by the dialogue’s turns and kept apart from the existing Ragnarok route: stanzas 44–53 are not repeated here.",
          },
          segments: vafthruthnismolTimelineSegments,
        },
      ],
    },
    {
      id: "norse-divine-order-era",
      order: 2,
      title: { "zh-Hant": "諸神秩序", en: "Divine Order" },
      summary: {
        "zh-Hant": "奧丁、神族家庭、華納和解、婚姻協議與寶物工藝構成神族秩序。",
        en: "Odin, divine households, Vanir settlement, marriage compacts, and crafted treasures form divine order.",
      },
      subEras: [
        {
          id: "norse-divine-order-route",
          order: 1,
          title: {
            "zh-Hant": "神族和解與寶物",
            en: "Divine Settlement and Treasures",
          },
          summary: {
            "zh-Hant":
              "神族秩序由權威、婚配、人質和寶物共同維持，也一直帶着破裂的可能。",
            en: "Divine order is held by authority, marriage, hostages, and treasures, while remaining fragile.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene(
              "norse-divine-order",
              ["odin-frigg-authority", "thor-as-future-force"],
              {
                title: {
                  "zh-Hant": "奧丁、弗麗嘉與托爾確立神族中心",
                  en: "Odin, Frigg, and Thor Set the Centre",
                },
                narrative: sceneNarrative(
                  "諸界成形後，故事進入阿斯神族的秩序。奧丁與弗麗嘉先佔住神族權威與家庭中心；他們不是單獨的人名，而是把神宮、王權、婚姻和預知壓力連在一起的核心。",
                  "托爾與妙爾尼爾隨後進入這個框架。錘不是普通武器，而是神族面對巨人世界時最容易被看見的力量；托爾的出現也讓神族秩序不只是議事與親族，還包括守護與反擊。",
                  "這段開場把北歐神族放在可以行動的中心：奧丁掌權，弗麗嘉連着家庭與誓言，托爾握着對外防線。後來的婚姻協議、寶物、偷竊和末日戰鬥，都會從這個脆弱但有力量的中心展開。神族秩序先被看見成家庭、王權與武力的組合；這個中心越清楚，後面裂開時也越有重量。",
                  "After the worlds take shape, the route enters Aesir order. Odin and Frigg first anchor authority and household, joining kingship, marriage, and foresight within the divine centre.",
                  "Thor and Mjolnir then give that centre visible force. The hammer is not just a weapon; it is the gods’ clearest answer to pressure from the giant world.",
                  "The opening makes divine order active: Odin rules, Frigg holds household and oath pressure, and Thor guards the border. Later marriages, treasures, thefts, and final battles unfold from this centre, giving later ruptures more weight.",
                ),
              },
            ),
            scene(
              "norse-divine-order",
              [
                "njord-hostage-settlement",
                "skadi-nine-night-compact",
                "freyr-freyja-vanir-children",
              ],
              {
                title: {
                  "zh-Hant": "人質、婚姻與華納子女",
                  en: "Hostages, Marriage, and Vanir Children",
                },
                narrative: sceneNarrative(
                  "神族秩序不是只靠阿斯神族內部完成。尼奧爾德作為華納人質進入阿斯神族，弗雷與芙蕾雅也因此成為神族故事裏的重要力量；和解不是抽象原則，而是靠人被送進另一個家庭維持。",
                  "絲卡蒂的婚姻協議又顯示秩序常以補償和妥協處理危機。她與尼奧爾德在海與山之間分住九夜，協議能建立聯繫，卻不能完全消除生活位置的差異。",
                  "這一段把華納、阿斯神族和巨人世界放進同一組脆弱安排。弗雷與芙蕾雅帶着華納背景進入神族中心，絲卡蒂則保留邊界人物的力量；後來弗雷的愛慾、寶物與末日缺劍，都會從這種既聯合又不完全融合的秩序中浮現。和解在此不是結束，而是繼續需要維持的狀態。",
                  "Divine order is not built only inside the Aesir. Njord enters as a Vanir hostage, and Freyr and Freyja become major powers within the gods’ story.",
                  "Skadi’s marriage settlement shows another kind of fragile order: compensation can create a bond, but the sea and mountain life cannot fully merge.",
                  "This scene places Vanir, Aesir, and giant-world figures within one negotiated structure. It is joined but not seamless, and later love, treasure, and loss grow from that fragility.",
                ),
              },
            ),
            scene("norse-divine-order", ["freyr-sees-gerd", "skirnir-errand"], {
              title: { "zh-Hant": "弗雷望見格爾德", en: "Freyr Sees Gerd" },
              narrative: sceneNarrative(
                "秩序建立後，慾望把神族視線推向巨人世界。弗雷坐上高座，看見格爾德，愛慕立即變成無法安放的焦慮；他的沉默與病態不是孤立情緒，而是神族與巨人世界再次相觸的入口。",
                "斯基爾尼爾接下求婚任務，帶着弗雷的馬與劍前往格爾德所在之處。談判從贈禮走向威嚇，最後把九夜等待寫進婚約。這段故事使愛、差使、武器和迫使同意的語言纏在一起。",
                "弗雷得到格爾德，卻失去自己的劍。這場婚戀在這裏埋下末日伏線：神族與巨人世界因此接近，弗雷未來在諸神黃昏中面對蘇爾特爾的能力也被削弱。愛慾帶來結合，也把防衛的缺口留到更遠處；這也是末日場上最早被寫下的個人代價之一。",
                "Once order is established, desire pushes divine sight toward the giant world. Freyr sees Gerd from the high seat, and longing turns into an illness of silence.",
                "Skirnir takes the errand with Freyr’s horse and sword. The negotiation moves from gifts to threats, and the agreement is fixed by nine nights of waiting.",
                "Freyr gains Gerd but gives up his sword. A love story therefore becomes a doom seed: the gods move closer to the giant world while Freyr’s future defence is weakened. One personal cost is already written into the final battlefield.",
              ),
            }),
            scene(
              "norse-divine-order",
              ["treasure-craft-contest", "treasures-enter-divine-order"],
              {
                title: {
                  "zh-Hant": "洛基引出神族寶物",
                  en: "Loki Brings the Treasures",
                },
                narrative: sceneNarrative(
                  "神族秩序也靠器物支撐，而器物常由麻煩開始。洛基的行動引出工藝競賽，侏儒打造出多件寶物；這不是單純展示清單，而是讓手藝、賭注和神族權力連在一起。",
                  "妙爾尼爾、德羅普尼爾、古林博斯提與其他寶物進入神族手中，每件物都改變後面故事的可能性。托爾從此以錘守住秩序，德羅普尼爾會在巴德爾葬禮再出現，弗雷的金豬和其他寶物則擴大神族的象徵力量。",
                  "這段場景顯示，神明不是只靠血統和誓言維持世界，也靠可被偷走、交換、贈送或失去的器物。寶物越重要，後面每一次失竊、抵押和交還都會帶來更大的秩序震動。神族的力量因此有了可見形體，也有了可被奪走的弱點。",
                  "Divine order also depends on objects, and objects often begin in trouble. Loki’s action draws out a craft contest, tying dwarven skill, wager, and divine power together.",
                  "Mjolnir, Draupnir, Gullinbursti, and other treasures enter the gods’ hands. Each changes what later stories can do: defence, funeral gift, splendour, and authority all become material.",
                  "The scene shows that the gods hold the world not only through birth and oath but through things that can be stolen, exchanged, gifted, or lost.",
                ),
              },
            ),
            scene("norse-divine-order", ["loki-among-aesir-and-sigyn"], {
              title: {
                "zh-Hant": "洛基與西格恩在神族之中",
                en: "Loki and Sigyn within the Aesir",
              },
              narrative: sceneNarrative(
                "寶物帶來神族可見的力量，也讓洛基成為改變秩序的人物。但在這些事件之前，《欺騙古魯菲》先把他放在阿斯神族的名單裏：他有巨人父親法爾包提與母親勞菲，卻同時被列為神族的一員。這個位置本身已經帶着兩邊世界交纏的張力。",
                "第33章形容洛基善於狡智，常使諸神陷入困境，又會以計謀幫助他們脫身；西格恩與納爾菲則讓他在神族生活中已有家庭。",
                "洛基不是從外面闖進來的破壞者。他在阿斯神族有親人，也有巨人血脈；往後衝突帶着家族失衡的重量。危機時，他會幫忙，也會令裂痕更深。每次危機不只像外敵入侵，更像神族原有的縫隙張開。",
                "The treasures give divine order visible force and make Loki an agent of change. Yet Gylfaginning first places him among the Aesir: he has the giant Farbauti as father and Laufey as mother, while still being numbered among the gods.",
                "Chapter 33 calls Loki cunning, says he often brings hardship upon the gods before helping them out by craft, and places Sigyn and Narfi within his divine household.",
                "Loki is not an outside destroyer. He has kin among the Aesir and giant parentage, so later conflict carries the weight of a household falling out of balance. Each crisis is not only an outside intrusion but an opening in a crack already within the gods’ home.",
              ),
            }),
          ],
        },
        {
          id: "norse-poetic-mead-route",
          order: 2,
          title: {
            "zh-Hant": "和平留下的詩蜜酒",
            en: "The Poetic Mead Left by Peace",
          },
          summary: {
            "zh-Hant":
              "《詩語法》第一章把克瓦希爾、蜜酒、赫尼特比約格與奧丁的歸來連成一條來源清楚的支線；它與此時代並列，而非替其他故事排定年代。",
            en: "Skaldskaparmal 1 links Kvasir, the mead, Hnitbjorg, and Odin’s return in one source-bounded strand. It sits alongside this era without imposing a date on the other stories.",
          },
          chronologyMode: "parallel",
          coverageStatus: "complete",
          segments: [
            scene(
              "norse-poetic-mead",
              ["kvasir-from-peace", "dwarves-kill-kvasir"],
              {
                title: {
                  "zh-Hant": "和解的遺物被釀成蜜酒",
                  en: "A Relic of Peace Is Brewed into Mead",
                },
                narrative: sceneNarrative(
                  "這條支線從阿斯與華納結束衝突後留下的一缸唾液開始。眾神以它造出克瓦希爾；文本說他無所不知，遇到難題的人都會向他求問。和平在這裏不是一個抽象的背景，而是被留在器皿中、又化成一位能回答問題的存在。",
                  "費亞拉爾和加拉爾邀克瓦希爾同行，卻殺死他，把血收進三個器皿，再與蜜混合。於是血不只被隱去：它被轉成一種飲物，文本以詩與學問的能力說明它的價值。這個轉換不替矮人補寫動機，只讓讀者看見知識如何被暴力奪走，又被做成可以交換的東西。",
                  "克瓦希爾不再以人物走在故事裏，詩蜜酒卻把他的來處帶進後面每一段交易。它由和解而生，經殺害而被改造；從此器物本身同時保存了和平、知識和失去。",
                  "This strand begins with the vat left after Aesir and Vanir make peace. The gods make Kvasir from it, and the text calls him so wise that people with difficult questions seek him out.",
                  "Fjalar and Galar kill Kvasir, gather his blood in three vessels, and mix it with honey. The source turns the loss into a drink associated with poetry and learned skill, without supplying a motive for the killing.",
                  "Kvasir no longer travels through the story as a person, but the mead carries his origin into every later bargain. Peace, knowledge, and loss are held together in the object.",
                ),
              },
            ),
            scene(
              "norse-poetic-mead",
              ["suttungr-takes-ransom", "gunnlod-guards-hnitbjorg"],
              {
                title: {
                  "zh-Hant": "賠償被鎖進山中",
                  en: "A Ransom Is Locked Inside the Mountain",
                },
                narrative: sceneNarrative(
                  "蜜酒接着成為矮人求生時能提出的賠償。費亞拉爾和加拉爾害死 Gillingr 與其妻，蘇頓格把他們放到漲潮就會沒頂的礁石上；兩人交出蜜酒，才換回性命。這一筆交易把原本由克瓦希爾而來的飲物，從矮人手中轉到巨人一方。",
                  "蘇頓格沒有把它留在眼前。他把蜜酒帶到赫尼特比約格，交給女兒岡恩洛德看守。文本給出的是守衛與藏匿的安排，沒有說岡恩洛德如何看待這份工作；因此時間線也只把她寫成守門的人，而不把沉默補成內心動機。",
                  "蜜酒從可交換的賠償變成被看守的寶物，故事的重心也從矮人的罪行移到山內的入口。想得到它的人，日後不只要面對蘇頓格，也要越過岡恩洛德與岩石。",
                  "The mead next becomes the ransom that saves the dwarves. After Fjalar and Galar kill Gillingr and his wife, Suttungr strands them where the rising sea will cover them; they surrender the mead for their lives.",
                  "Suttungr carries it to Hnitbjorg and assigns his daughter Gunnlod to guard it. The source supplies the arrangement, not Gunnlod’s private reason, so the timeline leaves that motive unstated.",
                  "What was a negotiable payment becomes a guarded treasure. The story now turns toward the mountain entrance, its keeper, and the obstacle of the rock.",
                ),
              },
            ),
            scene(
              "norse-poetic-mead",
              ["bolverkr-works-for-baugi", "rati-opens-the-rock"],
              {
                title: {
                  "zh-Hant": "Bolverkr 用勞作換到山口",
                  en: "Bolverkr Works His Way to the Mountain",
                },
                narrative: sceneNarrative(
                  "奧丁到包吉身邊時使用 Bolverkr 這個名字。九名工人因他借出的磨刀石互相爭奪而死，包吉忽然缺少收割的人手；奧丁提出替他做完一夏的工作，只要求喝一口蜜酒。這是計策與勞作並行的一段，不需要把化名變成另一位神祇。",
                  "包吉答應幫忙，卻說蘇頓格不會願意交出蜜酒。收成結束後，兩人走到赫尼特比約格；包吉用 Rati 鑽岩，先謊稱已經穿透。奧丁以吹進洞口的木屑識破謊話，要他再鑽一次，才得到真正的孔道，隨即化成蛇滑入。",
                  "奧丁尚未得到蜜酒，但他把交換的承諾推到藏匿地的內側。這一段的關鍵不是神力突然解決障礙，而是化名、工作、識破謊言與工具共同把道路打開。",
                  "Odin comes to Baugi under the name Bolverkr. When nine workers die fighting over his whetstone, he offers a summer’s labour in return for one drink of the mead. The alias belongs to this episode; it is not another god.",
                  "After the work, Baugi drills Hnitbjorg with Rati and falsely claims to have broken through. Odin tests the hole with chips, demands a real bore, and changes into a serpent to slip inside.",
                  "Odin has not yet taken the mead, but he has moved the bargain past the mountain’s outer boundary through work, detection, and a tool rather than a sudden display of power.",
                ),
              },
            ),
            scene(
              "norse-poetic-mead",
              ["gunnlod-three-draughts", "odin-returns-with-mead"],
              {
                title: {
                  "zh-Hant": "三口之後，蜜酒回到阿斯加德",
                  en: "After Three Draughts, the Mead Reaches Asgard",
                },
                narrative: sceneNarrative(
                  "山內的岡恩洛德讓奧丁住了三夜，並允許他喝三口。文本把許可與結果並排：奧丁每一口都喝空一個器皿，三口喝盡原先盛放蜜酒的三器。她的准許是此處可見的關係；故事沒有明說她的個人理由，因此不把它寫成愛、忠誠或背叛的判詞。",
                  "奧丁帶着蜜酒化為鷹離開，蘇頓格也化為鷹追趕。眾神在阿斯加德預備器皿，奧丁飛到城中便把蜜酒吐進去；文本也留下少量因追趕而灑落的酒，作為壞詩人的份額。這是故事的收束，但不需要把那個玩笑延伸成對所有詩人的評價。",
                  "詩蜜酒最終進入神族中心，克瓦希爾由和平而來的知識也因此以另一種形態被帶回。這是《詩語法》第一章內部完整的路線；它和本時代其他神族故事並列，不替它們排出先後。",
                  "Inside the mountain, Gunnlod lets Odin stay three nights and permits three draughts. The text puts permission and result together: each draught empties one vessel, while her personal reason remains unstated.",
                  "Odin leaves as an eagle with the mead, Suttungr pursues in eagle form, and the gods prepare vessels in Asgard. Odin deposits the mead there; the source also notes the small spill associated with poor poetry.",
                  "The mead enters the divine centre, carrying Kvasir’s peace-born knowledge back in another form. This is a complete route within Skaldskaparmal 1, placed beside rather than ahead of the era’s other stories.",
                ),
              },
            ),
          ],
        },
        {
          id: "norse-lokasenna-route",
          order: 3,
          title: {
            "zh-Hant": "埃吉爾的宴席與洛基之爭辯",
            en: "Ægir’s Feast and Lokasenna",
          },
          summary: {
            "zh-Hant": "這首詩從芬瑪芬格之死走到托爾使洛基離席；每一次指控都保留為宴席裡的說話者，而非單一的神話事實。",
            en: "This poem runs from Fimafeng’s death to Thor making Loki leave; every accusation remains a speaker in the hall, not a single mythic fact.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "依《洛基之爭辯》的場景順序排列，結尾散文所提的綁縛仍交由既有的巴德爾與受縛路線處理。",
            en: "Ordered by Lokasenna’s scene sequence; its closing prose about binding remains with the existing Baldr and binding route.",
          },
          segments: allSteps("norse-lokasenna", [
            "hall-praised-too-loudly",
            "loki-waits-at-door",
            "place-made-at-table",
            "bragi-and-idunn-hold-line",
            "hall-answers-back",
            "servants-and-watchman-speak",
            "one-last-cup-one-warning",
            "thor-arrives",
          ]),
        },
        {
          id: "norse-alvissmol-route",
          order: 4,
          title: {
            "zh-Hant": "阿爾維斯之歌：求婚、盤問與天明",
            en: "Alvissmol: Suit, Questions, and Daybreak",
          },
          summary: {
            "zh-Hant": "阿爾維斯以為自己能迎娶托爾的女兒；托爾用整夜的盤問把婚事拖到天明。",
            en: "Alvis believes he can marry Thor’s daughter; Thor delays the claim with questions until daylight.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "依《阿爾維斯之歌》第1至35節的內部順序；新娘的特魯德辨認與阿爾維斯的後果都保留在詩與註釋的界線內。",
            en: "Ordered by Alvissmol 1–35; the Thrud identification and Alvis’s outcome remain within the poem-and-note boundary.",
          },
          segments: [
            scene(
              "norse-alvissmol",
              ["a-bride-expected", "the-father-returns"],
              {
                title: { "zh-Hant": "阿爾維斯以為新娘在等他", en: "Alvis Thinks the Bride Awaits" },
                narrative: sceneNarrative(
                  "阿爾維斯走到門前時，已把婚事當成定局：新娘會裝點他的座位，他也急著帶她回家。詩沒有讓新娘說話，而是先讓求婚者的期待佔滿場面。",
                  "托爾回來後，立刻把這份期待停下來。他說女兒婚事的首要權利在父親手中，尤其是自己不在場時作出的許諾。這不是一段互訴心意的婚約，而是誰能替未出場的新娘決定的爭議。",
                  "傳統把詩中無名的新娘辨認為特魯德，但詩句沒有說出名字。時間線因而只把她放在這個可見的位置：她被人聲稱、被父親保留，卻沒有被替她補寫立場。托爾沒有答應，也沒有立刻趕走阿爾維斯；下一步是把衝突換成一項條件。",
                  "When Alvis comes to the door, he treats the marriage as settled: the bride will grace his benches, and he is eager to take her home. The poem gives the bride no speech and begins instead with the suitor’s confidence.",
                  "Thor returns and stops that confidence at once. He says a father has first right in his daughter’s marriage, particularly when an alleged promise was made in his absence. This is not a mutual courtship; it is a dispute over who may decide for a woman who never enters the dialogue.",
                  "Editorial tradition identifies the unnamed bride as Thrud, but the verses do not give her name. The Timeline therefore keeps her in the position the poem makes visible: claimed by one speaker and held back by another, without an invented reply. Thor neither consents nor simply drives Alvis away; he changes the conflict into a condition."
                ),
              },
            ),
            scene(
              "norse-alvissmol",
              ["desire-meets-a-condition", "earth-and-sky"],
              {
                title: { "zh-Hant": "婚事被改成一整夜的考驗", en: "Marriage Becomes an All-Night Test" },
                narrative: sceneNarrative(
                  "阿爾維斯沒有退去，仍要求托爾允許這門婚事。托爾便說，若他真能說出各個世界的知識，自己不會阻止他得到新娘。看似留下了一條路，實際上把決定延後到一場由托爾掌握節奏的問答。",
                  "第一批問題問大地與天穹在不同群體中的稱名。阿爾維斯答得流利，把人、眾神、華納、巨人、精靈與其他存在的詞語排在一起。這些回答讓他顯得博聞，卻不等於詩替讀者畫出一張固定的九界地圖。",
                  "此處的危險不是答錯，而是時間。每一個答案都令盤問得以繼續，求婚也仍停在門外。托爾把阿爾維斯最值得誇耀的東西——他的學識——變成拖慢他自己腳步的方法。",
                  "Alvis does not withdraw; he still asks Thor to permit the marriage. Thor replies that he will not stop him if he can name what Thor wants to know in every world. The offer looks open, yet it postpones the decision into an exchange whose rhythm Thor controls.",
                  "The first questions concern the names of earth and heaven among different groups. Alvis answers smoothly, setting the language of people, gods, Vanir, giants, elves, and others beside one another. His answers show learning, but they do not give the reader a fixed map of the Nine Worlds.",
                  "The danger is not a wrong answer but time. Each answer permits another question, while the marriage remains outside the door. Thor turns Alvis’s proudest possession—his knowledge—into the means of delaying him."
                ),
              },
            ),
            scene(
              "norse-alvissmol",
              ["moon-and-sun", "wind-sea-and-fire"],
              {
                title: { "zh-Hant": "日月與風海填滿整個夜晚", en: "Sun, Moon, Wind, and Sea Fill the Night" },
                narrative: sceneNarrative(
                  "盤問從大地與天穹轉到月亮和太陽。阿爾維斯仍能列出不同群體的稱名；日月在這裡不是要被征服的對手，而是讓問答一輪輪延長的題目。托爾既沒有爭論答案，也沒有讓話題回到新娘。",
                  "接著是風、平靜、海與火。阿爾維斯把冥界、巨人與華納的說法並排，每一組詞都顯示詩偏向詩人如何換名，而不是某個神如何行動。故事因此有一種奇特的靜止感：兩人一直說話，婚事卻沒有前進。",
                  "然而夜色本身正在前進。讀者知道托爾的問題不會無窮無盡；他只要讓阿爾維斯停留得夠久，真正改變局面的便不會是更高深的知識，而是天光。",
                  "The examination moves from earth and heaven to moon and sun. Alvis can still list the names used by different groups; these lights are not enemies to conquer but subjects that make the exchange last another round. Thor neither disputes the answers nor returns to the bride.",
                  "Then come wind, calm, sea, and fire. Alvis sets terms from Hel, giants, and Vanir beside one another. Each set shows the poem’s interest in how poets rename things more than in a new action by a god. The scene becomes strangely still: the two men keep speaking while the marriage never moves.",
                  "Yet the night itself moves forward. Thor’s questions need not be endless; if he keeps Alvis in place long enough, the force that changes the situation will not be deeper lore but daylight."
                ),
              },
            ),
            scene(
              "norse-alvissmol",
              ["wood-night-seed-and-ale", "day-arrives"],
              {
                title: { "zh-Hant": "最後的答案等到白晝", en: "The Last Answers Wait for Day" },
                narrative: sceneNarrative(
                  "木、夜、種子與酒成為最後一輪題目。阿爾維斯連這些也能回答，尤其把華納世界的說法列進種子與酒的詞語裡；他的學識沒有崩潰，托爾也承認自己未見過更豐富的古老知識。",
                  "讚賞隨即轉成揭露。托爾說白晝已捉住侏儒，陽光已照入廳堂。詩在這一句停止，沒有描寫阿爾維斯接下來的形體，也沒有回頭交代新娘最後歸誰。",
                  "這個結尾令前面的長問答有了另一層意思：托爾不是要贏得知識競賽，而是要把時間留到天亮。阿爾維斯的答案依然成立，卻沒有替他換到婚事；詩把勝負放在光線抵達的一刻，而不是放在誰說得最有學問。",
                  "Wood, night, seed, and ale form the final round. Alvis can answer these as well, including terms placed with Vanaheim for seed and drink; his learning does not collapse, and Thor admits he has not seen greater store of old knowledge.",
                  "Praise turns immediately into disclosure. Thor says that day has caught the dwarf and sunlight has entered the hall. The poem stops there: it does not describe a bodily change for Alvis, nor does it return to tell who receives the bride.",
                  "The ending changes the meaning of the long examination. Thor was not trying to win a contest of knowledge; he was holding time until morning. Alvis’s answers remain valid, yet they do not buy the marriage. Victory belongs to the arrival of light, not to the person who knew the most names."
                ),
              },
            ),
          ],
        },
        {
          id: "norse-harbarthsljoth-route",
          order: 5,
          title: {
            "zh-Hant": "哈爾巴德之歌：隔岸的嘲諷",
            en: "Harbarthsljoth: Scoffing Across the Sound",
          },
          summary: {
            "zh-Hant": "托爾返程時求渡，對岸自稱哈爾巴德的人以嘲諷、自誇與遠路回應。",
            en: "Returning home, Thor asks for a ferry; a man calling himself Harbard answers with mockery, boasts, and a long detour.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "依貝洛斯所譯導言、散文引子與第1至56節排列；導言的身份解讀不取代詩中哈爾巴德的自稱，故事亦止於拒渡。",
            en: "Ordered by Bellows’s introduction, prose opening, and stanzas 1–56; the introduction’s identity reading does not replace Harbard’s self-name in the poem, and the route stops at the refusal.",
          },
          segments: [
            scene("norse-harbarthsljoth", ["thor-reaches-sound", "thor-asks-for-ferry"], {
              title: { "zh-Hant": "返程的人遇上隔岸聲音", en: "A Returning Traveler Hears a Voice" },
              narrative: sceneNarrative(
                "托爾從東方返程，走到海峽邊時聽見有人在另一岸說話。他不是來尋找比試，也沒有帶著一段已完成的勝利故事；眼前先是一條水面與一艘不在自己這邊的船。",
                "他向對方問路，說自己正要回家，請那人把船帶過來。這是一個很小、很直接的要求，卻立刻把行程交到陌生人的手裡：托爾能否繼續前行，取決於對方願不願意靠岸。",
                "詩沒有替海峽定出地名，也沒有先交代掌船者的來歷。它先把兩人隔開，讓一次渡河的請求成為聲音、距離與權力的開端。",
                "Thor is returning from the east when he hears someone speaking from the other side of a sound. He has not come seeking a contest, nor does he arrive with a victory already completed; first there is water and a boat that is not on his shore.",
                "He asks who is there, says he is travelling home, and asks the man to bring the boat across. It is a small, direct request, yet it places the journey in a stranger’s hands: Thor can continue only if the other man chooses to come to shore.",
                "The poem gives neither a place-name for the sound nor an advance account of the boatman’s origin. It first keeps the two men apart, making a request for passage the beginning of voice, distance, and power."
              ),
            }),
            scene("norse-harbarthsljoth", ["harbard-starts-mocking", "names-across-sound"], {
              title: { "zh-Hant": "嘲諷之後才有名字", en: "Mockery Before Names" },
              narrative: sceneNarrative(
                "對岸的人沒有應允，反而先取笑托爾的衣著與飢餓。托爾聽到的不是擺渡人的條件，而是一連串要令他難堪的話；過河從一件可以安排的小事，變成兩人誰也不肯讓步的較量。",
                "托爾於是說自己是奧丁之子。對方沒有交出更長的身分來歷，只回答自己的名字是哈爾巴德。這個名字足以讓對話繼續，卻沒有消除隔岸之人的陌生感。",
                "貝洛斯的導言以奧丁的化身來閱讀這名擺渡人，但詩句本身仍只讓他自稱哈爾巴德。此刻托爾面對的，仍是隔岸說話的哈爾巴德。",
                "The man on the far bank does not agree; he begins by making fun of Thor’s dress and hunger. Thor hears not a ferryman’s terms but words meant to humiliate him, and the crossing becomes a contest neither man will yield.",
                "Thor then says that he is Odin’s son. The other man does not offer a longer history; he answers only that his name is Harbard. The name is enough to continue the exchange, without removing the stranger’s distance.",
                "Bellows’s introduction reads the ferryman as a shape assumed by Odin, but the verses still let him call himself Harbard. At this moment, Thor faces Harbard speaking from the far bank."
              ),
            }),
            scene("norse-harbarthsljoth", ["boasts-face-across-water", "glove-taunt-and-threat"], {
              title: { "zh-Hant": "功績與手套在水面兩邊碰撞", en: "Exploits and a Glove Clash Across Water" },
              narrative: sceneNarrative(
                "托爾用戰功回應輕蔑，哈爾巴德也說起自己如何在王侯之間煽動戰爭。兩人都把話說得很大，但這不是替誰記下一份可以離開現場查驗的生平；它是要在對方面前站穩的說法。",
                "哈爾巴德接著把托爾與一隻手套的故事拿來取笑。托爾以威脅回答，仍沒有得到船。言語在此不只是消磨時間：每一次回嘴都令兩人更難回到最初那個簡單的請求。",
                "水面保持原狀，船也留在對岸。托爾的力量沒有消失，哈爾巴德的嘴也沒有停下；這首詩的緊張正來自兩者都無法把對方逼到自己這一邊。",
                "Thor answers contempt with exploits, and Harbard says that he has stirred wars among rulers. Both men make their claims large, but the poem is not keeping biographies that can be checked away from the scene; each is trying to stand firm before the other.",
                "Harbard then turns a story about a glove into ridicule. Thor answers with a threat, still without getting the boat. The words do more than pass time: every retort makes it harder for either man to return to the first simple request.",
                "The water remains where it is, and the boat stays on the far shore. Thor’s strength has not vanished, and Harbard’s voice has not stopped; the tension of the poem comes from neither being able to force the other onto his own side."
              ),
            }),
            scene("norse-harbarthsljoth", ["sif-allegation-refused", "no-passage-detour"], {
              title: { "zh-Hant": "一句謊言，與一條遠路", en: "A Lie, Then a Long Road" },
              narrative: sceneNarrative(
                "哈爾巴德把挑釁帶到西芙，說她有情人。托爾沒有接受這句話，當面說他在說謊。詩讓這項指控停留在發話者口中，不要求讀者替它補上真相或後果。",
                "最後托爾命哈爾巴德把船帶來。哈爾巴德仍拒絕，指向一條要走很久的陸路；他不是給出折衷方案，而是把最初的障礙原封不動地留在托爾面前。",
                "詩到此收住。托爾是否走了那條路、何時回家，都沒有在這段對話裡交代；能確定的只是一艘沒有靠岸的船，和一場沒有替人解決任何事的爭辯。",
                "Harbard carries the taunt to Sif, saying that she has a lover. Thor does not accept the line and calls him a liar to his face. The poem leaves the allegation in its speaker’s mouth, without asking the reader to supply truth or consequence.",
                "At last Thor orders Harbard to bring the boat. Harbard still refuses, pointing to an overland route that will take a long time; he does not offer a compromise but leaves the original obstacle exactly where it began.",
                "The poem stops there. It does not tell whether Thor takes the road or when he reaches home; what remains certain is a boat that never comes to shore and an argument that solves nothing."
              ),
            }),
          ],
        },
        {
          id: "norse-grimnismol-route",
          order: 6,
          title: { "zh-Hant": "格里姆尼爾之歌：火中揭名", en: "Grimnismol: A Name Revealed in Fire" },
          summary: { "zh-Hant": "自稱格里姆尼爾的陌生人被困在兩火之間；阿格納送來酒角，奧丁最後才揭出自己。", en: "A stranger calling himself Grimnir is held between two fires; Agnar brings a horn, and only at the end does Odin reveal himself." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依手稿散文引子、1–3、45–54 節與結尾散文；第4至44節的神居名錄留作火中誦說，不重新拆成另一條宇宙故事。", en: "Ordered by the manuscript prose opening, stanzas 1–3 and 45–54, and closing prose; the halls catalogues of stanzas 4–44 remain speech from the fire rather than a second cosmology route." },
          segments: [
            scene("norse-grimnismol", ["brothers-lost-at-sea", "fosterling-king-wager"], {
              title: { "zh-Hant": "海邊失散的兄弟，長成兩位國王", en: "Brothers Lost at Sea Become Two Kings" },
              narrative: sceneNarrative(
                "手稿散文的引子說，赫勞東的兩個兒子阿格納與葛爾羅德在海上失散。年幼的阿格納被弗麗嘉收留；葛爾羅德則到了奧丁身邊。這不是一場兩兄弟彼此爭鬥的故事，而是兩段不同撫養如何把他們帶回同一片王權之下。",
                "葛爾羅德先回到父親身邊，坐上王位；多年後，阿格納也成了國王。奧丁與弗麗嘉於是為各自撫養的孩子打賭：哪一位會是較好的統治者。賭注把本來留在遠處的兩位神，帶到葛爾羅德宮廷將要發生的事旁邊。",
                "引子沒有把兩位國王排成誰善誰惡的定論。它只讓一項比較落在葛爾羅德身上，並讓一名陌生訪客成為這場比較的入口。",
                "The manuscript prose opening says that Hrauthung’s two sons, Agnar and Geirroth, are separated at sea. The young Agnar is taken in by Frigg, while Geirroth comes to Odin. This is not a tale of the brothers fighting each other; it is about two different fosterings that bring them back under the same horizon of kingship.",
                "Geirroth returns to his father first and takes the throne; years later, Agnar too is king. Odin and Frigg then wager over the fosterlings: which one will prove the better ruler? The wager brings two gods who had remained at a distance close to what is about to happen in Geirroth’s hall.",
                "The opening does not settle the brothers into a simple verdict of good and bad. It places a test on Geirroth and lets a stranger’s arrival become the door into it."
              ),
            }),
            scene("norse-grimnismol", ["fullas-warning", "grimnir-between-fires"], {
              title: { "zh-Hant": "警告沒有被聽進去", en: "A Warning Goes Unheeded" },
              narrative: sceneNarrative(
                "弗麗嘉派富拉去警告葛爾羅德：有一名來客會施法，使刀劍傷不了他；能認出他的方法，是看他從不與狗說話。這是弗麗嘉一方給國王的消息，不是一張替陌生人預先揭名的身分紙。",
                "奧丁隨後以格里姆尼爾之名來到宮中。葛爾羅德看見他不肯同狗交談，便把警告當真，卻沒有選擇相認或詢問；他把這名陌生人夾在兩團火之間，讓他受了八夜折磨。",
                "故事的殘酷不在於國王缺少線索，而在於他把線索變成了懲罰的理由。格里姆尼爾尚未宣告自己是誰，火已替宮廷定下了說話的條件。",
                "Frigg sends Fulla to warn Geirroth that a visitor will be hard to harm with weapons, and that he can be recognized because he never speaks with dogs. It is news from Frigg’s side of the wager, not an identity paper that names the stranger in advance.",
                "Odin then comes to the hall under the name Grimnir. Geirroth sees that he will not speak with dogs, takes the warning as true, and chooses neither recognition nor questions. He has the stranger held between two fires, where he suffers for eight nights.",
                "The cruelty lies not in the king having no clue, but in turning a clue into a reason to punish. Grimnir has not yet announced who he is; the fire has already set the terms on which anyone in the hall may speak."
              ),
            }),
            scene("norse-grimnismol", ["agnar-brings-horn", "halls-named-from-fire"], {
              title: { "zh-Hant": "一隻酒角，換來火邊的誦說", en: "A Horn Opens the Fire-Side Recital" },
              narrative: sceneNarrative(
                "年少的阿格納走近兩火之間，遞給格里姆尼爾一隻裝滿酒的角，說父親不該讓一個人無故受苦。格里姆尼爾喝下酒，答應這份善意會讓阿格納得到回報；宮廷裡第一個實際減輕他痛苦的人，不是國王。",
                "接著，他從火邊說起眾神的居處、土地與看守者。這些長長的名稱不是一條可沿途走完的地圖：它們是在受困者口中展開的知識，也把原本被迫沉默的陌生人變成掌握話語節奏的人。",
                "酒角沒有立刻停止火焰，卻改變了場面。阿格納的行動使格里姆尼爾開始誦說，而國王先前的猜疑越來越難承受。",
                "The young Agnar comes to the space between the fires and gives Grimnir a horn full of drink, saying that his father should not make a man suffer without cause. Grimnir drinks and promises that this kindness will bring Agnar a reward; the first person in the hall to ease his pain is not the king.",
                "He then speaks from the fire of the gods’ halls, lands, and keepers. The long catalogue of names is not a route someone can simply travel: it is knowledge unfolding in a captive’s voice, turning the stranger who was forced into silence into the person who controls the rhythm of speech.",
                "The horn does not put out the flames at once, but it changes the scene. Agnar’s act lets Grimnir begin his recital, while the king’s earlier suspicion grows harder and harder to bear."
              ),
            }),
            scene("norse-grimnismol", ["many-names-one-speaker", "odin-revealed-sword-falls"], {
              title: { "zh-Hant": "許多名字，最後只指向一人", en: "Many Names, One Speaker" },
              narrative: sceneNarrative(
                "到誦說的末尾，格里姆尼爾連續報出許多名字與稱號，最後說出奧丁的名字。這不是把每個名字拆成新的神祇，而是一名說話者在火中把自己的身分逐層推到葛爾羅德面前。",
                "葛爾羅德終於明白自己讓誰受苦，連忙起身去救他。可手稿結尾散文說，他拔劍時失足跌倒，自己的劍落在他身上。剛才仍坐在王位上的人，沒有等到補救完成便已死去。",
                "阿格納繼承王國；格里姆尼爾的承諾落在行動而非血統的次序上。故事結尾不是神明宣判一項抽象品格，而是讓一個晚來的辨認與一把劍，把賭局推向無法收回的結果。",
                "At the end of the recital, Grimnir gives a succession of names and epithets, finally naming Odin. This does not divide every name into a new god; one speaker in the fire is bringing his identity, layer by layer, before Geirroth.",
                "Geirroth at last understands whom he has made suffer and rises in haste to save him. But the manuscript’s closing prose says that he slips as he draws his sword and falls onto it. The man who was on the throne a moment earlier dies before his remedy can be completed.",
                "Agnar inherits the kingdom, and Grimnir’s promise falls on an act of kindness rather than the order of blood. The ending is not a god’s verdict on an abstract virtue: late recognition and a sword carry the wager to a result that cannot be taken back."
              ),
            }),
          ],
        },
        {
          id: "norse-skirnismol-route",
          order: 7,
          title: { "zh-Hant": "斯基爾尼爾之歌：火焰、咒語與九夜", en: "Skirnismol: Fire, Curse, and Nine Nights" },
          summary: { "zh-Hant": "弗雷望見格爾德後派斯基爾尼爾出使；禮物被拒，威脅與咒語後，格爾德指定九夜後的巴里。", en: "After Freyr sees Gerd, he sends Skirnir; gifts are refused, then threats and a curse follow, and Gerd names Barri nine nights away." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依引言、開頭散文及第 1–42 節；可能缺節與咒語段的傳本問題保留在詩內，也不把格爾德在威脅後的答應重寫成無壓力求愛。", en: "Ordered by the introduction, opening prose, and stanzas 1–42; a possible gap and textual problems in the curse remain within the poem, and Gerd’s agreement after threats is not rewritten as pressure-free courtship." },
          segments: [
            scene("norse-skirnismol", ["watchtower", "confession"], { title: { "zh-Hant": "高座上的望見與說不出的愁苦", en: "A Sight from the High Seat; Grief Spoken" }, narrative: sceneNarrative("弗雷坐在赫利德斯凱爾夫，從那裡望進巨人世界，看見格米爾的女兒格爾德。光從她的手臂映到海與天，他卻沒有因此得到安寧。", "尼奧爾德要斯基爾尼爾問明原因。斯基爾尼爾憑兩人年少時的信任追問，弗雷才說出自己想與格爾德同在，卻覺得沒有人會允許。", "故事先把欲求寫成沉默與失衡，而非已獲回應的愛情。", "Freyr sits in Hlithskjolf and looks into the giant world, seeing Gerd, Gymir’s daughter. Light from her arms shines over sea and sky, but gives him no peace.", "Njord sends Skirnir to ask why. Recalling their trust from youth, Skirnir draws out Freyr’s wish to be with Gerd and his sense that no god or elf will permit it.", "The poem first writes desire as silence and imbalance, not as love already returned." ) }),
            scene("norse-skirnismol", ["errand", "arrival"], { title: { "zh-Hant": "穿過火焰，來到格爾德的廳堂", en: "Through Flame to Gerd’s Hall" }, narrative: sceneNarrative("斯基爾尼爾索要能穿越黑暗與火焰的馬，以及可對巨人作戰的劍。弗雷交出這些東西，使者便騎入巨人世界。", "格米爾家門前有兇犬；山上的牧人說他不是注定要死，就是已經死了。斯基爾尼爾仍進入格爾德的廳堂。", "格爾德先怕外頭來的是殺兄之人，這場會面一開始便帶著敵意與危險。", "Skirnir asks for the horse that crosses dark and flame and a sword against giants. Freyr gives them, and the messenger rides into the giant world.", "Fierce dogs stand before Gymir’s home; a herdsman says Skirnir is either doomed or dead already. Still he enters Gerd’s hall.", "Gerd first fears that the visitor outside is her brother’s slayer, so the meeting begins in danger and hostility." ) }),
            scene("norse-skirnismol", ["gifts", "threat"], { title: { "zh-Hant": "禮物被拒，劍隨即出鞘", en: "Gifts Refused; the Sword Follows" }, narrative: sceneNarrative("斯基爾尼爾先給格爾德十一個金蘋果，再給每九夜生出八環的金環。她兩次拒絕，說父家不缺黃金，也不願和弗雷同住。", "他於是亮出劍，說會砍下她的頭，又威脅她的父親。格爾德沒有因此改口，反說格米爾若發現他在此，會來戰鬥。", "詩把禮物與武力緊連，不能把這段出使說成單純的戀愛遊說。", "Skirnir first offers Gerd eleven golden apples, then a ring that makes eight more every ninth night. She refuses both, saying her father’s house lacks no gold and she will not dwell with Freyr.", "He then shows his sword, saying he will cut off her head and threatening her father. Gerd still does not yield, saying that Gymir will fight if he finds Skirnir there.", "The poem binds gifts closely to force; this errand cannot be told as simple romantic persuasion." ) }),
            scene("norse-skirnismol", ["curse", "return"], { title: { "zh-Hant": "咒語之後，只剩九夜等待", en: "After the Curse, Only Nine Nights to Wait" }, narrative: sceneNarrative("斯基爾尼爾改用魔杖與咒語，威脅格爾德孤絕、羞辱、欲求與永無婚配。這段行文有缺字與編排問題，卻沒有抹去咒語施加的壓力。", "格爾德隨後請他收下蜜酒，說九夜後會在巴里與尼奧爾德之子相會。斯基爾尼爾回去，弗雷只問何時能有消息。", "他得到的只是巴里與九夜。詩在第九夜到來之前停下，沒有替這個約期寫成已發生的結合。", "Skirnir turns to a magic wand and curse, threatening Gerd with isolation, humiliation, longing, and never having a husband. The passage has damaged and rearranged lines, yet it does not erase the pressure of the curse.", "Gerd then offers him mead and says that in nine nights she will meet Njord’s son at Barri. Skirnir returns, and Freyr asks only when he may hear news.", "He receives only Barri and nine nights. The poem stops before the ninth night, never writing the appointment as an accomplished union." ) }),
          ],
        },
      ],
    },
    {
      id: "norse-giant-world-tests-era",
      order: 3,
      title: { "zh-Hant": "巨人世界試煉", en: "Giant-World Tests" },
      summary: {
        "zh-Hant": "托爾與同行者面對失錘、偽裝、烏特加德幻術與巨蛇試煉。",
        en: "Thor and companions face the stolen hammer, disguise, Utgard illusions, and the serpent test.",
      },
      subEras: [
        {
          id: "norse-thor-tests-route",
          order: 1,
          title: {
            "zh-Hant": "交易、失竊與巨人試煉",
            en: "Bargains, Thefts, and Giant Tests",
          },
          summary: {
            "zh-Hant": "托爾的力量被偷竊、偽裝、幻術與巨蛇反覆測試。",
            en: "Thor’s strength is tested by theft, disguise, illusion, and the serpent.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene(
              "norse-thor-giant-tests",
              [
                "hammer-stolen",
                "thrym-demand",
                "bridal-disguise",
                "hammer-recovered",
              ],
              {
                title: {
                  "zh-Hant": "失錘迫使托爾進入偽裝",
                  en: "The Stolen Hammer Forces Disguise",
                },
                narrative: sceneNarrative(
                  "托爾的故事先從失去力量標誌開始。妙爾尼爾不見了，索列姆提出以芙蕾雅為代價才歸還錘；神族面對的不是公開戰鬥，而是一場把婚姻、寶物和羞辱綁在一起的勒索。",
                  "芙蕾雅拒絕後，洛基與海姆達爾等神明推動偽裝計策，托爾被迫扮成新娘前往巨人世界。宴席上，他的食量和怒氣幾乎暴露身份，洛基則把異常一一說成新娘等待婚事的反應。",
                  "當妙爾尼爾被放到「新娘」膝上祝聖婚禮時，托爾重新握住自己的力量。這場喜劇式偽裝把秩序恢復過來，也讓錘的價值變得更清楚：它被偷走時，神族的防線也跟着失守；它回到托爾手中，安全才重新站穩。笑料背後，神族其實差點以婚姻和尊嚴換回防線。",
                  "Thor’s route begins with the loss of his clearest sign of force. Mjolnir is missing, and Thrym demands Freyja as the price for returning it.",
                  "When Freyja refuses, Loki and the gods drive a disguise plot. Thor goes as the bride, nearly exposing himself through appetite and rage, while Loki explains each oddity away.",
                  "When the hammer is placed in the bride’s lap to bless the marriage, Thor regains his power. The comic disguise restores order and makes the hammer’s value unmistakable.",
                ),
              },
            ),
            scene(
              "norse-thor-giant-tests",
              ["goats-and-servants", "skrymir-threshold"],
              {
                title: {
                  "zh-Hant": "山羊骨與斯克里米爾的門檻",
                  en: "Goat Bone and Skrymir’s Threshold",
                },
                narrative: sceneNarrative(
                  "另一組試煉從旅途開始。托爾帶着洛基前行，以自己的山羊供餐，又要求骨頭不得折斷；夏爾菲犯禁後，山羊復活時留下跛足，神明的食物規則立刻變成凡人家庭的代價。",
                  "夏爾菲與羅斯克瓦因此跟隨托爾，旅途也進入巨人世界的門檻。斯克里米爾的巨大身體、手套與看似輕鬆的同行，使托爾的力量第一次被放到不穩定的尺度中；他的錘擊看似打中，實際效果卻被巨人世界吸收或轉化。",
                  "這段不是單純旅行插曲。山羊骨讓人類僕從加入隊伍，斯克里米爾則把托爾帶到一個力量感會失準的世界。後面的烏特加德試煉因此已經準備好：托爾仍強，但他將被迫面對自己看不清的規則。力量越自信，越容易被陌生尺度誤導。",
                  "Another set of tests begins on the road. Thor serves his goats as food and commands that no bone be broken; when Thialfi breaks the rule, the revived goat is lamed and the human household pays the cost.",
                  "Thialfi and Roskva then follow Thor into the giant-world threshold. Skrymir’s immense body and strange companionship place Thor’s strength into an unstable scale.",
                  "This is more than travel. The goat bone brings mortal servants into the party, and Skrymir leads Thor toward a world where strength will be measured by hidden rules.",
                ),
              },
            ),
            scene(
              "norse-thor-giant-tests",
              ["utgard-contests", "thor-illusion-tests"],
              {
                title: {
                  "zh-Hant": "烏特加德的幻術競賽",
                  en: "Illusion Contests at Utgard",
                },
                narrative: sceneNarrative(
                  "烏特加德把托爾一行人的能力變成競賽。洛基比食量，夏爾菲比奔跑，托爾則先後喝角中之酒、舉起巨貓、又與老婦角力；每一場看似失敗，實際都被幻術藏起真正尺度。",
                  "巨人首領最後揭開真相：洛基面對的是火，夏爾菲追不上思想，托爾喝的是大海，舉的是耶夢加得，角力對手則是老年。這不是嘲笑托爾無力，而是顯示他的力量已經觸及世界級的對象，只是他當時不知道自己面對的是甚麼。",
                  "這段場景把英雄式力量推到宇宙邊界。托爾仍可撼動大海、抬起巨蛇、抵抗老年片刻，但神明的力量不能取消時間、自然與幻術。巨人世界在此不是單純敵營，而是把神明自信拆開重看的地方。失敗表面上屬於托爾，震動卻已經落在世界本身。",
                  "Utgard turns the travellers’ abilities into contests. Loki eats, Thialfi runs, and Thor drinks from a horn, lifts a cat, and wrestles an old woman. Each loss hides another scale.",
                  "The ruler reveals the illusions: Loki faced fire, Thialfi chased thought, Thor drank from the sea, lifted the Midgard Serpent, and wrestled old age.",
                  "The scene pushes heroic strength to cosmic limits. Thor is powerful enough to affect sea, serpent, and age, yet he cannot abolish nature, time, or deception.",
                ),
              },
            ),
            scene("norse-thor-giant-tests", ["hymir-serpent-fishing"], {
              title: {
                "zh-Hant": "希米爾船上的釣蛇",
                en: "The Serpent Fishing with Hymir",
              },
              narrative: sceneNarrative(
                "托爾與巨蛇的關係在希米爾船上再次變得直接。這一次不是烏特加德幻術中的巨貓，而是耶夢加得被牛頭作餌釣上來；托爾把戰鬥拉到海面之上，末日敵手短暫提前相遇。",
                "當巨蛇露出水面，托爾舉錘要擊殺牠，希米爾卻因恐懼割斷釣線。來源保留了這場對峙的中斷：托爾的力量已經足以把巨蛇拉近，但故事不讓最後決戰在此完成。",
                "這段因此像一次未能落下的末日預演。托爾和耶夢加得都被推到彼此面前，卻被帶回未完成狀態；真正的互殺仍要等到諸神黃昏，這條壓力線也暫時沉回海中。下一次巨蛇離開海面時，已不再只是試探，海面短暫合上，但戰鬥的方向早已被深深記住。",
                "Thor’s relation to the serpent becomes direct again on Hymir’s boat. This time the Midgard Serpent is not an illusion-cat but a real enemy drawn up by a bull’s head bait.",
                "Thor raises the hammer to strike, but Hymir cuts the line in fear. The source keeps the confrontation interrupted: Thor has brought the serpent close, but the final battle does not happen here.",
                "The scene becomes a postponed doom rehearsal. Thor and Jormungandr meet early, then return to unfinished tension until Ragnarok. The sea closes again, but the direction of battle has been marked.",
              ),
            }),
          ],
        },
        {
          id: "norse-thrymskvitha-route",
          order: 2,
          title: {
            "zh-Hant": "索列姆之歌：失錘、新娘與反擊",
            en: "Thrymskvitha: Lost Hammer, False Bride, Reversal",
          },
          summary: {
            "zh-Hant": "索列姆把妙爾尼爾藏在地下，要以芙蕾雅為妻；托爾戴上頭紗，等錘在婚宴中落回手裡。",
            en: "Thrym hides Mjolnir underground and demands Freyja as wife; Thor puts on the veil and waits for the hammer to fall into his hands at the feast.",
          },
          chronologyMode: "parallel",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "依〈索列姆之歌〉第 1–33 節的詩內次序。詩只寫索列姆承認藏錘，沒有指名最初偷竊者；本線也不混入烏特加德或釣蛇故事。",
            en: "Ordered within Thrymskvitha stanzas 1–33. The poem has Thrym admit hiding the hammer but names no initial thief, and this route does not merge in the Utgard or fishing stories.",
          },
          segments: [
            scene("norse-thrymskvitha", ["missing-hammer", "feather-dress"], {
              title: {
                "zh-Hant": "錘不見了，羽衣飛向巨人之地",
                en: "The Hammer Is Gone; the Feather-Dress Flies",
              },
              narrative: sceneNarrative(
                "托爾醒來時，妙爾尼爾已不在身邊。他翻找四周，鬍鬚震動，先把這件事告訴洛基：人間和天上都不知道，錘卻已被偷走。",
                "兩人走到芙蕾雅的住處，洛基借走她的羽衣，飛入巨人之地。他在土丘上找到索列姆；巨人正替狗繫金繩、替馬梳鬃，像是早已等着有人前來問話。",
                "失去的不是一件可隨時替代的器物。托爾沒有錘，神族便要先用查問與飛行去追回原本能以力量守住的東西。",
                "Thor wakes with Mjolnir gone. He searches about him, beard shaking, and tells Loki that the hammer has been stolen, though no one on earth or in heaven knows how.",
                "They go to Freyja’s hall, where Loki borrows her feather-dress and flies into giant land. He finds Thrym on a mound, fastening golden leashes on dogs and smoothing the manes of his horses, as if waiting to be questioned.",
                "What is lost is not replaceable. Without the hammer, the gods must begin with inquiry and flight to recover what Thor normally guards by force.",
              ),
            }),
            scene("norse-thrymskvitha", ["demand", "freyja-refuses"], {
              title: {
                "zh-Hant": "索列姆開出條件，芙蕾雅拒絕",
                en: "Thrym Sets His Terms; Freyja Refuses",
              },
              narrative: sceneNarrative(
                "索列姆承認自己把托爾的錘埋在地下深處，並說除非芙蕾雅成為他的妻子，誰也不能把它帶回。洛基回到神族住處，把這個條件原原本本說出來。",
                "眾神於是要芙蕾雅披上新娘頭紗。她怒得讓整座大廳震動，布里辛加曼也在胸前斷裂；她說若真的前往巨人之地，所有神明都會把她看成最不知羞恥的人。",
                "她沒有成為交換品。她的拒絕把索列姆原本想用婚事鎖住的條件推回眾神手中，逼他們想出另一種取錘的辦法。",
                "Thrym admits that he has buried Thor’s hammer deep underground and says no one will take it back unless Freyja becomes his wife. Loki returns to the gods’ home and repeats the condition exactly.",
                "The gods ask Freyja to put on the bridal veil. Her anger shakes the hall and breaks Brisingamen at her breast; she says all the gods would call her shameless if she went to giant land.",
                "She does not become the price of exchange. Her refusal sends Thrym’s marriage condition back to the gods and forces them to find another way to regain the hammer.",
              ),
            }),
            scene("norse-thrymskvitha", ["council", "wedding-road"], {
              title: {
                "zh-Hant": "托爾戴上頭紗，洛基同行",
                en: "Thor Takes the Veil; Loki Goes with Him",
              },
              narrative: sceneNarrative(
                "眾神與女神聚在一起商議。海姆達爾說，讓托爾戴上新娘頭紗，再把布里辛加曼掛在胸前；裙襬垂到膝頭，鑰匙在腰間作響，寶石和帽子把偽裝做全。",
                "托爾起初不肯，說人們會笑他像女人。洛基卻提醒他，若妙爾尼爾不能帶回，巨人就會住進阿斯加德。於是托爾穿上裝束，洛基也說自己要作侍女同行。",
                "山羊拉着車衝向巨人之地，山岩震裂，土地如被火燒。托爾沒有放下怒氣，只把怒氣暫時藏在頭紗之後，等索列姆親手把錘送回來。",
                "Gods and goddesses gather in council. Heimdall says that Thor should take the bridal veil and Brisingamen, with a skirt to the knees, keys at the waist, gems on the breast, and a cap to complete the disguise.",
                "Thor first refuses, saying people will call him womanly. Loki answers that giants will live in Asgard if Mjolnir is not brought home. Thor puts on the attire, and Loki says he will go as serving-maid.",
                "Goats draw the wagon toward giant land, splitting mountains and burning earth. Thor does not lay aside his anger; he hides it behind the veil until Thrym himself sends the hammer back.",
              ),
            }),
            scene("norse-thrymskvitha", ["feast", "recovery"], {
              title: {
                "zh-Hant": "婚宴裡的破綻，祝聖時的反擊",
                en: "A Feast Near Exposure; a Hallowing Turned to Attack",
              },
              narrative: sceneNarrative(
                "索列姆以為芙蕾雅已到了，命巨人準備婚宴。假新娘一人吃下一頭牛、八條鮭魚和所有為女子準備的美食，又喝下三桶蜜酒；索列姆驚訝得問，哪有新娘這樣進食。",
                "洛基坐在旁邊，說「芙蕾雅」因為太想來巨人之地，八夜沒有吃過，也八夜沒有睡過。索列姆掀開頭紗想吻她，見到怒目如火，洛基又立刻替她把異樣說成八夜未眠的渴望。",
                "最後索列姆命人把妙爾尼爾放到新娘膝上，替兩人的婚事祝聖。托爾一見錘便大笑，先殺索列姆，再擊倒巨人；失去的力量回到手中，這場婚宴也在第 33 節隨錘的取回而結束。",
                "Thrym believes Freyja has arrived and orders a wedding feast. The false bride eats an ox, eight salmon, all the food set for women, and three tuns of mead; Thrym marvels at a bride with such an appetite.",
                "Loki sits nearby and says that ‘Freyja’ has neither eaten nor slept for eight nights because she longed so for giant land. Thrym lifts the veil to kiss her and sees eyes burning like fire, and Loki again turns the strange sight into eight sleepless nights of desire.",
                "At last Thrym orders Mjolnir placed in the bride’s lap to hallow the marriage. Thor laughs when he sees the hammer, kills Thrym first, then strikes down the giants; strength returns to his hand, and the poem ends at stanza 33 with the hammer recovered.",
              ),
            }),
          ],
        },
        {
          id: "norse-geirrod-journey-route",
          order: 3,
          title: {
            "zh-Hant": "托爾到蓋羅德的廳堂",
            en: "Thor at Geirrod's Hall",
          },
          summary: {
            "zh-Hant":
              "一則有完整內部次序、但未與其他神話絕對排序的巨人世界故事。",
            en: "A giant-world story with internal sequence but no absolute place among the other myths.",
          },
          chronologyMode: "parallel",
          coverageStatus: "complete",
          segments: [
            scene(
              "norse-geirrod-journey",
              [
                "geirrod-captures-loki",
                "loki-ransoms-life",
                "gridr-warns-thor",
              ],
              {
                title: {
                  "zh-Hant": "洛基的贖命把托爾帶進陷阱",
                  en: "Loki's Ransom Leads Thor into the Trap",
                },
                narrative: sceneNarrative(
                  "蓋羅德捉住鷹形的洛基，三個月不給食物，逼他以誓言換命。洛基答應把托爾帶到巨人住處，並讓他不帶妙爾尼爾與力量腰帶。",
                  "托爾途中住進格里德家。她明說蓋羅德狡詐難纏，借出力量腰帶、鐵手套和自己的杖；來源也稱她是沉默的維達爾之母。",
                  "托爾並非毫無準備地踏入陷阱，但他能使用的防身之物已不再是原本習以為常的武器。這條路的壓力由洛基的求生選擇開始，再由格里德的具體援助改變。",
                  "Geirrod captures Loki in hawk form, starves him for three months, and forces an oath to bring Thor without Mjolnir or the strength-girdle.",
                  "On the way Thor stays with Gridr, who warns him and lends girdle, iron gloves, and her staff. The account also calls her Vidar's mother.",
                  "Thor does not enter wholly unprepared, but his protection is now borrowed rather than customary. Loki's survival choice begins the pressure, and Gridr's concrete help changes it.",
                ),
              },
            ),
            scene(
              "norse-geirrod-journey",
              [
                "vimur-rises",
                "gjalp-at-source",
                "chair-trap-at-geirrod-hall",
                "thor-crushes-daughters",
              ],
              {
                title: {
                  "zh-Hant": "河流與廳堂連成兩重伏擊",
                  en: "River and Hall Become Two Ambushes",
                },
                narrative: sceneNarrative(
                  "托爾涉入維穆爾時，河水升到肩頭；洛基抓住力量腰帶跟在後方。托爾看見加爾普橫跨山溝，正令洪水暴漲。",
                  "他向水源擲石，靠花楸登岸。到了蓋羅德的廳堂，加爾普與格雷普又藏在唯一的椅子下，讓托爾向屋頂升去；托爾以格里德之杖抵住屋樑，反壓二人。",
                  "來源把旅程拆成河流與屋內的連續阻攔：一重想把他困在路上，另一重想把他困在座位上。托爾能前進，是因為每次都把對方的安排變成可以反制的支點。",
                  "At Vimur the water rises to Thor's shoulders, with Loki holding on behind the girdle. Thor sees Gjalp causing the spate from the ravines.",
                  "He throws a stone and reaches shore by a rowan. In Geirrod's hall, Gjalp and Greip raise his chair toward the roof, and Thor braces Gridr's staff against the rafters.",
                  "The route makes river and hall into linked ambushes: one would stop him on the way, the other in his seat. Each time Thor turns the opponent's arrangement into a point from which to resist.",
                ),
              },
            ),
            scene("norse-geirrod-journey", ["thor-kills-geirrod"], {
              title: {
                "zh-Hant": "熾鐵回到蓋羅德身上",
                en: "The Heated Iron Returns to Geirrod",
              },
              narrative: sceneNarrative(
                "蓋羅德在滿是火的廳堂裡拿起燒紅鐵條，躲到鐵柱後再擲向托爾。",
                "托爾以鐵手套接住它，把鐵條擲回，穿過蓋羅德、鐵柱和牆，直入地中。",
                "故事的結尾不讓托爾以慣用的錘取勝，而讓格里德借出的手套承接蓋羅德最後的攻擊。陷阱的主人被自己的器物擊中，旅程的每一次借力也在此收束。",
                "In the fire-filled hall, Geirrod takes a glowing iron bar, hides behind an iron pillar, and throws it at Thor.",
                "Thor catches it with the iron gloves and returns it through Geirrod, pillar, wall, and into the earth.",
                "The ending does not give Thor victory by his customary hammer. Gridr's borrowed gloves receive Geirrod's final attack, and the trap's owner is struck by his own weapon.",
              ),
            }),
          ],
        },
        {
          id: "norse-hymiskvitha-route",
          order: 4,
          title: {
            "zh-Hant": "希米爾之歌：大鍋、世界蛇與宴席",
            en: "Hymiskvitha: Kettle, Serpent, and Feast",
          },
          summary: {
            "zh-Hant": "埃吉爾需要大鍋，托爾與提爾前往希米爾家；釣起世界蛇後，托爾把大鍋帶回眾神。",
            en: "Ægir needs a kettle, so Thor and Tyr go to Hymir’s home; after the world-serpent encounter, Thor returns it to the gods.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "依《希米爾之歌》的場景順序排列。世界蛇在本詩中沉回海裡，提爾對希米爾的父系說法亦只屬這條來源線。",
            en: "Ordered by Hymiskvitha’s scene sequence. The serpent sinks back into the sea here, and Tyr’s Hymir parentage belongs to this source route alone.",
          },
          segments: allSteps("norse-hymiskvitha", [
            "feast-needs-kettle",
            "tyr-knows-house-beyond-waves",
            "guests-beneath-kettles",
            "ox-head-for-sea",
            "serpent-reaches-boat",
            "strength-tested-way-home",
            "kettle-too-heavy-tyr",
            "ale-at-aegirs-hall",
          ]),
        },
      ],
    },
    {
      id: "norse-bindings-and-safeguards-era",
      order: 4,
      title: {
        "zh-Hant": "束縛、誓言與脆弱守護",
        en: "Bindings, Oaths, and Fragile Safeguards",
      },
      summary: {
        "zh-Hant":
          "芬里爾受縛與多個神族守護片段共同顯示誓言、工藝與巨人餘波如何維持又破壞秩序。",
        en: "Fenrir and related safeguard fragments show how oaths, craft, and giant aftershocks preserve and damage order.",
      },
      subEras: [
        {
          id: "norse-fenrir-binding-route",
          order: 1,
          title: { "zh-Hant": "芬里爾受縛", en: "Binding of Fenrir" },
          summary: {
            "zh-Hant":
              "芬里爾被帶到神前、被餵養、被試鏈，最後以提爾的手為代價受縛。",
            en: "Fenrir is brought before the gods, fed, tested with fetters, and bound at the cost of Tyr’s hand.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene(
              "binding-of-fenrir",
              ["fenrir-dangerous-kindred", "tyr-feeds-growing-wolf"],
              {
                title: {
                  "zh-Hant": "危險血脈進入神族住所",
                  en: "Dangerous Kin Enter the Gods’ Home",
                },
                narrative: sceneNarrative(
                  "芬里爾故事不是從咬斷手開始，而是從神明聽見預言開始。洛基與安格爾波達的子女在巨人世界長大，預言卻說災禍會由這一族而來；奧丁於是把危機帶到神族面前。",
                  "芬里爾在眾神眼前長大，其他神明越來越不敢接近，只有提爾願意餵養牠。這份日常靠近使提爾與狼之間先有信任痕跡，也讓神族的恐懼不再只是遠方傳聞。",
                  "這段開場把預言、親族和治理困境放在同一處。神族把危險收進中心，卻無法消除它；提爾的勇氣也先於犧牲被看見，為後來 Lyngvi 島上的保證埋下最沉重的代價。狼還未受縛，破裂已經在共同生活中慢慢長大，控制從一開始就混着親近、恐懼和背信的長久可能性。",
                  "Fenrir’s story does not begin with the bitten hand. It begins when the gods hear prophecy about Loki and Angrboda’s children and bring that danger before them.",
                  "Fenrir grows under divine eyes. The other gods fear approaching him, while Tyr alone feeds him, leaving a trace of trust before any binding takes place.",
                  "The opening joins prophecy, kinship, and rule. The gods bring danger into their centre without removing it, and Tyr’s courage becomes the ground for the later pledge on Lyngvi. Control is mixed from the start with closeness, fear, and the possibility of betrayal.",
                ),
              },
            ),
            scene("binding-of-fenrir", ["laeding-breaks", "dromi-breaks"], {
              title: {
                "zh-Hant": "兩條鐵鏈先後失效",
                en: "Two Iron Fetters Fail",
              },
              narrative: sceneNarrative(
                "當芬里爾的力量變得無法忽視，神族先以 Lædingr 試探牠。他們把束縛說成試力和名聲，芬里爾接受挑戰，一掙便把鐵鏈崩斷；第一次控制失敗反而證明牠更可怕。",
                "第二條 Drómi 更強，話術也更接近陷阱。芬里爾知道難度提高，仍然讓他們綁上；結果鐵鏈再次被扯斷，神族明白不能只用更重的鐵去回答這頭狼。",
                "兩次失敗把故事從力量競賽推向欺瞞與工藝。芬里爾已證明普通束縛無效，神族若要成功，就必須找一種看似微弱、卻不按鐵鏈規則運作的東西。神族越想控制牠，也越清楚自己不能正面勝過牠；每一次斷裂都讓狼更有聲望，也讓眾神更難光明正大地收場，局面越來越壞。",
                "When Fenrir’s strength can no longer be ignored, the gods first test him with Laedingr. They frame the fetter as a matter of strength and fame, but Fenrir snaps it apart.",
                "Dromi is stronger, and the challenge feels more dangerous. Fenrir still accepts, and the second fetter also breaks.",
                "The failures move the story from strength to craft and deception. Ordinary iron cannot answer the wolf; the gods need something that does not behave like iron at all. Each break gives the wolf more fame and leaves the gods with less room for an honourable ending.",
              ),
            }),
            scene(
              "binding-of-fenrir",
              ["gleipnir-from-dark-elves", "pledge-at-lyngvi"],
              {
                title: {
                  "zh-Hant": "Gleipnir 把試力變成誓言",
                  en: "Gleipnir Turns Strength into Pledge",
                },
                narrative: sceneNarrative(
                  "鐵鏈失敗後，奧丁轉向隱秘工藝。斯基爾尼爾被派往黑精靈之地，侏儒製成 Gleipnir；它由貓足聲、女人鬍鬚、石根、熊筋、魚氣與鳥唾等不可能之物構成，外表卻像柔軟細絲。",
                  "神族把芬里爾帶到阿姆斯瓦特尼爾湖中的 Lyngvi 島，展示這條看似無害的束帶。芬里爾看出可疑：若它真的微弱，掙脫沒有名聲；若有陷阱，神族未必會釋放牠。",
                  "於是牠要求有人把手放進自己口中作保，提爾把手伸進狼口。從這一刻起，諸神不再只是測試一條束帶，而是把自己的信用押在承諾上；Gleipnir 會綁住狼，提爾的手則承受失信的代價。柔軟束帶真正考驗的，是神明會否守信。因此，最細的束帶也成了最沉的道德重量。",
                  "After iron fails, Odin turns to hidden craft. Skirnir is sent to the Black Elves’ region, and the dwarves make Gleipnir from impossible things, though it looks like a soft ribbon.",
                  "The gods bring Fenrir to Lyngvi and show him the binding. He sees the problem: if it is weak, breaking it brings no fame; if it hides a trap, the gods may not release him.",
                  "Fenrir demands a hand as pledge, and Tyr places his hand in the wolf's mouth. From that moment, the gods are not only testing a band; they are staking their honour on a promise. Gleipnir will bind the wolf, and Tyr's hand will bear the cost if they break their word. The light ribbon tests whether the gods will keep faith, so the smallest binding becomes the heaviest moral weight.",
                ),
                primaryFocus: { kind: "node", nodeId: "gleipnir" },
              },
            ),
            scene("binding-of-fenrir", ["tyr-loses-hand"], {
              title: {
                "zh-Hant": "提爾失手，芬里爾留到末日",
                en: "Tyr Loses a Hand, Fenrir Remains Until Doom",
              },
              narrative: sceneNarrative(
                "Gleipnir 套上後，芬里爾立即全力掙扎。前兩條鐵鏈都因牠的力量斷裂，這一次束帶卻越掙越緊；諸神看見束縛成功，沒有依約放牠。",
                "芬里爾明白保證已被背棄，便咬下提爾仍在口中的手。神族暫時控制了預言中的狼，卻把失信寫進自己的秩序；提爾原本把右手放進狼口作保，如今成為獨手的神，缺手也成為這場勝利永遠可見的代價。",
                "束帶困住芬里爾，卻沒有取消預言。神族得到的是喘息，不是無代價的安全：一頭狼留在深處，提爾失去右手，而那份以承諾換來的信任再也不能回到原來的樣子。從此以後，提爾的斷手也提醒神明，恐懼會要求他們付出甚麼。",
                "Once Gleipnir is placed on Fenrir, he strains with all his power. The ribbon tightens instead of breaking, and the gods do not release him.",
                "Fenrir understands the broken pledge and bites off Tyr’s hand. The gods control the wolf for now, but deception enters divine order, and Tyr’s missing hand becomes the visible cost.",
                "The band holds Fenrir but does not cancel prophecy; the gods gain only a temporary breath at the cost of Tyr’s hand.",
              ),
            }),
            scene("binding-of-fenrir", ["gelgja-gjoll-thviti-fastening"], {
              title: {
                "zh-Hant": "束縛被拉進深處",
                en: "The Binding Is Drawn into the Deep",
              },
              narrative: sceneNarrative(
                "Gleipnir 困住芬里爾後，神族仍要把這一瞬間的成功變成長久困禁。",
                "神族把 Gelgja 穿過束帶末端，接上巨大的 Gjoll 石，再把石頭固定在 Thviti。束帶收緊後，這些沉重的固定物不讓芬里爾把它拖回湖岸；每次反撲都拉向更深的重量，牠越想逃，越遠離神族，也越難再看見諸神。狼的怒氣沒有消失，只被石與深處一同壓住。",
                "這不是新一場比試，而是把剛剛贏得的片刻，變成再也不能自行結束的囚禁。預言沒有被消除，只被壓到看不見的地方。芬里爾留在深處，神族則知道危險仍在等待，束縛也沒有鬆開。石、鏈與深處把神族暫時贏得的安全，變成牠每天都要承受的重量。",
                "Once Gleipnir catches Fenrir, the gods turn a moment of success into lasting confinement.",
                "The gods do not leave the binding at the lakeshore. They carry their success downward, so every struggle meets a deeper weight and Fenrir is pulled farther from the gods’ sight.",
                "This is not another trial but the moment of victory turned into a captivity Fenrir cannot end himself. Prophecy is not erased, only pressed out of sight; he remains below, and the danger still waits. Stone, chain, and depth turn the gods’ temporary safety into a weight he must continue to bear.",
              ),
            }),
            scene(
              "binding-of-fenrir",
              ["sword-and-van-river", "bound-until-ragnarok"],
              {
                title: {
                  "zh-Hant": "狼口的痛楚留成河名",
                  en: "The Wolf’s Pain Remains as a River",
                },
                narrative: sceneNarrative(
                  "困住芬里爾的身體並不等於消除牠的威脅。神族把一把劍塞進狼口，令牠被迫張開雙顎。",
                  "從牠口中流出的涎水形成范河。提爾的手、狼口的劍和河水都記住神族如何換取暫時安全。神族不殺芬里爾，因為不願以狼血玷污他們視為聖地的地方；他們選擇把牠留在那裡。",
                  "芬里爾留到諸神黃昏；受縛延遲牠與奧丁的命運，卻沒有解除它。神族用看守換得一時安寧，卻一直知道受縛不等於無害。這份克制沒有使威脅消失，只把日後必須面對的災難留給更遠的時刻。狼口仍張着，河水仍在流，而神族的安靜始終帶着未來會破裂的陰影。看守也不會停止，牠的怒氣也留在河名與神族記憶裏。",
                  "Holding Fenrir’s body does not remove the threat. The gods force a sword into his mouth and leave his jaws open.",
                  "The slaver that runs from him becomes River Van. Tyr’s hand, the sword, and the water remember how the gods bought temporary safety.",
                  "Fenrir remains for Ragnarok; the binding delays his fate with Odin without dissolving it.",
                ),
              },
            ),
          ],
        },
        {
          id: "norse-fragile-safeguards-route",
          order: 2,
          title: {
            "zh-Hant": "脆弱守護與巨人餘波",
            en: "Fragile Safeguards and Giant Aftershocks",
          },
          summary: {
            "zh-Hant":
              "短篇片段補足束縛細節、青春守護、寶物代價、智慧、建城交易與巨人決鬥。",
            en: "Shorter fragments add binding details, guarded youth, treasure costs, wisdom, a building bargain, and giant duels.",
          },
          chronologyMode: "parallel",
          coverageStatus: "complete",
          segments: [
            scene(
              "norse-oaths-treasures-giants",
              [
                "laeding-and-dromi-named",
                "gelgja-gjoll-thviti-fastening",
                "sword-and-van-river",
              ],
              {
                title: {
                  "zh-Hant": "受縛留下名字、錨點與痛苦",
                  en: "The Binding Leaves Names, Anchors, and Pain",
                },
                narrative: sceneNarrative(
                  "芬里爾受縛的細節在這條支線中被重新放大。Lædingr 與 Drómi 不再只是失敗的鐵鏈，而是被命名的前兩次嘗試；Gleipnir 的成功也因此建立在普通力量兩度失效之上。",
                  "束帶隨後被接到 Gelgja、Gjoll 與 Thviti 等固定物上，受縛不只是套在狼身上的柔帶，而是一整組把危機壓到深處的裝置。劍撐住狼口，Ván 河則留下牠流出的痛苦。",
                  "這些細節不改寫芬里爾故事，卻讓它的代價更具體。神族把預言中的狼鎖住，也留下鐵鏈之名、深處錨點、張開的狼口與流動的痛苦；束縛因此成為地景和記憶的一部分。每個名字都把一層控制與一層傷口固定下來。",
                  "This sequence magnifies the details of Fenrir’s binding. Laedingr and Dromi are not only failed fetters; their names preserve the two attempts that make Gleipnir’s success meaningful.",
                  "Gleipnir is then fixed through Gelgja, Gjoll, and Thviti, while the sword and the river Van preserve the wolf’s pain.",
                  "The details do not rewrite the Fenrir story. They make its cost material: names, anchors, a forced-open mouth, and flowing pain turn the binding into landscape and memory.",
                ),
              },
            ),
            scene(
              "norse-oaths-treasures-giants",
              [
                "bragi-and-idunn",
                "thjazi-takes-idunn",
                "idunn-restored-thjazi-atoned",
              ],
              {
                title: {
                  "zh-Hant": "伊登被奪，神族青春動搖",
                  en: "Idunn Is Taken and Divine Youth Falters",
                },
                narrative: sceneNarrative(
                  "神族秩序的另一種脆弱藏在青春裏。布拉基與伊登的關係先把詩歌、婚姻和青春守護放在一起；伊登的蘋果不是普通食物，而是神明持續年輕的條件。",
                  "夏基奪走伊登後，神族立刻變老，洛基被迫補救。他把伊登帶回，夏基追趕而來，最後在阿斯神族的防線中被殺；危機解除，卻沒有把巨人世界的餘波完全抹去。",
                  "這段故事說明神族不是天然不朽到毫無依賴。他們需要被守護的器物，也會因洛基牽出的事故而迅速衰敗。伊登回來後，青春恢復，但神族與巨人世界的互害又多了一層記憶。年輕不再像永久屬性，而像需要反覆保住的秩序資源；青春因此變成要被看守、追回和重新安放的共同生命線。",
                  "Another divine fragility lies in youth. Bragi and Idunn first join poetry, marriage, and guarded renewal; her apples are the condition by which the gods remain young.",
                  "When Thjazi takes Idunn, the gods age, and Loki is forced to repair the harm. He brings her back, Thjazi pursues, and the gods kill the giant at their boundary.",
                  "The scene shows that the gods depend on guarded things. Youth returns, but the aftershock of giant-world harm remains in divine memory. Renewal has to be watched, recovered, and placed back inside order.",
                ),
              },
            ),
            scene(
              "norse-oaths-treasures-giants",
              ["sif-hair-and-treasures", "sif-and-ullr", "mimir-and-wisdom"],
              {
                title: {
                  "zh-Hant": "頭髮、寶物與智慧之泉",
                  en: "Hair, Treasures, and the Well of Wisdom",
                },
                narrative: sceneNarrative(
                  "洛基剪去希芙的頭髮，表面上像一場惡作劇，實際卻牽出更大的補償。為了修補損害，他推動侏儒工藝，金髮與寶物再次進入神族秩序。",
                  "希芙與烏勒爾的神族位置也在這些片段中被固定：她不是只有受害者身份，還連到家庭與後續神族名單；烏勒爾則作為重要神名保留在秩序邊緣。",
                  "密米爾與智慧之泉帶來另一種守護。神族需要武器、青春和寶物，也需要記憶與智慧的深處來源。這組短篇讓神族世界更像一個靠多種支點維持的整體，而不是只有奧丁與托爾的戰鬥場。物件、親族和知識各自承擔不同的保護重量；少了任何一點，神族中心都會顯出新的裂縫、依賴和補償壓力，並迅速失衡。",
                  "Loki cutting Sif’s hair looks like mischief, but it draws out a larger act of compensation. Dwarven craft repairs the harm and brings new treasures into divine order.",
                  "Sif and Ullr also gain clearer places within the divine network: she is more than the victim of the episode, and Ullr remains a significant divine name at the edge of the order.",
                  "Mimir and the well of wisdom add another support. The gods need weapons, youth, treasures, memory, and wisdom; their world rests on many fragile points. Lose any one of them, and new dependence appears at the centre.",
                ),
              },
            ),
            scene(
              "norse-oaths-treasures-giants",
              ["builder-bargain", "builder-wage-lost"],
              {
                title: {
                  "zh-Hant": "建城交易差點交出太陽、月亮與芙蕾雅",
                  en: "The Builder Bargain Nearly Costs Too Much",
                },
                narrative: sceneNarrative(
                  "阿斯加德的防線也由危險交易塑成。建城巨人提出建牆，報酬卻是芙蕾雅、太陽和月亮；神族接受附帶期限的協議，以為可以讓條件變得不可能。",
                  "工程因斯瓦迪爾法利的力量而迅速推進，神族發現自己快要輸掉不該交出的代價。洛基被迫干預，變形引開馬匹，使建城者無法如期完成；托爾最後以暴力終止失控的交易。",
                  "這段不是單純說明城牆來源，而是暴露神族秩序怎樣靠賭注、期限和補救維持。防線被建立，太陽、月亮與芙蕾雅沒有交出，但神族也再次證明自己的安全常常建立在高風險承諾之上。城牆越堅固，背後的破約壓力越清楚；秩序看似被城牆保護，實際也被自己的承諾追趕。",
                  "Asgard’s defence is shaped by a dangerous bargain. A builder offers to make the wall, but the requested payment is Freyja, the sun, and the moon.",
                  "The work advances too quickly because of Svadilfari. Loki is forced to intervene, draws the horse away, and Thor ends the broken bargain with violence.",
                  "The scene is not merely an origin for the wall. It exposes how divine safety depends on wagers, deadlines, and risky repair. The wall protects order, but the promise behind it keeps chasing the gods.",
                ),
              },
            ),
            scene(
              "norse-oaths-treasures-giants",
              ["hrungnir-in-asgard", "hrungnir-duel", "vali-avenger-survivor"],
              {
                title: {
                  "zh-Hant": "赫朗格尼爾闖席，瓦利連到復仇與新生",
                  en: "Hrungnir Intrudes, Vali Links Vengeance and Renewal",
                },
                narrative: sceneNarrative(
                  "赫朗格尼爾的故事把巨人威脅帶進神族宴席。他闖入阿斯加德，在酒與誇口中把神族中心變成危險場所；問題不只在邊界之外，而是已經坐到神明席上。",
                  "托爾與赫朗格尼爾的決鬥把這份侮辱轉回戰鬥。巨人倒下，托爾也承受碎石與傷勢，顯示神族可以壓下入侵，卻不是毫無代價地維持尊嚴。",
                  "瓦利在這段故事末端帶來另一種時間壓力：他既連到巴德爾之死後的復仇，也在火後新生名單中留存。這使短篇不只是普通插曲，而是在神族受辱、復仇責任與末日後延續之間架起一條暗線。巨人入席、雷神受傷與新世界名單彼此呼應；短篇因此把局部衝突接到後來更大的毀滅與延續。",
                  "Hrungnir’s story brings giant threat into the gods’ feast. He enters Asgard, and boasting turns the divine centre itself into a dangerous place.",
                  "Thor’s duel returns the insult to combat. The giant falls, but Thor is wounded by stone, so divine honour is preserved at a cost.",
                  "Vali then links this story to another pressure: vengeance after Baldr’s death and survival after the fire. The short fragments become a line between insult, revenge, and renewal, not a detachable aside.",
                ),
              },
            ),
          ],
        },
      ],
    },
    {
      id: "norse-death-and-punishment-era",
      order: 5,
      title: {
        "zh-Hant": "死亡、拒絕與受罰",
        en: "Deaths, Refusals, and Punishments",
      },
      summary: {
        "zh-Hant":
          "巴德爾之死、赫爾之行、托克拒哭與洛基受縛把秩序推向末日陰影。",
        en: "Baldr's death, Hermod's ride, Thokk's refusal, and Loki's binding move order toward doom.",
      },
      subEras: [
        {
          id: "norse-baldrs-draumar-route",
          order: 1,
          title: { "zh-Hant": "巴德爾之夢", en: "Baldr’s Dreams" },
          summary: {
            "zh-Hant": "奧丁下到冥界求問，女先知只給出死亡、報復與末日的預言。",
            en: "Odin descends to the underworld for an answer, and the seeress gives only prophecy of death, vengeance, and doom.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene("norse-baldrs-draumar", ["baldrs-dreams", "odin-descends"], {
              title: { "zh-Hant": "惡夢把奧丁帶往尼弗爾赫爾", en: "Dark Dreams Carry Odin to Niflhel" },
              narrative: sceneNarrative(
                "巴德爾連續做下不祥夢。眾神與女神聚在一起，不知道死亡的影子為何落到他身上。",
                "奧丁沒有留在會議裡等待答案。他替斯萊普尼爾備鞍，穿過加姆，騎到赫爾的高殿與一座女先知的墳前。",
                "危險還沒有化成一擊，神族卻已經知道它正在靠近。奧丁的路徑不是為了阻止一場已發生的死亡，而是想在命運落下以前問清楚它的名字。",
                "Baldr begins to dream dark dreams. Gods and goddesses gather, unable to tell why death’s shadow has fallen on him.",
                "Odin does not wait in the council. He saddles Sleipnir, passes Garm, and rides to Hel’s high hall and a seeress’s grave.",
                "The danger has not become a blow, but the gods already know it is near. Odin rides not to undo a death, but to name it before it arrives.",
              ),
            }),
            scene("norse-baldrs-draumar", ["seeress-rises", "false-name"], {
              title: { "zh-Hant": "墓門打開，奧丁先說假名", en: "The Grave Opens; Odin Gives a False Name" },
              narrative: sceneNarrative(
                "奧丁在東面的墓門前施法，長眠的女先知被迫起來。她先問是誰把她從雪、雨與露水下喚回。",
                "奧丁沒有報出本名，只自稱道路馴服者。他問赫爾為誰鋪滿金飾，又為誰釀下蜜酒。",
                "這段對話從一開始就不平等：奧丁要從死者口中取得答案，女先知也很快看出他正在遮掩身分。詩保留了她的沉默與抵抗，沒有把她變成順從的傳話者。",
                "Odin works magic at the eastern grave-door, compelling the long-dead seeress to rise. She first asks who has called her back from snow, rain, and dew.",
                "Odin does not give his name. Calling himself Way-tamer, he asks for whom Hel has spread rings and brewed mead.",
                "The exchange is unequal from the start: Odin seeks an answer from the dead, and the seeress soon sees that he hides his identity. The poem keeps her resistance and silence intact.",
              ),
            }),
            scene("norse-baldrs-draumar", ["mead-for-baldr", "hodr-named"], {
              title: { "zh-Hant": "蜜酒為巴德爾而備，霍德爾被說出", en: "Mead Is Ready for Baldr; Hodr Is Named" },
              narrative: sceneNarrative(
                "女先知說赫爾已為巴德爾備下明亮的蜜酒。這不是安慰，而是一幅死亡已預先安排好的圖景。",
                "奧丁追問誰會奪去巴德爾的生命，女先知答道：霍德爾。她沒有講槲寄生，也沒有講射擊；這首詩只把將至的死亡說成一句無法收回的預言。",
                "巴德爾仍在生者的世界，冥界卻已有他的席位。詩的恐怖不在於展示殺害，而在於答案已經在死者的屋裡等着，眾神卻不能從預言中得到逃路。",
                "The seeress says bright mead is already prepared for Baldr in Hel. This is no comfort; it is an image of death already arranged.",
                "Odin asks who will take Baldr’s life, and she answers: Hodr. She names neither mistletoe nor a shot; this poem makes the coming death an irrevocable prophecy.",
                "Baldr still belongs to the living world, but the dead already have a place for him. The dread lies not in showing the killing, but in hearing that the answer waits where the gods cannot escape it.",
              ),
            }),
            scene("norse-baldrs-draumar", ["vali-named", "seeress-unmasks-odin"], {
              title: { "zh-Hant": "瓦利的預言，與仍未到來的末日", en: "Vali’s Prophecy and a Doom Still to Come" },
              narrative: sceneNarrative(
                "奧丁再問誰會為巴德爾報復。女先知說琳德爾會生下瓦利；他尚未洗手梳髮便會出戰。",
                "當奧丁又問哭泣的少女，女先知終於叫破他的名字。她讓他回家，並說在洛基脫縛、諸神遭劫以前，再不會有人像他這樣來訪。",
                "這不是已完成的復仇或末日，而是連續向前逼近的預言。巴德爾的夢使死亡有了名字，瓦利使報復有了名字，洛基的脫縛則把這一夜的求問接到更遙遠的毀滅。",
                "Odin asks who will avenge Baldr. The seeress says Rindr will bear Vali, who will fight before he has washed or combed his hair.",
                "When Odin asks once more, the seeress finally speaks his true name. She tells him to ride home and says no one will visit her so again before Loki is loosed and the gods meet their doom.",
                "Neither vengeance nor doom has happened here; both press forward as prophecy. Baldr’s dream gives death a name, Vali gives vengeance a name, and Loki’s release carries this night’s question toward a farther destruction.",
              ),
            }),
          ],
        },
        {
          id: "norse-baldr-loki-route",
          order: 2,
          title: { "zh-Hant": "巴德爾與洛基", en: "Baldr and Loki" },
          summary: {
            "zh-Hant": "巴德爾的死亡與洛基受縛，把神族秩序推向不可逆的裂口。",
            en: "Baldr’s death and Loki’s binding push divine order toward an irreversible break.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene(
              "norse-baldr-loki",
              ["baldr-dreams", "frigg-oaths", "loki-finds-mistletoe"],
              {
                title: {
                  "zh-Hant": "夢、誓言與被遺漏的槲寄生",
                  en: "Dreams, Oaths, and the Overlooked Mistletoe",
                },
                narrative: sceneNarrative(
                  "巴德爾的故事先以夢開始。死亡預兆進入神族中心，奧丁與弗麗嘉的家庭不再只是權威所在，也成為恐懼所在；神明知道最明亮的神可能會被奪走。",
                  "弗麗嘉向萬物取誓，要求它們不傷害巴德爾。誓言看似把世界全部納入保護，但槲寄生因細小而被遺漏；洛基得知這個缺口後，故事立刻從守護轉向可被利用的漏洞。",
                  "這段場景把悲劇寫得很精準：神族不是沒有保護巴德爾，而是保護近乎完整卻不完整。被排除在誓言之外的小枝條，會比眾神看得見的武器更致命。死亡因此不是從外敵闖入，而是從保護系統的一點空隙長出來；越細小的遺漏，越能穿透最完整的集體防線，並徹底改寫神族命運。",
                  "Baldr’s story begins with dreams. Death enters the divine household as a warning, and Odin and Frigg’s centre becomes a place of fear.",
                  "Frigg takes oaths from things not to harm Baldr, but mistletoe is left out because it seems too small. Loki learns the gap and turns protection into vulnerability.",
                  "The tragedy is precise: the gods do protect Baldr, but not completely. A small excluded thing will become deadlier than visible weapons, piercing the collective defence and reshaping divine fate.",
                ),
              },
            ),
            scene("norse-baldr-loki", ["hodr-shot", "funeral-and-draupnir"], {
              title: {
                "zh-Hant": "霍德爾一擊與巴德爾葬禮",
                en: "Hodr’s Shot and Baldr’s Funeral",
              },
              narrative: sceneNarrative(
                "神明把巴德爾的無傷變成遊戲，向他投擲物件而不造成傷害。洛基把槲寄生交給霍德爾，引導這位盲眼神明射出致命一擊；歡樂場面在一瞬間變成死亡現場。",
                "巴德爾倒下後，神族為他舉行葬禮。南娜死去，德羅普尼爾被放到葬船上，托爾以錘祝聖火葬；那些前面象徵秩序和寶物的物件，現在進入喪禮。",
                "這段把神族世界從可修補的危機推到失去不可逆的位置。巴德爾之死不是戰場上的普通死亡，而是發生在保護誓言、兄弟之手和洛基計策交會之處，神族的內部信任因此破裂。葬船帶走的不只是一位神，也是一種曾以為能保全的秩序；從此，寶物與祝聖也只能陪伴失去，不能真正逆轉失去。",
                "The gods turn Baldr’s safety into a game, throwing things at him without harm. Loki gives mistletoe to blind Hodr and guides the fatal shot.",
                "Baldr falls, and the gods hold his funeral. Nanna dies, Draupnir is placed on the ship, and Thor hallows the burning with the hammer.",
                "The scene moves divine order from repairable danger to irreversible loss. Baldr dies where oath, brotherly hand, and Loki’s scheme meet. Treasure and hallowing can accompany loss, but they cannot undo it.",
              ),
            }),
            scene("norse-baldr-loki", ["hermod-to-hel", "thokk-refuses"], {
              title: {
                "zh-Hant": "赫爾提出條件，托克拒絕哭泣",
                en: "Hel Sets the Condition, Thokk Refuses to Weep",
              },
              narrative: sceneNarrative(
                "死亡之後，神族仍嘗試把巴德爾帶回。赫爾莫德騎往赫爾，求死者世界釋放巴德爾；故事從阿斯加德的葬禮走到死者所在之地。",
                "赫爾提出條件：若萬物都為巴德爾哭泣，他可以回來。世界似乎願意哀悼，直到托克拒絕哭泣；這個拒絕使條件失敗，也讓懷疑再次指向洛基的變形與惡意。",
                "這段場景把悲劇鎖死。巴德爾不只是被殺，也被拒絕回返；神族曾用誓言保護他，現在又用哀悼試圖救他，兩次近乎全體的行動都因一個缺口失敗。前一次缺口是槲寄生，這一次則是一個拒絕哭泣的聲音；悲劇的重量，正來自差一點全世界都已同意救回他，卻仍被最後一個聲音關上出口，也把希望留在門外。",
                "After death, the gods still try to bring Baldr back. Hermod rides to Hel and asks the realm of the dead to release him.",
                "Hel sets a condition: if all things weep for Baldr, he may return. The world seems to mourn until Thokk refuses, and suspicion returns to Loki.",
                "The tragedy is locked. Baldr is not only killed; he is denied return. Two almost universal acts, oath and mourning, fail because of one gap. Its weight comes from how close the whole world came to bringing him back.",
              ),
            }),
            scene(
              "norse-baldr-loki",
              ["loki-bound", "sigyn-venom-until-ragnarok"],
              {
                title: {
                  "zh-Hant": "洛基被捕並受縛",
                  en: "Loki Is Caught and Bound",
                },
                narrative: sceneNarrative(
                  "托克拒哭後，洛基不再只是惹禍者，而成為神族必須處置的破裂來源。他變形逃避，藏在遠處，仍然不能完全離開神族追捕的範圍。",
                  "神明捕住洛基，以他兒子的命運作成懲罰，把他綁在石上，蛇毒滴落在他臉上；西格恩在旁接毒，當她移開盆時，痛苦使洛基掙動，大地也隨之震動。",
                  "這段受縛與芬里爾受縛互相呼應，但情感更黑暗。神族把洛基固定住，卻沒有修復巴德爾之死；他被留到末日前，像一個仍會回到世界的傷口。毒液、守候與震動都在提醒神族：懲罰能延後危機，不能取消裂口。神族把破壞者鎖住，也把自己無法癒合的故事鎖在世界深處，等它在末日重新裂開、再次猛烈震動。",
                  "After Thokk’s refusal, Loki is no longer merely troublesome; he becomes the rupture the gods must punish. He changes shape and hides, but cannot escape.",
                  "The gods catch him, bind him to stones through the fate of his sons, and set venom dripping above his face while Sigyn tries to catch it.",
                  "This binding echoes Fenrir’s, but darker. Loki is fixed in place, yet Baldr is not restored; the wound remains until doom. The gods bind the destroyer and also bury their own unhealed story until it breaks open again.",
                ),
              },
            ),
          ],
        },
      ],
    },
    {
      id: "norse-ragnarok-era",
      order: 6,
      title: { "zh-Hant": "諸神黃昏與新生", en: "Ragnarok and Renewal" },
      summary: {
        "zh-Hant": "束縛破裂、最後戰鬥、世界焚毀與火後新生構成末日路線。",
        en: "Broken bonds, final battles, world-burning, and renewal form the doom route.",
      },
      subEras: [
        {
          id: "norse-ragnarok-route",
          order: 1,
          title: { "zh-Hant": "末日與新生", en: "Doom and Renewal" },
          summary: {
            "zh-Hant":
              "末日由長冬、天象崩壞、束縛斷裂、最後戰鬥與新生連成一條路線。",
            en: "Doom moves through winter, broken sky, broken bonds, final battles, and renewal.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene(
              "norse-ragnarok",
              ["awful-winter", "sun-moon-wolves", "fetters-and-sea"],
              {
                title: {
                  "zh-Hant": "長冬、日月被吞與束縛斷裂",
                  en: "Winter, Swallowed Lights, and Broken Bonds",
                },
                narrative: sceneNarrative(
                  "諸神黃昏不是直接從戰場開始。長冬先破壞季節與人間親族秩序，世界在刀兵與寒冷中失常；戰士尚未集結，末日已經進入日常。",
                  "接着天象被攻擊。斯庫爾吞下蘇爾，哈提奪去瑪尼，星辰消失；天空不再只是背景，而成為會被狼吞奪的秩序。大地震動後，束縛與海潮也開始破裂。",
                  "芬里爾脫身，耶夢加得攪動海水，納格爾法被放出。前面故事中的延宕全部回到現場：受縛不再有效，巨蛇不再只是預演，末日從徵兆變成真正集結。宇宙秩序先在天、地、海和束縛上同時鬆開；這不是一條新災難，而是所有被延後的危機同時越過臨界點，把世界從日常推入不可回頭的戰時，再沒有退路可走了。",
                  "Ragnarok does not begin directly on the battlefield. Long winter first breaks seasons and kinship order; doom enters ordinary life before armies gather.",
                  "Then the sky is attacked. Skoll swallows Sol, Hati takes Mani, and the stars vanish. After the earth shakes, bonds and sea begin to break.",
                  "Fenrir gets free, Jormungandr drives the sea over land, and Naglfar is loosed. Earlier delays return as active crisis. This is not a new disaster, but every deferred danger crossing the threshold into irreversible war.",
                ),
              },
            ),
            scene(
              "norse-ragnarok",
              ["muspell-to-vigrid", "gjallarhorn-council"],
              {
                title: {
                  "zh-Hant": "軍勢赴維格利德，號角召集諸神",
                  en: "Hosts Move to Vigrid, the Horn Calls the Gods",
                },
                narrative: sceneNarrative(
                  "危機有了通路後，敵對軍勢開始向維格利德聚集。蘇爾特爾與穆斯貝爾之子從火界而來，洛基也回到末日場景，彩虹橋在重壓下斷裂。",
                  "海姆達爾吹響加拉爾號角，神明集會，奧丁前往密米爾之泉求取最後智慧，世界樹震動。看守、王權、智慧與宇宙支架同被召入末日。",
                  "這一幕把分散的災兆收束成最後戰場。橋斷不是單獨事故，而是神族通道被戰爭壓垮；號角不只是通知，而是宣告前面累積的誓言、束縛、武器和預言都要在此交還。維格利德成為多條故事線共同抵達的地方；從此，道路變成戰線，守門的聲音變成最後召集，諸神只能走向命運，再無旁路。",
                  "Once the crisis has routes, hostile forces move toward Vigrid. Surtr and the sons of Muspell come from fire, Loki returns to the doom scene, and Bifrost breaks under pressure.",
                  "Heimdall blows Gjallarhorn, the gods assemble, Odin seeks Mimir’s well for final counsel, and Yggdrasil trembles.",
                  "Scattered signs become a battlefield. The broken bridge shows divine passage collapsing under war, while the horn calls every earlier oath, binding, weapon, and prophecy back into the present. Roads become battle-lines, and the gods move toward fate.",
                ),
              },
            ),
            scene("norse-ragnarok", ["odin-fenrir-vidar"], {
              title: {
                "zh-Hant": "奧丁被芬里爾吞下，維達爾復仇",
                en: "Fenrir Swallows Odin, Vidar Avenges Him",
              },
              narrative: sceneNarrative(
                "奧丁與芬里爾的對決把受縛故事的預言帶到終點。早前神族把狼留到諸神黃昏，現在期限抵達；神王面對的不是普通敵人，而是自己一直試圖延後的命運。",
                "芬里爾吞下奧丁，使預言成為戰場事實。維達爾隨即踏住狼顎，撕裂或殺死芬里爾，為父親復仇；父子、狼與末日之間的關係在一場殘酷反擊中完成。",
                "這段不是單純勝負交換。神族失去最高王者，即使復仇成功，也不能把舊秩序帶回來。芬里爾死亡後，受縛故事的壓力解除，世界卻已經無法回到受縛以前的穩定。預言完成的一刻，也是神王時代被吞沒的一刻；維達爾的復仇保存了父子名分，卻救不回被吞下的統治中心與整個舊世界。",
                "Odin’s battle with Fenrir brings the prophecy of the binding story to its limit. The wolf the gods delayed now reaches the god-king.",
                "Fenrir swallows Odin. Vidar immediately avenges his father, setting his foot against the wolf’s jaw and killing him.",
                "This is not a simple exchange of victory and defeat. The highest god is gone, and revenge cannot restore the old order. Fenrir’s pressure ends only after the world has already broken. Vidar preserves the father-son duty, not the old world.",
              ),
            }),
            scene(
              "norse-ragnarok",
              ["thor-serpent-duel", "freyr-tyr-loki-fall"],
              {
                title: {
                  "zh-Hant": "托爾、弗雷、提爾與海姆達爾的終戰",
                  en: "Thor, Freyr, Tyr, and Heimdall in the Last Battle",
                },
                narrative: sceneNarrative(
                  "最後戰場把多條早前伏線一同拉緊。托爾面對耶夢加得，這不再是希米爾船上被割斷釣線的預演；巨蛇與雷神終於在末日完成對決。",
                  "托爾殺死耶夢加得，走出九步後被毒所殺。弗雷面對蘇爾特爾，沒有自己的劍；提爾與加姆互殺，洛基與海姆達爾也同歸於盡。每一場戰鬥都帶着之前故事留下的缺口。",
                  "這一幕讓神族勝利與死亡同時發生。敵人倒下，神明也倒下；武器失去、守門職責、被束縛者回返與未完成對峙，都在末日裏結算。戰場因此不是一場單一大戰，而是多條故事債務同時到期。每個對手都像前文留下的名字再次現身；這也是前面每次失物、試煉和束縛留下的最後共同回聲之一。",
                  "The final field tightens several earlier threads at once. Thor faces Jormungandr not as a fishing rehearsal but as the completed doom duel.",
                  "Thor kills the serpent, then dies after nine steps from its venom. Freyr faces Surtr without his sword; Tyr and Garm kill each other; Loki and Heimdall also fall together.",
                  "Victory and death arrive together. Earlier losses, duties, bindings, and unfinished confrontations all come due on the same field. The battle is the last echo of every theft, test, and bond.",
                ),
              },
            ),
            scene("norse-ragnarok", ["surtr-fire", "renewed-gods"], {
              title: {
                "zh-Hant": "蘇爾特爾之火後，眾神再聚",
                en: "After Surtr’s Fire, the Gods Gather Again",
              },
              narrative: sceneNarrative(
                "最後，蘇爾特爾的火焚燒世界。這不是普通戰鬥尾聲，而是舊秩序的全面崩潰：神明倒下，橋已斷裂，世界樹震動，火把戰場擴展成宇宙毀滅。",
                "火熄後，大地從海中升起。維達爾與瓦利仍在，巴德爾和霍德爾從赫爾歸來；托爾的兒子也把妙爾尼爾帶到伊達平原。",
                "這不是把舊日神族原封不動地還回來。失去仍在，但毀壞之後已有新的聚會；在伊達平原，他們談起舊日的事與古老的符文。諸神黃昏的終點因此先是讓神明在另一個世界重新相見，並從記憶裏辨認自己失去過甚麼。",
                "At last Surtr’s fire burns the world. This is not a mere closing battle but the collapse of the old order: gods fall, the bridge is broken, the tree trembles, and fire reaches the cosmos.",
                "When the fire subsides, the earth rises from the sea. Vidar and Vali remain; Baldr and Hodr return from Hel, and Thor’s sons bring Mjolnir to Ida-Plain.",
                "This is not the old divine order returned unchanged. Loss remains, yet a new gathering begins. On Ida-Plain they speak of old events and old runes; the first ending of Ragnarok is the gods meeting again in another world and remembering what the old one cost.",
              ),
            }),
            scene("norse-ragnarok", ["lif-and-lifthrasir-repopulate"], {
              title: {
                "zh-Hant": "兩人讓人類回到大地",
                en: "Two People Return Humankind to Earth",
              },
              narrative: sceneNarrative(
                "新世界仍需要人類。烈火穿過世界時，利夫與利夫特拉西爾藏在霍德密米爾之林，沒有隨舊世界一同消失。",
                "他們以晨露為食，等到火後的大地重新可居；從這兩人開始，後代再度使人類遍及世界。",
                "原典沒有把這段寫成一場英雄式逃亡；它只留下躲藏、等待和晨露。兩人沒有靠勝利取得新世界，也沒有立刻建立新的王權；他們先活下來，才有後代。他們安靜的延續已經足夠讓大地重新有人居住，也慢慢有了新的日常生活。正因這份樸素，重建人世的重量才落在兩個倖存者身上。因此新生並非只屬於回來的神，神話讓世界在失去後重新有了人的未來。",
                "The renewed world still needs human beings. As fire passes over the world, Lif and Lifthrasir hide in Hoddmimir’s Wood rather than vanish with the old world.",
                "They live on morning dew until the earth can be lived on again; from these two, later descendants people the world.",
                "The source does not turn this into a heroic escape. It gives only a hiding place, waiting, and morning dew. They do not win the new world in battle or immediately found a new court; they endure first, then have descendants. Their quiet continuance is enough for the earth to be inhabited again by future families. That plainness makes the renewal of human life rest on two survivors. Renewal therefore does not belong only to returning gods: the myth gives the world people, descendants, and a future after loss.",
              ),
            }),
          ],
        },
      ],
    },
    {
      id: "norse-heroic-branch-era",
      order: 7,
      title: { "zh-Hant": "英雄詩與沃爾松族", en: "Heroic Lays and Volsungs" },
      summary: {
        "zh-Hant": "西格爾德、法夫尼爾、受詛黃金、婚姻欺瞞與復仇形成英雄支線。",
        en: "Sigurd, Fafnir, cursed gold, marriage deception, and vengeance form the heroic cycle.",
      },
      subEras: [
        {
          id: "norse-volsungs-route",
          order: 1,
          title: {
            "zh-Hant": "西格爾德與沃爾松族",
            en: "Sigurd and the Volsungs",
          },
          summary: {
            "zh-Hant":
              "沃爾松族支線把神明干預、受詛黃金、龍殺、婚姻欺瞞和復仇連在一起。",
            en: "The Volsung story links divine intervention, cursed gold, dragon-slaying, marriage deception, and revenge.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          segments: [
            scene("norse-volsungs", ["branstock-sword", "sigurd-inheritance"], {
              title: {
                "zh-Hant": "樹幹之劍傳到西格爾德",
                en: "The Branstock Sword Reaches Sigurd",
              },
              narrative: sceneNarrative(
                "英雄支線從沃爾松族廳堂中的陌生來客開始。奧丁把劍插入樹幹，只有西格蒙德能拔出；神器不是單純寶物，而是把神明干預直接放進家族命運。",
                "劍後來破碎，經過希約蒂絲保存，成為西格爾德繼承的材料。故事把父系榮耀、母親保存和未來英雄連在一起：西格爾德尚未出場完成偉業，命運已經以碎片形式等着他。",
                "這段開場使沃爾松族故事不只是人間冒險。奧丁的行動、家族暴力與武器傳承同時建立，英雄的身份從一開始就被神明贈物與破碎遺產塑造。後面的龍殺和婚姻悲劇，都會握着這把被重鍛的舊劍前進；劍身未合之前，家族的榮耀與災禍已經一併交到下一代手中，並等待日後重鍛。",
                "The heroic cycle begins with a stranger in the Volsung hall. Odin drives a sword into the Branstock tree, and only Sigmund can draw it.",
                "The sword later breaks, is preserved through Hjordis, and becomes the material Sigurd will inherit. Fatherly glory, maternal preservation, and future heroism are joined before Sigurd’s deeds begin.",
                "The Volsung story is therefore not only a human adventure. Divine intervention, family violence, and weapon inheritance shape the hero from the start. Before the blade is whole, glory and disaster have already passed to the next generation.",
              ),
            }),
            scene(
              "norse-volsungs",
              [
                "regin-fosters-sigurd",
                "andvari-loses-hoard",
                "hreidmar-house-falls",
              ],
              {
                title: {
                  "zh-Hant": "雷金、格拉姆與受詛黃金",
                  en: "Regin, Gram, and the Cursed Gold",
                },
                narrative: sceneNarrative(
                  "西格爾德長大後，雷金成為他的養育者和推動者。格拉尼被選出，格拉姆被重鍛，英雄的馬、劍與訓練逐步齊備；但雷金的指引並不純粹，背後連着自己家族的黃金舊事。",
                  "安德瓦利被迫交出寶藏並咒詛黃金；赫雷德瑪爾家得到賠償後，詛咒沒有停在物件上，而是進入親族。法夫尼爾殺父奪金，化身為守財的龍，雷金也帶着仇恨與慾望把西格爾德推向牠。",
                  "這段讓屠龍故事有了真正重量。西格爾德不是隨便去殺一條怪物，而是被捲進一份由神明賠償、侏儒詛咒、父子互害和兄弟貪念構成的黃金災禍。黃金在到達英雄手中以前，已經吞掉一個家庭；養育、訓練與誘惑在同一人身上清楚重疊。",
                  "As Sigurd grows, Regin becomes his fosterer and driver. Grani is chosen, Gram is reforged, and the hero’s horse, sword, and training come together.",
                  "Behind Regin’s guidance lies the old hoard. Andvari is forced to give up the treasure and curses it; Hreidmar’s house receives the gold, and Fafnir kills his father for it.",
                  "The dragon-slaying gains weight. Sigurd is not hunting a random monster; he is drawn into a disaster made from compensation, curse, kin-killing, and greed. Fosterage, training, and temptation overlap in Regin.",
                ),
              },
            ),
            scene(
              "norse-volsungs",
              [
                "regin-sends-sigurd",
                "fafnir-slaying",
                "regin-slaying-and-gold",
              ],
              {
                title: {
                  "zh-Hant": "西格爾德殺死法夫尼爾並奪得黃金",
                  en: "Sigurd Slays Fafnir and Takes the Gold",
                },
                narrative: sceneNarrative(
                  "雷金把西格爾德推向法夫尼爾，屠龍行動於是開始。西格爾德在道路上埋伏，以格拉姆刺中龍；英雄名聲在這一擊中成形，但龍臨死前也指出黃金帶來的危險。",
                  "法夫尼爾死後，西格爾德烤龍心，聽懂鳥語，得知雷金準備背叛他。於是他殺死雷金，把黃金、格拉姆與龍血知識一起帶走；原本被雷金安排的復仇，反過來吞掉安排者。",
                  "這段場景把西格爾德推到英雄高峰，也把詛咒推到他身上。龍死了，危險沒有消失，只是換了主人；從這一刻起，黃金會跟着英雄進入婚姻、王庭與死亡。屠龍勝利越耀眼，後面的關係裂縫越難避開；勝利由此不再清白，而成為下一段悲劇攜帶的刺眼光芒與陰影。",
                  "Regin sends Sigurd against Fafnir, and the dragon-slaying begins. Sigurd ambushes the dragon and strikes with Gram, winning heroic fame while Fafnir warns of the gold’s danger.",
                  "Afterward, Sigurd roasts the dragon’s heart, understands birds, and learns that Regin plans betrayal. He kills Regin and takes the gold.",
                  "The scene raises Sigurd to heroic height while moving the curse onto him. The dragon dies, but the danger changes hands and travels toward court, marriage, and death. The victory shines, but no longer innocently.",
                ),
              },
            ),
            scene(
              "norse-volsungs",
              ["brynhild-and-giukings", "gunnar-wooing-deception"],
              {
                title: {
                  "zh-Hant": "布倫希爾德、久基王庭與求婚欺瞞",
                  en: "Brynhild, the Giukings, and the Wooing Deception",
                },
                narrative: sceneNarrative(
                  "屠龍之後，西格爾德進入另一種危險：王庭與婚姻。布倫希爾德、古德倫、貢納爾和久基家族把英雄帶進盟約、愛慕和政治安排之中。",
                  "求婚欺瞞把危機推到核心。西格爾德以變換身份的方式幫貢納爾通過火焰，取得布倫希爾德；表面上婚約完成，實際上身份、記憶與承諾都已被扭曲。",
                  "這段不是屠龍後的平靜獎賞，而是英雄悲劇的第二個引爆點。格拉姆與黃金帶來的名聲進入王庭後，變成婚姻欺瞞與榮譽衝突；接下來的死亡不是突然發生，而是由這份錯位逐步推出。英雄越被接入王族，真相越難保持完整；王庭給英雄新的位置，也把他的名字放進更難脫身的謊言、義務和榮譽壓力之中。",
                  "After the dragon-slaying, Sigurd enters another danger: court and marriage. Brynhild, Gudrun, Gunnar, and the Giukings draw the hero into alliance, desire, and political arrangement.",
                  "The wooing deception becomes the core. Sigurd changes identity to help Gunnar pass the flame and win Brynhild, so marriage is outwardly completed while identity and promise are distorted.",
                  "This is not a calm reward after the dragon. Heroic fame enters court and becomes deception, honour conflict, and the seed of death. The court gives the hero a new place while trapping his name inside lie, duty, and honour.",
                ),
              },
            ),
            scene("norse-volsungs", ["betrayal-and-death", "atli-aftershock"], {
              title: {
                "zh-Hant": "西格爾德之死與阿特利餘波",
                en: "Sigurd’s Death and Atli’s Aftershock",
              },
              narrative: sceneNarrative(
                "欺瞞揭開後，王庭內部的誓言與婚姻開始崩壞。布倫希爾德的憤怒、古德倫的婚姻位置、貢納爾與兄弟們的決定，把西格爾德推向背叛。",
                "西格爾德被殺，英雄支線從勝利轉入哀悼和報復。屠龍者沒有死在龍口中，而是死在人的關係裏；這使沃爾松族故事的悲劇重心從怪物轉到盟友、親族與婚姻。",
                "阿特利的餘波把受詛黃金和家族仇恨繼續推遠。西格爾德之死不是支線終點，而是下一輪暴力的入口；黃金所帶來的災禍已經越過英雄本人，進入更廣的親族毀滅。沃爾松族支線因此以餘波收束，而不是以英雄勝利收束；英雄故事最後留下的不是安定王朝，而是被詛咒推動的餘波與親族廢墟之中。",
                "When the deception is exposed, oaths and marriages inside the court collapse. Brynhild’s anger, Gudrun’s position, and the choices of Gunnar and his brothers push Sigurd toward betrayal.",
                "Sigurd is killed. The dragon-slayer does not die in a monster’s mouth but within human relationships, shifting the tragedy from creature to kinship and alliance.",
                "Atli’s aftershock carries cursed gold and family hatred onward. Sigurd’s death is not the end of the story but the opening of another wave of violence. What remains is not a settled dynasty, but aftermath and kinship ruin.",
              ),
            }),
          ],
        },
        {
          id: "norse-volund-route",
          order: 2,
          title: { "zh-Hant": "沃倫德之歌", en: "The Lay of Volund" },
          summary: {
            "zh-Hant":
              "這首英雄詩以離散、囚禁、強迫與飛離收束，不與沃爾松族家系合併。",
            en: "This heroic lay ends in separation, captivity, coercion, and flight; it is not merged into Volsung genealogy.",
          },
          chronologyMode: "parallel",
          coverageStatus: "complete",
          segments: [
            scene(
              "norse-volund",
              [
                "brothers-and-swan-maidens",
                "women-depart-brothers-divide",
                "nidud-seizes-smith",
              ],
              {
                title: {
                  "zh-Hant": "離散後的扣押",
                  en: "Separation and Seizure",
                },
                narrative: sceneNarrative(
                  "三兄弟與天鵝少女相伴七個冬天；到第八個冬天，女子離開，這段相伴便告結束。",
                  "埃吉爾和斯拉格菲德出發尋找伴侶，沃倫德卻留在烏爾夫達爾。尼杜德發現他獨居，隨即把他捉走，帶到自己的土地。",
                  "這不是一段可略去的浪漫前史。詩把離散、尋找和留下排成一條因果線：當同伴不在場，擅長鍛造的沃倫德就更容易被王權攫取。原詩沒有替尼杜德補上一個必要的正當理由，也沒有說另外兩兄弟何時回來；時間線只把可見的失去和扣押相連，讓後來的囚禁有可見的前因，也不把留守者的遭遇寫成他自己應得的選擇後果。",
                  "Three brothers and swan-maidens live together for seven winters; in the eighth, the women leave and that companionship ends.",
                  "Egil and Slagfith leave to seek their partners, while Volund remains at Ulfdal. Nithud finds him alone, seizes him, and takes him to his own land.",
                  "This is not disposable romantic background. The lay makes separation, searching, and staying a causal chain: once his companions are absent, the skilled Volund becomes more vulnerable to royal seizure. The poem supplies neither a necessary justification for Nithud nor a return for the other brothers; the timeline joins only the visible loss and seizure, so the later confinement has a visible cause without making the person who stays responsible for the harm done to him.",
                ),
              },
            ),
            scene(
              "norse-volund",
              [
                "sword-ring-and-captivity",
                "island-confinement",
                "bodvild-brings-ring",
              ],
              {
                title: {
                  "zh-Hant": "島上工坊的囚禁",
                  en: "Captivity in the Island Workshop",
                },
                narrative: sceneNarrative(
                  "尼杜德奪走沃倫德的劍與戒指，又使他失去行動能力，將他安置在湖中島上做工。",
                  "戒指後來斷裂，尼杜德的女兒博德維爾德帶着它到島上，請沃倫德修補。她的來訪源自一件物品，也發生在囚禁關係沒有解除的空間裏。",
                  "這一段把王權、物件與脆弱處境壓縮在同一座工坊。沃倫德的技藝仍被索取，卻不能改變他遭拘禁的事實；博德維爾德也不是自由進入一段相遇，而是沿着父親持有的戒指走進一個不對等局面。修戒看似是日常請求，卻不能沖淡島上空間由扣押和強制勞動構成的條件；那件被帶來的物品沒有替任何人帶來真正安全。",
                  "Nithud takes Volund’s sword and ring, deprives him of freedom of movement, and sets him to work on an island in a lake.",
                  "When the ring breaks, Nithud’s daughter Bodvild brings it to the island for repair. Her visit begins with an object and occurs inside a space where the captivity has not ended.",
                  "Here royal power, objects, and vulnerability are compressed into one workshop. Volund’s skill is still demanded, yet it does not undo his confinement; Bodvild also enters not a free encounter but an unequal situation shaped by her father’s possession of the ring. Repairing a ring may look like an ordinary request, but it cannot soften the conditions of seizure and forced labour that make the island space possible; the object brought there does not make anyone safe.",
                ),
              },
            ),
            scene("norse-volund", ["revenge-on-sons", "coercion-and-flight"], {
              title: {
                "zh-Hant": "報復留下的傷害",
                en: "Harm Left by Revenge",
              },
              narrative: sceneNarrative(
                "沃倫德以尼杜德兩名未具名的兒子為報復目標，殺死他們，並把殘酷的證物送入王室。",
                "他也在博德維爾德來訪時強迫她，之後做成飛行裝置離開。尼杜德追問時，沃倫德從空中說明兒子已死，博德維爾德則向父親哭訴自己受到的傷害。",
                "飛離並沒有把這首詩收成一場乾淨的勝利。沃倫德逃離囚禁，卻把殺害與強迫留下給沒有掌握權力的孩子和女兒；尼杜德失去兒子，也仍須面對由自己扣押所展開的連鎖傷害。詩在沃倫德離開後並未交代補償或修復，因此不能替任何一方補上和解。時間線保留逃脫與後果在同一收束裏，避免把報復誤寫成正義已經完成。",
                "Volund takes Nithud’s two unnamed sons as targets of revenge, kills them, and sends cruel tokens into the royal household.",
                "When Bodvild visits, he coerces her; he then makes a flying device and leaves. From the air he tells Nithud that the sons are dead, while Bodvild weeps to her father about the harm done to her.",
                "Flight does not turn the lay into a clean victory. Volund escapes confinement but leaves killing and coercion with children and a daughter who did not hold the power; Nithud loses his sons and still faces the chain of harm that began with his seizure. The poem gives no later compensation or repair, so reconciliation cannot be supplied for either side. The timeline keeps escape and consequence together, rather than mistaking revenge for completed justice.",
              ),
            }),
          ],
        },
        {
          id: "norse-helgi-hundingsbane-route",
          order: 3,
          title: {
            "zh-Hant": "赫爾吉・洪丁斯巴尼第一歌",
            en: "The First Lay of Helgi Hundingsbane",
          },
          summary: {
            "zh-Hant":
              "命運、血仇、被安排的婚約與戰後結果形成一條平行英雄詩支線。",
            en: "Fate, blood-debt, an imposed pledge, and battle's outcome form a parallel heroic-lay branch.",
          },
          chronologyMode: "parallel",
          coverageStatus: "complete",
          segments: [
            scene(
              "norse-helgi-hundingsbane",
              ["helgi-birth-under-norns", "sigmund-names-helgi"],
              {
                title: { "zh-Hant": "出生與命名", en: "Birth and Naming" },
                narrative: sceneNarrative(
                  "博爾希爾德生下赫爾吉；諾恩來到嬰兒身旁，西格蒙德再替兒子命名。",
                  "命運語言與父系命名並列，為年輕英雄標出將走向名聲與衝突的一條路。",
                  "這首歌不提供可與其他赫爾吉或沃爾松材料無縫拼合的固定年表；時間線只保留詩中可見的出生、塑造與命名。",
                  "Borghild bears Helgi; the Norns come to the infant, and Sigmund names his son.",
                  "Fate-language stands beside paternal naming, marking a young hero's path toward renown and conflict.",
                  "The lay does not provide a fixed calendar that can be seamlessly joined to other Helgi or Volsung material; the timeline keeps only its visible birth, shaping, and naming.",
                ),
              },
            ),
            scene(
              "norse-helgi-hundingsbane",
              ["helgi-kills-hunding", "sons-demand-and-fall"],
              {
                title: { "zh-Hant": "血債擴大", en: "Blood-Debt Widens" },
                narrative: sceneNarrative(
                  "十五歲的赫爾吉殺死洪丁；洪丁諸子立刻要求報償，隨後也在衝突中倒下。",
                  "第一次殺戮沒有停留成孤立的英雄戰功，而是被推入另一輪索償。",
                  "血仇是敘事的推進力：一方的死亡成為另一方提出要求的理由，後來的勝利不會令暴力看似自然終止。",
                  "At fifteen Helgi kills Hunding; Hunding's sons immediately demand payment and later fall in conflict.",
                  "The first killing does not remain an isolated heroic feat, but is pushed into another demand for requital.",
                  "Blood-debt drives the story: one death gives another side reason to press a claim, and later victory does not make violence appear naturally settled.",
                ),
              },
            ),
            scene(
              "norse-helgi-hundingsbane",
              ["sigrun-names-the-pledge", "fleet-sets-out"],
              {
                title: {
                  "zh-Hant": "西格倫的拒絕與航程",
                  en: "Sigrun's Refusal and the Voyage",
                },
                narrative: sceneNarrative(
                  "西格倫說父親已把她許給霍特布羅德，而她不願前往；赫爾吉答應迎戰，隨辛菲奧特利率船隊出發。",
                  "海上遇險時，詩又把西格倫放在船隊旁，讓她的意願和行動都留在戰事之前。",
                  "婚約安排不是她的同意；時間線保留她先表明意願、再施以援助的次序，不讓詩末結果倒灌成她從未說過拒絕。",
                  "Sigrun says her father has pledged her to Hothbrodd and that she does not want to go; Helgi sets out with Sinfjotli and a fleet.",
                  "When danger rises at sea, the lay places Sigrun beside the ships, keeping both her will and her action visible before battle.",
                  "A marriage pledge is not her consent; the timeline preserves refusal before aid rather than allowing the ending to erase what she said.",
                ),
              },
            ),
            scene(
              "norse-helgi-hundingsbane",
              ["hothbrodd-musters", "frekastein-victory"],
              {
                title: { "zh-Hant": "弗雷卡斯坦戰後", en: "After Frekastein" },
                narrative: sceneNarrative(
                  "霍特布羅德召集盟友；在弗雷卡斯坦，西格倫辨認赫爾吉一方獲勝，並確認霍特布羅德倒下。",
                  "詩以婚配結果收束，但沒有倒回去說西格倫早前的拒絕從未存在。",
                  "弗雷卡斯坦與洛加菲厄爾都不作現代定位；這條時間線保留勝負、婚約和意願之間未被化解的張力。",
                  "Hothbrodd musters allies; at Frekastein, Sigrun recognizes Helgi's side as victorious and confirms Hothbrodd's fall.",
                  "The lay closes with a bride-outcome, but does not travel back to say Sigrun's earlier refusal never existed.",
                  "Frekastein and Logafjoll receive no modern location; this route keeps the unresolved tension among victory, pledge, and will.",
                ),
              },
            ),
          ],
        },
        {
          id: "norse-helgi-hjorvarthsson-route",
          order: 4,
          title: { "zh-Hant": "赫爾吉・希約爾瓦德松之歌", en: "The Lay of Helgi Hjorvarthsson" },
          summary: {
            "zh-Hant": "無名的赫爾吉獲得名字、追討母族舊仇，最後在斯瓦娃與赫辛的誓言之間走向弗雷卡斯坦。",
            en: "The unnamed Helgi receives a name, answers his mother’s old wrong, and reaches Frekastein between the vows of Svava and Hethin.",
          },
          chronologyMode: "parallel",
          coverageStatus: "complete",
          segments: hjorvarthssonTimelineSegments,
        },
        {
          id: "norse-atlamol-route",
          order: 5,
          title: { "zh-Hant": "格陵蘭阿特利之歌", en: "The Greenland Ballad of Atli" },
          summary: {
            "zh-Hant": "這條支線保留較長詩篇的家庭警告、海程與赫尼夫隆結局；它與沃爾松故事同樣指向阿特利，卻不把兩種敘法合成一條唯一後續。",
            en: "This route preserves the longer poem’s household warnings, sea journey, and Hniflung ending. It points to Atli alongside the Volsung story without merging both tellings into a single sequel.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "按《格陵蘭阿特利之歌》的敘事順序安排；同一死亡與復仇的其他來源仍保有各自版本。",
            en: "Ordered by the Atlamol’s narrative sequence; other sources for the deaths and revenge retain their own versions.",
          },
          segments: allSteps("norse-atlamol", ["warning-falsified", "kostbera-reads-danger", "glaumvor-dreams", "sons-at-fjord", "vingi-reveals-trap", "gudrun-takes-sword", "brothers-last-sounds", "death-feast-for-atli", "hniflung-joins-revenge"]),
        },
        {
          id: "norse-hervor-awakening-route",
          order: 6,
          title: { "zh-Hant": "赫爾沃爾喚醒安甘蒂爾", en: "Hervor Wakes Angantyr" },
          summary: {
            "zh-Hant": "一把劍經過家族、決鬥與墓丘，最後被安甘蒂爾的女兒赫爾沃爾帶離薩姆瑟島。",
            en: "A sword passes through family, duel, and burial mound before Angantyr’s daughter Hervor carries it away from Samsey.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "依《赫爾沃爾與海德雷克王傳奇》R 本第1至4章的順序安排；不把其他版本或後來的海德雷克故事當作必然後續。",
            en: "Ordered by chapters 1–4 of the R text of Hervarar saga; other redactions and Heidrek’s later story are not treated as a necessary sequel.",
          },
          segments: hervorAwakeningTimelineSegments,
        },
        {
          id: "norse-helgakvitha-hundingsbana-ii-route",
          order: 7,
          title: { "zh-Hant": "赫爾吉第二歌：達格與墓丘", en: "Helgi II: Dag and the Mound" },
          summary: {
            "zh-Hant": "達格為父親復仇，西格倫在墓丘與赫爾吉短暫相見；這條線跟隨拼接文本的死亡與夜訪段落，不把它當成唯一的赫爾吉生平。",
            en: "Dag avenges his father, and Sigrun briefly meets Helgi in the mound. This route follows the composite text’s death and night-visit passages without making them Helgi’s single biography.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "依貝洛斯英譯《赫爾吉・洪丁斯班尼第二歌》的散文第 VI 節、28–37 節、墓丘段與 39–50 節安排；文本本身由非連續片段拼接，早段求婚與戰事另留在第一歌的路線。",
            en: "Ordered by prose section VI, stanzas 28–37, the mound passage, and stanzas 39–50 in Bellows’s Helgakvitha Hundingsbana II. The text itself is composite; its earlier courtship and battles remain in the First Lay route.",
          },
          segments: [
            scene("norse-helgakvitha-hundingsbana-ii", ["dag-seeks-revenge", "odin-gives-spear"], {
              title: { "zh-Hant": "達格向奧丁求一條復仇的路", en: "Dag Asks Odin for a Path to Vengeance" },
              narrative: sceneNarrative(
                "霍格尼死後，達格為父親向奧丁獻祭。散文緊接著說奧丁把一支矛交到他手裡。",
                "文本沒有解釋奧丁為何給矛，也沒有替達格把復仇變成無可質疑的正義；它只把這兩個行動相連。",
                "因此這一幕讀的是一份武器如何進入家族衝突，不是奧丁意圖的完整說明。",
                "After Hogni dies, Dag sacrifices to Odin for his father. The prose then says that Odin puts a spear in his hand.",
                "The text does not explain why Odin gives it, nor does it turn Dag’s vengeance into unquestionable justice; it only joins those acts together.",
                "This scene therefore follows how a weapon enters a family conflict, not a full account of Odin’s intention.",
              ),
            }),
            scene("norse-helgakvitha-hundingsbana-ii", ["dag-kills-helgi", "sigrun-curses-brother"], {
              title: { "zh-Hant": "費約圖爾林德的死訊", en: "News of Death from Fjoturlund" },
              narrative: sceneNarrative(
                "達格在費約圖爾林德用那支矛刺穿赫爾吉，然後把消息帶給西格倫。這個地名沒有可靠的現代位置，卻把一擊留在明確的詩中現場。",
                "西格倫以她和赫爾吉曾立的誓言詛咒兄長。她的話是失去丈夫後的發話，而不是時間線替讀者驗證的法術結果。",
                "同一段落讓兄妹關係與婚姻同時斷裂；它不要求讀者選擇哪一種悲傷較有資格。",
                "At Fjoturlund, Dag pierces Helgi with the spear, then takes the news to Sigrun. The place has no secure modern location, yet it keeps the blow in a named scene of the poem.",
                "Sigrun curses her brother by the oaths she and Helgi had made. Her words are speech after a husband’s death, not a spell the timeline verifies for the reader.",
                "The same passage fractures sibling bond and marriage at once; it does not ask us to decide which grief deserves more standing.",
              ),
            }),
            scene("norse-helgakvitha-hundingsbana-ii", ["dag-offers-rings", "helgi-received-in-valhalla"], {
              title: { "zh-Hant": "賠償補不回人，散文轉到瓦爾哈拉", en: "Compensation Cannot Restore Him; the Prose Turns to Valhalla" },
              narrative: sceneNarrative(
                "達格提出紅環與土地，卻沒有讓西格倫重新快樂。這是一份承認傷害的賠償，不是兩人和解的證明。",
                "接在詩段之間的散文說赫爾吉到了瓦爾哈拉，奧丁要他同掌一切。這個安排屬於本版本的插段，不替每個死者寫成通行的去處。",
                "把兩幕放在一起，能看見文本如何先留住西格倫的失去，再切換到赫爾吉被接納的敘述。",
                "Dag offers red rings and land, yet Sigrun does not become happy again. It is compensation that acknowledges harm, not proof that the two are reconciled.",
                "Prose inserted between the poem’s fragments says Helgi reaches Valhalla and Odin asks him to rule with him. That placement belongs to this version’s insertion, not a universal destination for every dead person.",
                "Placed together, the scenes show how the text first holds on to Sigrun’s loss, then switches to an account of Helgi being received.",
              ),
            }),
            scene("norse-helgakvitha-hundingsbana-ii", ["maid-sees-riders", "sigrun-at-barrow"], {
              title: { "zh-Hant": "墓丘開啟的一夜", en: "One Night with the Mound Open" },
              narrative: sceneNarrative(
                "侍女夜裡看見赫爾吉與隨從騎回墓丘，西格倫便進去替他鋪床。赫爾吉說自己必須在天亮前離開。",
                "下一夜他沒有再回來。結尾散文再說西格倫早逝，並以保留語氣提到兩人後世重生的信念。",
                "這條路停在未再歸來之處：墓丘夜訪是詩中的短暫相見，不是一條可以延伸成固定復活規則的門。",
                "A maiden sees Helgi and his followers ride back to the mound at night, and Sigrun enters to make him a bed. Helgi says he must leave before dawn.",
                "He does not return on the next night. The closing prose then says Sigrun dies early and cautiously mentions a belief that they were reborn later.",
                "This route stops where he does not return: the mound visit is the poem’s brief meeting, not a door that can be extended into a fixed rule of resurrection.",
              ),
            }),
          ],
        },
        {
          id: "norse-fra-dautha-sinfjotla-route",
          order: 8,
          title: { "zh-Hant": "辛菲奧特利之死", en: "Of Sinfjotli’s Death" },
          summary: {
            "zh-Hant": "毒酒、父子與一艘消失在峽灣的船，把赫爾吉材料接到西格爾德故事之前。",
            en: "Poisoned drink, father and son, and a boat vanishing on a fjord place this prose between Helgi material and the Sigurth story.",
          },
          chronologyMode: "sequence",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "依〈辛菲奧特利之死〉散文的行動順序；未具名兄長、女子和船夫不會被補成固定角色，船夫也不等同奧丁。",
            en: "Ordered by the prose actions in Fra Dautha Sinfjotla; its unnamed brother, woman, and ferryman are not supplied as fixed figures, and the ferryman is not equated with Odin.",
          },
          segments: [
            scene("norse-fra-dautha-sinfjotla", ["kin-and-blank", "brother-killed", "atonement"], {
              title: { "zh-Hant": "一個留白的名字，引出放逐與賠償", en: "A Blank Name Brings Exile and Atonement" },
              narrative: sceneNarrative(
                "博爾吉爾德的兄長沒有名字。散文只說他和辛菲奧特利同時追求一名女子，辛菲奧特利便殺了他。",
                "博爾吉爾德要繼子離去；西格蒙德提出賠償金，她只得接受。這筆錢收下了，卻沒有讓故事裡的死亡消失。",
                "開頭刻意留下兩個人的名字空白，使責任不會被後來附上的名字帶偏。能確定的是一個人死了、家裡沒有恢復平靜，而賠償只能暫時把衝突留在屋內。",
                "Borghild’s brother has no name. The prose says only that he and Sinfjotli court the same woman, and Sinfjotli kills him.",
                "Borghild tells her stepson to leave; Sigmund offers atonement-money, which she must accept. The money is received, but the death does not disappear.",
                "The opening deliberately leaves two names blank, so later supplied names cannot pull responsibility elsewhere. What is certain is a death, a household left unrestored, and compensation that can only keep the conflict inside for a time.",
              ),
            }),
            scene("norse-fra-dautha-sinfjotla", ["first-horn", "second-horn"], {
              title: { "zh-Hant": "兩角毒酒被西格蒙德飲下", en: "Sigmund Drinks Two Poisoned Horns" },
              narrative: sceneNarrative(
                "在葬宴上，博爾吉爾德端來第一角酒。辛菲奧特利一看便說酒混濁，西格蒙德拿過來喝下。",
                "第二角酒又來，父子之間重演同樣的警告與替飲。散文說西格蒙德能承受毒藥，兒子卻只能抵抗沾在皮膚外的毒。",
                "這兩次看似把危險擋在外面，卻把第三次變得更難解。故事沒有說博爾吉爾德放棄，也沒有讓前兩次的成功保證兒子能平安離開宴席。",
                "At the funeral feast, Borghild brings the first horn. Sinfjotli sees the drink is muddy, and Sigmund takes it and drinks.",
                "The second horn comes, repeating warning and rescue between father and son. The prose says Sigmund can withstand poison, while his sons can resist it only on their outer skin.",
                "The first two acts seem to hold danger outside, yet make the third harder to read. The story does not say Borghild gives up, nor does the earlier success guarantee that the son can leave the feast safely.",
              ),
            }),
            scene("norse-fra-dautha-sinfjotla", ["third-horn", "sinfjotli-dies"], {
              title: { "zh-Hant": "第三角酒，辛菲奧特利死去", en: "The Third Horn; Sinfjotli Dies" },
              narrative: sceneNarrative(
                "第三角酒到來時，辛菲奧特利再一次說酒混濁。西格蒙德沒有替他喝，而是叫他讓酒從鬍鬚流過。",
                "辛菲奧特利喝下後立刻死去。散文沒有說西格蒙德是否知道毒藥仍會害死他，也沒有替父親補上一個可以解釋悲劇的意圖。",
                "父子間最後一句話因此比答案更沉。它把原本反覆成功的保護折斷，讓死亡留在一句沒有被後文說明的命令後面。",
                "When the third horn arrives, Sinfjotli again says the drink is muddy. Sigmund does not drink for him, but tells him to let it run through his beard.",
                "Sinfjotli dies at once after drinking. The prose does not say whether Sigmund knows the poison will still kill him, nor does it supply an intention that could explain the tragedy away.",
                "Their last exchange weighs more heavily than an answer. It breaks a protection that had worked twice before and leaves death after an order the prose does not explain.",
              ),
            }),
            scene("norse-fra-dautha-sinfjotla", ["ferry-disappears"], {
              title: { "zh-Hant": "遺體過峽灣，船夫沒有名字", en: "The Body Crosses the Fjord; the Ferryman Has No Name" },
              narrative: sceneNarrative(
                "西格蒙德把兒子的遺體抱了很遠，來到狹長的峽灣。小船上的男子說可以載遺體過去。",
                "遺體一上船，船便滿了。船夫叫西格蒙德繞內灣走，自己把船划開，隨即消失。",
                "散文不說他是誰，也不讓西格蒙德追上他。這個短段落在無法同渡之處結束，才把編者的敘事轉向西格蒙德與日後的西格爾德。",
                "Sigmund carries his son’s body far until he reaches a narrow, long fjord. A man in a small boat offers to take the body across.",
                "Once the body is aboard, the boat is full. The ferryman tells Sigmund to go around the inner end, pushes off, and disappears.",
                "The prose does not name him or let Sigmund catch up. This short passage ends where the two cannot cross together, only then turning the compiler’s narrative toward Sigmund and the later Sigurth.",
              ),
            }),
          ],
        },
        {
          id: "norse-gripisspo-route",
          order: 9,
          title: { "zh-Hant": "格里皮爾的預言", en: "Gripir’s Prophecy" },
          summary: { "zh-Hant": "西格爾德要求外舅把命運說到底；格里皮爾先說名聲，最後才說出無法避免的傷口。", en: "Sigurth asks his uncle to tell fate through; Gripir begins with fame and ends at the wound that cannot be avoided." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依引言、散文與第 1–53 節；這是一首較晚的預言摘要，所有後續均維持為本詩所說的未來，不取代其他英雄詩。", en: "Ordered by the introduction, prose, and stanzas 1–53; this late prophetic epitome leaves all later material as future speech within this poem and does not replace the other heroic lays." },
          segments: [
            scene("norse-gripisspo", ["arrival", "kinship-named", "first-future"], {
              title: { "zh-Hant": "到廳堂來問一生", en: "Coming to the Hall to Ask a Life" },
              narrative: sceneNarrative("西格爾德獨自到格里皮爾的廳前。蓋提爾通報他，格里皮爾親自出來接見。", "西格爾德稱這位王為母親的兄弟，要求他說出自己的一生。格里皮爾先答以名聲、勇氣與慷慨。", "這個親屬稱呼只在本詩的框架中保留；譯註甚至懷疑格里皮爾是詩人為預言所造的人物。", "Sigurth comes alone to Gripir’s hall. Geitir announces him, and Gripir comes out to receive him.", "Sigurth calls the king his mother’s brother and asks for the shape of his life. Gripir first answers with fame, courage, and generosity.", "The kinship title remains inside this poem’s frame; the translator’s note even suspects that Gripir was made for its prophecy."),
            }),
            scene("norse-gripisspo", ["vengeance-and-dragon", "sleeping-woman"], {
              title: { "zh-Hant": "光榮的預言一項項排開", en: "The Glorious Forecast Unfolds Item by Item" },
              narrative: sceneNarrative("格里皮爾接着說父親與埃利米的仇、洪丁之子、法夫尼爾、雷金和寶藏。", "他又說到火光中的沉睡女子與會傳授智慧的相遇。", "這些是摘要詩預先說出的關卡；實際的戰鬥、相遇與命名，仍要在各自的詩歌裡看見不同版本。", "Gripir goes on to name the wrongs of Sigurth’s father and Eylimi, Hunding’s sons, Fafnir, Regin, and the hoard.", "He also speaks of a sleeping woman in firelight and a meeting that will give wisdom.", "These are waypoints spoken in an epitome; the actual fights, meeting, and names still have differing versions in their own lays."),
            }),
            scene("norse-gripisspo", ["truth-pressed", "forgetting-and-wooing"], {
              title: { "zh-Hant": "格里皮爾欲止，西格爾德逼問", en: "Gripir Tries to Stop; Sigurth Presses Him" },
              narrative: sceneNarrative("格里皮爾說所見已盡，西格爾德卻知道他還有話沒有說。", "在逼問下，預言轉入久基之家、遺忘的飲料、代貢納爾求婚和布琳希爾德的怨恨。", "引言明說這個次序把互相衝突的傳統接在一起。時間線因此只呈現格里皮爾如何說，不把它變成唯一的西古爾德傳記。", "Gripir says his sight is finished, but Sigurth knows he has left words unsaid.", "Pressed further, the prophecy turns to Gjuki’s house, a drink of forgetting, wooing for Gunnar, and Brynhild’s anger.", "The introduction says this order joins conflicting traditions. The timeline therefore shows how Gripir speaks, not a single biography of Sigurth."),
            }),
            scene("norse-gripisspo", ["death-spoken"], {
              title: { "zh-Hant": "最壞的話說完，仍要離開", en: "The Worst Is Said; He Must Still Leave" },
              narrative: sceneNarrative("西格爾德不准外舅留下空白，要求知道死亡由誰帶來。格里皮爾終於把背叛與死說到最後。", "詩沒有重演任何一首詩中的殺局，也沒有讓預言替人免除它。", "它在告別處結束：知道未來不是逃出未來，西格爾德仍要騎離這座廳堂。", "Sigurth does not let his uncle leave a blank; he asks who will bring death. Gripir finally speaks betrayal and death to the end.", "The poem does not replay the killing scene of any lay, nor does prophecy excuse anyone from it.", "It ends at departure: knowing the future is not escaping it, and Sigurth still rides away from the hall."),
            }),
          ],
        },
        {
          id: "norse-svipdagsmol-route",
          order: 10,
          title: { "zh-Hant": "斯維普達格之歌：咒語、門前與相認", en: "Svipdagsmol: Charms, the Gate, and Recognition" },
          summary: { "zh-Hant": "格羅阿在墳前念出九道咒；下一首詩已把斯維普達格放在火環廳前，最後由孟格洛斯說出等待。", en: "Groa speaks nine charms at the grave; the next poem already places Svipdag at the fire-ringed hall, where Mengloth finally speaks her waiting." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依貝洛斯引言、〈格羅阿之咒〉第 1–16 節與〈菲奧爾斯維德之歌〉第 17–66 節。這是兩首晚期紙本詩的合讀；中間未被寫下的行程、守備名稱的不確定，以及孟格洛斯與其他女神的推測均不被補作事件。", en: "Ordered by Bellows’s introduction, Grougaldr 1–16, and Fjolsvinnsmol 17–66. This is a composite reading of two late paper-manuscript poems; the unwritten journey, unstable defensive names, and speculation identifying Mengloth with other goddesses are not supplied as events." },
          segments: [
            scene("norse-svipdagsmol", ["stepmothers-charge", "calling-groa"], { title: { "zh-Hant": "一件無名的差事，叫醒一位母親", en: "An Unnamed Errand Wakes a Mother" }, narrative: sceneNarrative("斯維普達格說繼母要他去尋孟格洛斯，卻沒有說明理由。害怕這個差事時，他走到格羅阿的墳門前，叫已逝的母親回答。", "格羅阿先問兒子為何仍要來找她；他承認自己怕那條路。這段對話只留下無名的繼母與一件難以承擔的命令。", "故事不替繼母安排心思，也不把這個開端改成一場已有地圖的英雄出征。", "Svipdag says that his stepmother has ordered him to find Mengloth, without giving her reason. Afraid of the errand, he comes to Groa’s grave threshold and calls on his dead mother to answer.", "Groa first asks why her son has come to seek her still; he admits that he fears the road. The dialogue leaves an unnamed stepmother and a command that is hard to bear.", "The story gives the stepmother no invented motive and does not remake this beginning into a mapped heroic expedition." ) }),
            scene("norse-svipdagsmol", ["nine-charms", "unwritten-passage"], { title: { "zh-Hant": "九道咒之後，詩留下空白", en: "After Nine Charms, the Poem Leaves a Blank" }, narrative: sceneNarrative("格羅阿逐一念出九道咒，為兒子可能遇到的危險求護。她給的是話與保護，並沒有替他跨過任何一座門。", "她的咒語結束後，下一首詩已直接讓斯維普達格站在火焰環繞的廳前。兩段之間沒有保留下來的行程。", "這條時間線把空白留在原處：不能從護身咒推演出一連串文本沒有寫下的試煉。", "Groa speaks nine charms in turn, asking protection against dangers her son may meet. She gives words and protection, not passage through any gate for him.", "When her charms end, the next poem already has Svipdag before a hall ringed by fire. No journey survives between the two passages.", "This timeline leaves that blank where it is: protective charms cannot be turned into a chain of trials the text never gives." ) }),
            scene("norse-svipdagsmol", ["gate-questioning", "hall-names"], { title: { "zh-Hant": "火環前，名字被拖進問答", en: "Before the Fire-Ring, a Name Is Drawn into Dialogue" }, narrative: sceneNarrative("菲奧爾斯維德在廳前問來客從何而來、要找誰。斯維普達格起初自稱「風寒」，守門人便以廳外的火焰與防備拒絕他。", "問答越說越深，帶出孟格洛斯、萊夫亞貝格與許多守備。這些名字有些在傳本與編校裡並不穩定，卻足以讓門前的拒絕成為兩人說話的節奏。", "故事不把名稱清單硬接成一條可以破解的關卡，因為詩本身沒有讓斯維普達格實際走完那個循環。", "Fjolsvith asks the visitor where he comes from and whom he seeks. Svipdag initially calls himself ‘Wind-Cold,’ and the gatekeeper rejects him with the fire and defences outside the hall.", "The exchange goes deeper, naming Mengloth, Lyfjaberg, and many defences. Some names are unstable in the witnesses and editorial handling, yet they give rhythm to the refusal at the gate.", "The story does not force that list of names into a solvable course, because the poem never has Svipdag actually complete such a cycle." ) }),
            scene("norse-svipdagsmol", ["name-recognized", "mutual-recognition"], { title: { "zh-Hant": "名字被認出，等待終於有人回答", en: "The Name Is Recognized; Waiting Receives an Answer" }, narrative: sceneNarrative("菲奧爾斯維德終於知道來客是斯維普達格，門前的盤問便停止了。", "孟格洛斯說自己一直在萊夫亞貝格等待；斯維普達格也說出長久的想念。", "這個版本在相認處收束，不補上婚禮、後來的生活，或把孟格洛斯固定認作任何其他女神。", "Fjolsvith finally learns that the visitor is Svipdag, and the gate-side questioning stops.", "Mengloth says that she has been waiting at Lyfjaberg, and Svipdag also speaks of long longing.", "This version closes at recognition, without supplying a wedding, a later life, or a fixed identification of Mengloth with any other goddess." ) }),
          ],
        },
        {
          id: "norse-grottasongr-route",
          order: 11,
          title: { "zh-Hant": "磨坊之歌：太平被磨成預告", en: "Grottasongr: Peace Ground into a Warning" },
          summary: { "zh-Hant": "芬雅與曼雅被迫推動格羅蒂，從王命下的財富與太平，一直唱到弗羅迪將失位的預告。", en: "Fenja and Menja are compelled to turn Grotti, singing from a king’s demanded wealth and peace to a warning that Frodi will lose his seat." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依二十四節工作歌安排。作品存於《散文埃達》手稿而非《王室手稿》；米辛格、夜襲與海鹽皆屬相鄰散文，不列為詩內場景。", en: "Ordered by the twenty-four-stanza work song. The poem survives in manuscripts of the Prose Edda rather than the Codex Regius; Mysing, the night raid, and sea salt belong to adjacent prose and are not poem scenes." },
          segments: [
            scene("norse-grottasongr", ["two-women-at-mill", "wealth-and-peace"], { title: { "zh-Hant": "兩位推磨者與王所要的太平", en: "Two Women at the Mill; the Peace the King Demands" }, narrative: sceneNarrative("芬雅與曼雅到了弗羅迪家，被帶到名為格羅蒂的巨大磨坊前。這座磨能隨推磨者的話產出所求之物。", "王要她們先磨出財富、太平和福祉；歌裡的願語甚至說，世上不應有人傷害另一人。", "太平在這裡不是兩位巨女自由給出的祝福，而是王命先要求的產物。", "Fenja and Menja arrive at Frodi’s house and are set before the great mill called Grotti. The mill can make what its turners name.", "The king first demands wealth, peace, and prosperity; the song even wishes that no one should harm another.", "Peace here is not a blessing freely offered by the two giant women. It is a product the king first commands." ) }),
            scene("norse-grottasongr", ["no-rest", "king-does-not-know"], { title: { "zh-Hant": "不得歇息，王也未曾問過", en: "No Rest, and a King Who Never Asked" }, narrative: sceneNarrative("弗羅迪聽見歌，卻只吩咐她們繼續；休息不得長過杜鵑鳴叫，或他念一首歌。", "兩女回應，他買下她們時只看見力量與外貌，從沒問過她們的來歷。", "命令越短促，磨坊裡的歌越不再只是替王祝福。", "Frodi hears the song but only orders them on: no rest longer than a cuckoo’s call or one of his own songs.", "The two women answer that when he bought them, he saw only strength and appearance, never asking where they came from.", "The sharper the command, the less the mill-song remains only a blessing for the king." ) }),
            scene("norse-grottasongr", ["giant-memory", "cold-at-frodi-house"], { title: { "zh-Hant": "巨人的記憶留在寒冷屋裡", en: "Giant Memory inside the Cold House" }, narrative: sceneNarrative("芬雅與曼雅在歌裡回望巨人親族、移動山石與瑞典戰場；這些既是自身記憶，也是給王的提醒。", "她們現在卻在弗羅迪家受役，腳踩泥濘、頭頂寒冷，推著被稱為敵人的磨石。", "詩讓遠古力量和眼前勞役同時被說出，不用一段更大的家譜取代兩人的聲音。", "Fenja and Menja look back in song to giant kin, moved mountains, and a Swedish battlefield; these are both their memories and a warning to the king.", "Yet they now serve in Frodi’s house, feet in mud and heads in cold, turning the millstone they call an enemy.", "The poem lets ancient strength and present labour be spoken together, without replacing the women’s voices with a larger genealogy." ), edgeIds: ["fenja-shares-mill-song-with-menja-grottasongr"] }),
            scene("norse-grottasongr", ["fire-and-fall", "mill-breaks"], { title: { "zh-Hant": "火光、失位與已磨完的話", en: "Fire, Lost Rule, and the Words ‘It Is Ground’" }, narrative: sceneNarrative("兩女說手不會獲准休息，隨即把磨聲推向帶血武器、王城火光和弗羅迪將失去的王座。", "磨坊震動，重石裂開；她們說：已磨完。", "二十四節歌到此結束。相鄰散文另講夜襲與海鹽，這條時間線不把那個後日談演進這座磨坊。", "The women say their hands will not be allowed rest, then drive the mill-song toward bloodied weapons, fire at the king’s hall, and Frodi’s lost throne.", "The mill trembles and the heavy stone breaks; they say that it is ground.", "The twenty-four stanzas end here. Adjacent prose tells of a night raid and sea salt, but this timeline does not turn that later tale into a scene at this mill." ) }),
          ],
        },
        {
          id: "norse-guthrunarkvitha-i-route",
          order: 12,
          title: { "zh-Hant": "古德倫第一歌：遺體前的悼歌", en: "Guthrun I: A Lament at the Body" },
          summary: { "zh-Hant": "古德倫在西古爾德遺體旁沉默；安慰、揭開裹屍布與控訴依次使悼歌展開。", en: "Guthrun is silent beside Sigurd’s body; comfort, unveiling, and accusation unfold the lament in turn." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依開頭散文、1–25 節及結尾散文安排；控訴與歸責均是本詩的發話，後續詩不被接入。", en: "Ordered by opening prose, stanzas 1–25, and closing prose; accusations and blame are speech within this poem, and later lays are not joined." },
          segments: [
            scene("norse-guthrunarkvitha-i", ["gudrun-cannot-weep", "gjaflaug-speaks"], { title: { "zh-Hant": "沉默與第一份安慰", en: "Silence and the First Comfort" }, narrative: sceneNarrative("古德倫守在西古爾德遺體旁，心近乎裂開卻哭不出來。賈夫勞格說起自己的失親，仍沒有碰到她的眼淚。", "這不是比較誰失去更多；詩讓第一份安慰失效，保留古德倫尚未能說出口的悲傷。", "賈夫勞格的名字只在這首悼歌裡出現，因此這條線也不替她補出詩外家譜。", "Guthrun keeps vigil beside Sigurd’s body, near to breaking but unable to weep. Gjaflaug tells of her own losses, yet cannot reach her tears.", "This is not a contest over who has lost more. The poem lets the first comfort fail, preserving Guthrun’s grief before she can speak it.", "Gjaflaug’s name appears only in this lament, so the route does not invent a biography for her beyond it." ) }),
            scene("norse-guthrunarkvitha-i", ["herborg-speaks", "gollrond-unveils-body"], { title: { "zh-Hant": "赫爾堡說完，裹屍布被揭開", en: "After Herborg Speaks, the Shroud Is Lifted" }, narrative: sceneNarrative("赫爾堡講完失親、埋葬與受俘，古德倫仍然沉默。戈爾倫德改命人揭開西古爾德的裹屍布，把他的頭放到古德倫膝上。", "兩個安慰者的經驗沒有被用來替代古德倫的悲傷；戈爾倫德改變的是她能否親眼面對遺體。", "這個轉折只屬本詩的悼歌場景，不重複西古爾德的死亡故事。", "Herborg finishes her account of loss, burial, and captivity, and Guthrun remains silent. Gollrond instead has Sigurd’s shroud lifted and his head placed on Guthrun’s knees.", "The two comforters’ experience does not replace Guthrun’s grief; Gollrond changes whether she can face the body herself.", "This turning point belongs only to the poem’s lament scene and does not repeat Sigurd’s death story." ) }),
            scene("norse-guthrunarkvitha-i", ["gudrun-weeps", "gudrun-accuses-brothers"], { title: { "zh-Hant": "眼淚變成對兄弟的話", en: "Tears Become Words for Her Brothers" }, narrative: sceneNarrative("古德倫看見血、失明的眼和胸口的傷，終於哭出來。她隨即把悲傷指向久基諸子，並點名貢納爾。", "她的控訴是悼詞中的發話，不替讀者完成一場跨文本的審判。", "詩在哭泣後才讓責任被說出，因而把哀傷與指責放在同一口氣裡。", "Guthrun sees blood, blinded eyes, and the chest wound, then finally weeps. She immediately directs grief toward Gjuki’s sons and names Gunnar.", "Her accusation is speech inside a lament, not a cross-text trial completed for the reader.", "Only after tears does the poem let responsibility be spoken, holding grief and blame in the same breath." ) }),
            scene("norse-guthrunarkvitha-i", ["gudrun-curses-gold", "brynhild-answers"], { title: { "zh-Hant": "金子、布琳希爾德與離去", en: "Gold, Brynhild, and Departure" }, narrative: sceneNarrative("古德倫詛咒貢納爾的金子，又把不幸指向布琳希爾德。布琳希爾德反過來把痛苦歸咎阿特利；這一節本身損壞，且與其他版本並不一致。", "結尾散文讓古德倫離開，並提到布琳希爾德死亡，卻把細節留給別的材料。", "悼歌在離開處停下：它讓多份說法相碰，沒有把其中一份封成唯一的西古爾德故事。", "Guthrun curses Gunnar’s gold and points grief toward Brynhild. Brynhild in turn blames Atli; the stanza itself is damaged and does not agree with other versions.", "Closing prose has Guthrun leave and mentions Brynhild’s death, while leaving details to other material.", "The lament stops with departure: it lets several accounts meet without sealing any one as the single Sigurd story." ) }),
          ],
        },
        {
          id: "norse-sigurtharkvitha-en-skamma-route",
          order: 13,
          title: { "zh-Hant": "西古爾德短歌：破誓的床邊版本", en: "The Short Lay of Sigurd: An Oath-Broken Bedside" },
          summary: { "zh-Hant": "短歌從久基家的誓言走到床邊殺局；它保留文本缺口與彼此競爭的版本。", en: "The Short Lay moves from Gjuki-house oaths to a bedside killing, retaining textual gaps and competing versions." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依引言與第 1–11、17–28 節安排；第 12–16 節缺失，床邊殺局不可與其他詩的戶外版本合併。", en: "Ordered by the introduction and stanzas 1–11 and 17–28; stanzas 12–16 are lost, and the bedside killing cannot be merged with another lay’s outdoor version." },
          segments: [
            scene("norse-sigurtharkvitha-en-skamma", ["oaths-with-gunnar", "gudrun-given", "riding-to-brynhild"], { title: { "zh-Hant": "信任、婚姻與同行求婚", en: "Trust, Marriage, and a Shared Wooing Ride" }, narrative: sceneNarrative("短歌先讓西古爾德信任久基的兒子，與他們立誓；古德倫被許給他，西古爾德隨貢納爾向布琳希爾德而去。", "這些安排把婚姻和盟誓放在同一個王家內，卻不替讀者整理成唯一無縫的家譜或前史。", "同行是本歌的開端，不是往後每一版本都相同的求婚證明。", "The Short Lay first has Sigurd trust Gjuki’s sons and swear oaths with them; Guthrun is given to him, and he rides with Gunnar toward Brynhild.", "These arrangements place marriage and alliance in one royal house without turning them into a single seamless genealogy or backstory.", "The shared ride is this lay’s beginning, not proof that every version of the wooing is the same." ) }),
            scene("norse-sigurtharkvitha-en-skamma", ["sword-between", "brynhild-speaks", "brynhild-presses-gunnar"], { title: { "zh-Hant": "一把劍與布琳希爾德的話", en: "A Sword and Brynhild’s Words" }, narrative: sceneNarrative("本歌說西古爾德和布琳希爾德同床時有一把劍隔在中間，接著讓布琳希爾德獨自說出她想要西古爾德。", "她以離去逼迫貢納爾面對西古爾德之死；這是詩中的壓力，不是整個傳統可以一次判定的責任表。", "劍、欲望與威脅都留在短歌的處理方式裡，因為其他詩和傳奇並不完全一致。", "This lay says a sword lay between Sigurd and Brynhild in bed, then lets Brynhild speak alone of wanting Sigurd.", "She presses Gunnar with departure and Sigurd’s death; this is pressure inside the poem, not a responsibility chart settled for the whole tradition.", "The sword, desire, and threat remain within the Short Lay’s handling, because other lays and sagas do not fully agree." ) }),
            scene("norse-sigurtharkvitha-en-skamma", ["hogni-refuses", "gunnar-chooses-gotthorm", "gotthorm-strikes"], { title: { "zh-Hant": "霍格尼拒絕，貢納爾另選戈特霍姆", en: "Hogni Refuses; Gunnar Chooses Gotthorm" }, narrative: sceneNarrative("霍格尼說不該以劍破壞和西古爾德立下的誓。貢納爾卻選中沒有參與誓言的戈特霍姆；遺失段落之後，劍已在西古爾德心口。", "時間線不填補第 21 節留下的空白，也不替戈特霍姆增加短歌沒有交代的心思。", "這是一個床邊／室內殺局版本；其他材料把死亡放在戶外，兩者都不被抹平。", "Hogni says the oaths with Sigurd must not be broken by swords. Gunnar instead chooses Gotthorm, who did not share the oaths; after a gap, the sword is already in Sigurd’s heart.", "The timeline does not fill the blank left in stanza 21 or add motives the Short Lay never gives Gotthorm.", "This is a bedside, indoor killing version; other material places the death outdoors, and neither is erased." ) }),
            scene("norse-sigurtharkvitha-en-skamma", ["sigurd-strikes-back", "gudrun-wakes", "sigurd-warns-gudrun"], { title: { "zh-Hant": "反擊、醒來與臨終話", en: "Return Stroke, Awakening, and Dying Words" }, narrative: sceneNarrative("西古爾德起身反殺戈特霍姆。古德倫在他身邊醒來時，西古爾德警告幼子無路可逃，並在本歌中把責任指向布琳希爾德。", "這是臨終者的話，不把他的指認升格成跨文本的最後裁決。", "短歌到此保留一個沒有真正關上的傷口：誓言已破，反擊完成，家族仇恨卻沒有結束。", "Sigurd rises and kills Gotthorm in return. As Guthrun wakes beside him, he warns that their young son cannot escape and, in this lay, names Brynhild.", "These are the words of a dying man; his attribution is not elevated into a cross-text final verdict.", "The lay leaves an open wound: the oaths are broken and the return stroke is complete, but the family feud has not ended." ) }),
          ],
        },
        {
          id: "norse-helreith-brynhildar-route",
          order: 14,
          title: { "zh-Hant": "布琳希爾德冥途歌：赫爾路上的辯白", en: "Brynhild’s Hell-Ride: A Defense on the Hel-Road" },
          summary: { "zh-Hant": "布琳希爾德死後在赫爾路上回答女巨人；詩把衝突的傳統留在她的辯白裡。", en: "After death, Brynhild answers a giantess on the Hel-road; the poem keeps conflicting traditions inside her defense." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依開頭散文與第 1–14 節安排；兩座火堆、身分和同床敘事均與其他來源有張力，不予統一。", en: "Ordered by opening prose and stanzas 1–14; its two pyres, identities, and shared-bed account stand in tension with other sources and are not unified." },
          segments: [
            scene("norse-helreith-brynhildar", ["brynhild-rides", "giantess-challenges"], { title: { "zh-Hant": "車走赫爾路，岩居者攔下來", en: "A Wagon on the Hel-Road, Stopped by a Rock-Dweller" }, narrative: sceneNarrative("火葬後，布琳希爾德乘車走向赫爾之路。詩一開始就和其他火葬說法不同，因此時間線只跟隨這一首歌的車行。", "岩居女巨人攔住她，並非為了宣布判決，而是逼出一場帶敵意的問答。", "死後道路在這裡是一個說話的場面：布琳希爾德仍要回應別人如何講述她。", "After the pyre, Brynhild rides toward Hel. The poem already differs from other funeral accounts, so the timeline follows only this lay’s wagon journey.", "The rock giantess stops her, not to announce a verdict but to force a hostile exchange.", "Here the road after death is a scene of speech: Brynhild must still answer how others tell her story." ) }),
            scene("norse-helreith-brynhildar", ["giantess-accuses", "brynhild-answers"], { title: { "zh-Hant": "指控遇上布琳希爾德的回答", en: "An Accusation Meets Brynhild’s Answer" }, narrative: sceneNarrative("女巨人說布琳希爾德害久基家衰敗；布琳希爾德反說久基的後人使她不幸、成為破誓者。", "兩邊都在說責任，但這不是時間線替讀者完成的審判。", "詩讓互相衝突的說法面對面存在，並不要求我們把其中一方變成全傳統唯一答案。", "The giantess says Brynhild brought down Gjuki’s house; Brynhild answers that Gjuki’s heirs made her joyless and a breaker of oaths.", "Both sides speak of responsibility, but the timeline does not complete a trial for the reader.", "The poem lets conflicting accounts stand face to face without requiring one to become the only answer for the tradition." ) }),
            scene("norse-helreith-brynhildar", ["brynhild-recalls-battle", "odin-punishes"], { title: { "zh-Hant": "一場勝利換來沉睡與火環", en: "A Victory Brings Sleep and Fire" }, narrative: sceneNarrative("布琳希爾德說她使希亞爾姆貢納爾下冥，把勝利給另一人；奧丁因此震怒。", "接著是盾牆、沉睡和高火。這是她回顧自身過去的版本，不替所有女武神故事訂出同一套懲罰規則。", "火環讓下一個來者必須被辨認，卻不把布琳希爾德壓成只有等待被救的一個角色。", "Brynhild says she sent Hjalmgunnar down to Hel and gave victory to another man, drawing Odin’s anger.", "Shields, sleep, and high fire follow. This is her own version of the past, not one punishment rule for every valkyrie story.", "The ring of fire makes the next arrival recognizable without reducing Brynhild to someone who only waits to be rescued." ) }),
            scene("norse-helreith-brynhildar", ["sigurd-crosses-fire", "shared-bed"], { title: { "zh-Hant": "金、同床與沒有封口的說法", en: "Gold, a Shared Bed, and an Account Left Open" }, narrative: sceneNarrative("西古爾德帶著法夫尼爾守護的金穿過火環。布琳希爾德說二人同床八夜未相碰；古德倫的話則讓她得知受騙。", "這個版本既有可能插入的詩節，也同其他西古爾德材料不完全一致。", "她最後說自己仍會同西古爾德活著；時間線停在這句辯白，不把它做成已驗證的死後規則。", "Sigurd crosses the fire with the gold Fafnir guarded. Brynhild says they shared a bed untouched for eight nights; Guthrun’s words then make her learn she was deceived.", "This version includes a possibly interpolated stanza and does not fully agree with other Sigurd material.", "She ends by saying she and Sigurd will still live together; the timeline stops at that defense instead of making it a verified afterlife rule." ) }),
          ],
        },
        {
          id: "norse-brot-sigurtharkvithu-route",
          order: 15,
          title: { "zh-Hant": "西古爾德殘篇：戶外殺局的版本", en: "Fragment of a Sigurd Lay: The Outdoor Killing Version" },
          summary: { "zh-Hant": "殘篇從破誓爭辯走到戶外殺局與烏鴉預言，卻不抹平床邊版本。", en: "The fragment moves from an oath dispute to an outdoor killing and raven prophecy without erasing the bedside version." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依存留第 1–20 節與結尾散文安排；八頁已失，戶外與床邊殺局明確並存。", en: "Ordered by surviving stanzas 1–20 and closing prose; eight leaves are lost, and outdoor and bedside killings explicitly coexist." },
          segments: [
            scene("norse-brot-sigurtharkvithu", ["oath-dispute", "gotthorm-prepared"], { title: { "zh-Hant": "爭辯把戈特霍姆推向殺局", en: "A Dispute Moves Gotthorm toward the Killing" }, narrative: sceneNarrative("貢納爾說西古爾德破誓；霍格尼把恨意指向布琳希爾德。格里姆希爾德隨即讓人餵戈特霍姆狼與蛇肉。", "殘篇沒有留下這場爭辯之前的全部材料，因此時間線不替誰補出完整動機。", "餵食是一個古老的煽動凶暴母題，不是可驗證的心理診斷。", "Gunnar says Sigurd broke oaths, and Hogni points to Brynhild’s anger. Grimhild then has Gotthorm fed wolf and snake flesh.", "The fragment does not retain all the material before this dispute, so the timeline does not supply anyone’s full motive.", "The feeding is an old motif for inciting ferocity, not a verifiable psychological diagnosis." ) }),
            scene("norse-brot-sigurtharkvithu", ["outdoor-death", "raven-speaks"], { title: { "zh-Hant": "萊茵河以南與烏鴉的話", en: "South of the Rhine and the Raven’s Words" }, narrative: sceneNarrative("殘篇把西古爾德的死亡放在萊茵河以南的戶外。烏鴉隨後說阿特利之刃、鎖鏈與尼夫龍家的後果。", "結尾散文也承認另一種床邊殺局；這一幕只走戶外版本，沒有把另一個版本刪去。", "烏鴉的話指向後來，並不在此刻提前實現。", "The fragment places Sigurd’s death outdoors south of the Rhine. The raven then speaks of Atli’s blade, chains, and the Niflungs’ consequences.", "Closing prose also admits a different bedside killing; this scene follows only the outdoor version and does not delete the other.", "The raven’s words point later, rather than being fulfilled here early." ) }),
            scene("norse-brot-sigurtharkvithu", ["gudrun-grieves", "brynhild-answers"], { title: { "zh-Hant": "古德倫哀哭，布琳希爾德反說守誓", en: "Guthrun Grieves; Brynhild Says the Oaths Were Kept" }, narrative: sceneNarrative("古德倫為西古爾德哀哭，布琳希爾德卻對貢納爾說西古爾德曾守誓。", "她的話與貢納爾的指控互相衝突；時間線讓兩種說法同時可見，而非裁決誰是真相。", "西古爾德死後，王庭沒有人只剩下一種說法或感受。", "Guthrun grieves for Sigurd, while Brynhild tells Gunnar that Sigurd kept his oaths.", "Her words conflict with Gunnar’s accusation; the timeline keeps both accounts visible rather than deciding which is true.", "After Sigurd’s death, no single account or feeling prevails at court." ) }),
            scene("norse-brot-sigurtharkvithu", ["sword-between", "brynhild-dreams"], { title: { "zh-Hant": "隔床之劍與未實現的夢", en: "The Sword Between and an Unfulfilled Dream" }, narrative: sceneNarrative("布琳希爾德回憶鋒利的劍隔在她與西古爾德之間，又夢見貢納爾被束縛、尼夫龍家墜落。", "隔床之劍屬她提出的求婚版本；夢也只是殺局後向未來伸出的說法。", "殘篇停在預言的邊緣，讓後續英雄詩處理它們的回聲。", "Brynhild recalls the sharp sword between her and Sigurd, then dreams Gunnar bound and the Niflungs falling.", "The sword belongs to her version of the wooing, and the dream is only speech reaching toward a future after the killing.", "The fragment stops at prophecy’s edge and leaves later heroic lays to handle its echoes." ) }),
          ],
        },
        {
          id: "norse-guthrunarkvitha-ii-route",
          order: 16,
          title: { "zh-Hant": "古德倫第二歌：回顧與不祥夢", en: "The Old Lay of Guthrun: Recollection and Ominous Dream" },
          summary: { "zh-Hant": "古德倫在阿特利處回顧西古爾德、丹麥與再婚，結尾停在不祥夢。", en: "At Atli’s court, Guthrun recalls Sigurd, Denmark, and remarriage, ending at an ominous dream." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依開頭散文與第 1–45 節安排；開頭時代錯置、死亡版本、缺行和夢兆都不被統一或提前實現。", en: "Ordered by opening prose and stanzas 1–45; the opening anachronism, death versions, omissions, and dream omens are neither unified nor fulfilled early." },
          segments: [
            scene("norse-guthrunarkvitha-ii", ["opening", "marriage"], { title: { "zh-Hant": "共同哀哭回到早年婚配", en: "Shared Grief Returns to an Earlier Marriage" }, narrative: sceneNarrative("提奧達雷克與古德倫同在阿特利處哀哭；古德倫從久基把她交給西古爾德說起。", "這個散文框架帶有時代錯置，不是時間線替讀者確認的歷史宮廷。", "它讓古德倫成為回顧者，而非只在一個死亡場面被觀看的人。", "Thjothrek and Guthrun grieve together at Atli’s court; Guthrun begins with Giuki giving her to Sigurd.", "The prose frame carries an anachronism and is not historical court fact confirmed by the timeline.", "It makes Guthrun a narrator of recollection, not someone watched only in a death scene." ) }),
            scene("norse-guthrunarkvitha-ii", ["grani-returns", "death-recalled"], { title: { "zh-Hant": "空鞍、南路與兩種死亡版本", en: "An Empty Saddle, the Southern Road, and Two Death Versions" }, narrative: sceneNarrative("格拉尼無主人返回，古德倫向牠求訊。霍格尼說西古爾德死在南路。", "這是森林／戶外版本的回顧，不能改正短歌的床邊版本或殘篇的安排。", "馬的低頭與兄弟的話讓失去先被感到，才被說出。", "Grani returns without his master, and Guthrun asks him for news. Hogni says Sigurd died on the southern road.", "This is a recollected forest/outdoor version; it cannot correct the bedside version in the Short Lay or the Fragment’s arrangement.", "The horse’s lowered head and the brothers’ words make loss felt before it is spoken." ) }),
            scene("norse-guthrunarkvitha-ii", ["denmark", "return"], { title: { "zh-Hant": "丹麥的停留與被找回", en: "A Danish Stay and Being Found Again" }, narrative: sceneNarrative("古德倫離開殺局後在丹麥與索拉同住。格里姆希爾德找到她，久基家再次把她帶進與阿特利的婚配安排。", "遺失和插入的行文不容許我們補出一條完整的說服過程。", "這一幕只寫回返與安排，讓古德倫的哀傷沒有被『補償』抹掉。", "After the killing, Guthrun lives in Denmark with Thora. Grimhild finds her, and Gjuki’s house again brings her into a marriage arrangement with Atli.", "Omissions and interpolations do not allow a complete process of persuasion to be invented.", "This scene records return and arrangement without letting ‘amends’ erase Guthrun’s grief." ) }),
            scene("norse-guthrunarkvitha-ii", ["atli-court", "dream"], { title: { "zh-Hant": "新宮廷未抹去舊人，不祥夢也未實現", en: "A New Court Does Not Erase the Old; the Dream Is Not Fulfilled" }, narrative: sceneNarrative("即使在阿特利處，古德倫仍回想西古爾德。阿特利夢見子嗣危險，古德倫回答並把不祥感連向兄弟。", "夢是一段對話中的預兆，不是時間線提前展演兒子的死亡。", "這首歌停在對未來的恐懼，讓後續材料自己處理它的回聲。", "Even at Atli’s court, Guthrun still remembers Sigurd. Atli dreams danger to their sons; Guthrun answers and joins foreboding to her brothers.", "The dream is an omen within dialogue, not an early enactment of the sons’ deaths.", "The lay stops at fear of the future and lets later material handle its echo." ) }),
          ],
        },
        {
          id: "norse-guthrunarkvitha-iii-route",
          order: 17,
          title: { "zh-Hant": "古德倫第三歌：沸水鍋", en: "The Third Lay of Guthrun: The Boiling Cauldron" },
          summary: { "zh-Hant": "赫爾嘉的控告使古德倫面對阿特利；她以誓言和沸水鍋洗清自己，赫爾嘉隨後受罰。", en: "Herkja’s accusation brings Guthrun before Atli; Guthrun clears herself by oath and boiling cauldron, and Herkja is then punished." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依引言、開頭散文與第 1–10 節安排；提奧德里克在阿特利宮廷的時代錯置、薩克西身份未明，以及試煉的詩歌限定都不被補寫。", en: "Ordered by the introduction, opening prose, and stanzas 1–10; Thjothrek’s anachronistic court setting, Saxi’s unknown identity, and the lay-bound ordeal are not supplemented." },
          segments: [
            scene("norse-guthrunarkvitha-iii", ["accusation", "atli-questions"], { title: { "zh-Hant": "赫爾嘉的話令阿特利起疑", en: "Herkja’s Words Make Atli Suspicious" }, narrative: sceneNarrative("赫爾嘉說自己看見古德倫和提奧德里克同臥。阿特利的沉默與怒意隨即轉成直接的質問。", "這是詩中一方提出的告發；它不會因為先說出口便成為已證實的事。", "古德倫必須在一個把私密哀傷誤讀成背叛的宮廷裡回答。", "Herkja says she saw Guthrun and Thjothrek together in bed. Atli’s troubled silence hardens into a direct question.", "The accusation belongs to one speaker in the poem; being spoken first does not make it proven.", "Guthrun must answer in a court that has mistaken private grief for betrayal." ) }),
            scene("norse-guthrunarkvitha-iii", ["gudrun-oath", "gudrun-grief"], { title: { "zh-Hant": "她以誓言說出悲傷", en: "She Swears and Names Her Grief" }, narrative: sceneNarrative("古德倫以聖石起誓，說她和提奧德里克只是暗中互訴悲傷。她接著對阿特利說，他奪走了她的兄弟與族人。", "詩歌把提奧德里克和她的相處說成哀傷的交談，也不替兩人的歷史身份補上更多細節。", "她沒有讓哀傷只停在回憶裡，而是把它帶到阿特利面前。", "Guthrun swears by the sacred stone that she and Thjothrek only spoke their griefs in private. She then tells Atli that he took her brothers and kinsmen.", "The lay names her meeting with Thjothrek as shared grief and supplies no fuller biography for their historical identities.", "She does not leave grief in memory; she brings it before Atli." ) }),
            scene("norse-guthrunarkvitha-iii", ["saxi-called", "gudrun-tested"], { title: { "zh-Hant": "鍋底的石頭", en: "Stones from the Kettle’s Bottom" }, narrative: sceneNarrative("古德倫召來薩克西祝聖沸水鍋，然後把手伸到鍋底，取出閃亮的石頭。", "薩克西在詩中只有「南方王」這個稱呼；鍋的儀式背景不必由後人補成完整傳記。", "她的手沒有受傷，於是控訴第一次有了不能忽略的回答。", "Guthrun summons Saxi to hallow the boiling cauldron, then reaches its bottom and brings out shining stones.", "Saxi has only the poem’s title, “southern king”; the ritual setting does not need a supplied biography.", "Her hand is unhurt, and the accusation receives an answer that cannot be ignored." ) }),
            scene("norse-guthrunarkvitha-iii", ["gudrun-cleared", "herkja-tested"], { title: { "zh-Hant": "同一鍋落在赫爾嘉手上", en: "The Same Kettle for Herkja" }, narrative: sceneNarrative("阿特利見古德倫的手完好，便要赫爾嘉到同一鍋前受試。赫爾嘉雙手燙傷，最後被投入泥沼。", "這是這首短歌給出的結局，不把它擴成其他故事裡的報復或法律制度。", "古德倫的冤屈在此得到回應，而這首歌也在赫爾嘉受罰處停下。", "Atli sees Guthrun’s hand whole and orders Herkja to the same kettle. Herkja’s hands are burned, and she is finally thrown into a bog.", "This is the ending given by this short lay, not a route into other stories of revenge or legal practice.", "Guthrun’s grievance receives its answer here, and the lay stops with Herkja’s punishment." ) }),
          ],
        },
        {
          id: "norse-oddrunargratr-route",
          order: 18,
          title: { "zh-Hant": "奧德倫之哀歌：趕赴與未能相救", en: "The Lament of Oddrun: The Journey and the Failed Rescue" },
          summary: { "zh-Hant": "奧德倫先救助博爾格尼，再回顧自己愛貢納爾、趕去相救卻失去他的哀歌。", en: "Oddrun aids Borgny, then recalls loving Gunnar, rushing to save him, and losing him." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依引言、開頭散文與第 1–32 節安排；手稿節序、地理與阿特利之母的版本差異都保留在詩中，不由時間線修正。", en: "Ordered by the introduction, opening prose, and stanzas 1–32; manuscript order, geography, and the variant around Atli’s mother remain those of the poem rather than being corrected by the timeline." },
          segments: [
            scene("norse-oddrunargratr", ["labour", "journey"], { title: { "zh-Hant": "奧德倫趕去博爾格尼身邊", en: "Oddrun Rides to Borgny" }, narrative: sceneNarrative("博爾格尼因難產受苦，奧德倫得知後牽出黑馬，趕到高廳。", "這首歌的地名和路徑並不穩定；此處只跟隨她從聽聞到抵達的動作。", "她走進床邊時，危險還沒有被講成自己的哀歌。", "Borgny suffers in difficult labour. Hearing of it, Oddrun brings out her black horse and rides to the high hall.", "The lay’s places and route are unstable; this scene follows only her move from hearing to arrival.", "When she reaches the bedside, danger has not yet become her own lament." ) }),
            scene("norse-oddrunargratr", ["secret", "birth"], { title: { "zh-Hant": "秘密相愛後的孩子", en: "Children after a Secret Love" }, narrative: sceneNarrative("侍女說維爾蒙德與博爾格尼秘密相伴五年。奧德倫坐在床邊施咒，博爾格尼終於生下一男一女。", "詩中對『霍格尼殺手』的稱呼需要註釋才能連到維爾蒙德，因此這裡保留它作為詩內的說法。", "新生讓痛苦暫時退去，也讓兩名女子的舊事開始浮現。", "A servant says Vilmund and Borgny were secretly together for five years. Oddrun sits at the bedside and chants, and Borgny finally gives birth to a son and a daughter.", "The poem’s phrase for Hogni’s slayer needs a note to connect it with Vilmund, so it remains an attributed claim within the lay.", "The birth briefly draws pain away and brings the women’s older history to the surface." ) }),
            scene("norse-oddrunargratr", ["thanks", "lament"], { title: { "zh-Hant": "感謝引出奧德倫的失去", en: "Thanks Draws Out Oddrun’s Loss" }, narrative: sceneNarrative("博爾格尼感謝奧德倫，奧德倫卻說自己只是履行舊誓。她開始回憶對貢納爾的愛，以及阿特利拒絕接納婚配。", "這是奧德倫的第一人稱回顧，不把詩外的家譜與婚約補進她的話。", "幫別人渡過生產，反而讓她不得不說起自己從未渡過的失去。", "Borgny thanks Oddrun, but Oddrun says she only fulfilled an old oath. She begins to remember loving Gunnar and Atli’s refusal of the marriage payment.", "This is Oddrun’s first-person recollection and does not supply genealogy or marriage terms beyond her words.", "Helping another woman through labour makes her speak of a loss she could never cross." ) }),
            scene("norse-oddrunargratr", ["spying", "rescue"], { title: { "zh-Hant": "豎琴聲後仍來得太遲", en: "Even the Harp Comes Too Late" }, narrative: sceneNarrative("阿特利派人窺探奧德倫和貢納爾；後來霍格尼被害，貢納爾落入蛇穴。奧德倫聽見豎琴聲，乘船趕去，卻無法挽回他。", "第 30 節對阿特利之母的說法與其他版本不合；這一幕只保留奧德倫哀歌中的未命名婦人。", "故事在奧德倫對貢納爾的哀哭裡結束，而不是替其他阿特利故事預先裁定版本。", "Atli sends men to spy on Oddrun and Gunnar; later Hogni is killed and Gunnar is put in the serpent cave. Oddrun hears the harp, sails to him, and cannot bring him back.", "Stanza 30’s account of Atli’s mother differs from other versions; this scene retains only the unnamed woman in Oddrun’s lament.", "The story ends with Oddrun grieving for Gunnar, rather than deciding versions for other Atli stories in advance." ) }),
          ],
        },
        {
          id: "norse-atlakvitha-route",
          order: 19,
          title: { "zh-Hant": "阿特利之歌：戒指、蛇穴與復仇", en: "The Lay of Atli: Ring, Serpents, and Revenge" },
          summary: { "zh-Hant": "阿特利以財寶誘使古德倫的兄弟赴宴；她的警告未果，最後以宴席與火回應。", en: "Atli lures Guthrun’s brothers to a feast with wealth; her warning fails, and she answers with a feast and fire." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依開頭散文與第 1–46 節安排；缺節、說話者不明與兩個兒子被殺的過程不由時間線補全。", en: "Ordered by opening prose and stanzas 1–46; gaps, uncertain speakers, and the killing of the two sons are not filled in by the timeline." },
          segments: [
            scene("norse-atlakvitha", ["invitation", "warning"], { title: { "zh-Hant": "一封邀請與一枚戒指", en: "An Invitation and a Ring" }, narrative: sceneNarrative("阿特利派克內弗羅特帶著財寶承諾而來。古德倫卻把狼毛織進戒指，讓兄弟看出這不是普通邀請。", "這枚戒指是本歌的警告；不改寫成《阿特利敘事歌》的盧恩訊息。", "兄弟知道危險，仍決定經暗林赴約。", "Atli sends Knefröth with promises of wealth. Guthrun, however, weaves wolf hair into a ring so her brothers can see that this is no ordinary invitation.", "The ring is this lay’s warning, not a rewritten version of the runic message in the Atlamol.", "The brothers understand the danger and still choose to go through Myrkwood." ) }),
            scene("norse-atlakvitha", ["journey", "capture"], { title: { "zh-Hant": "暗林盡頭的擒縛", en: "Capture beyond Myrkwood" }, narrative: sceneNarrative("貢納爾和霍格尼帶著隨從離家。到達阿特利一方後，貢納爾被縛，霍格尼仍以劍替他奮戰。", "詩在這段有缺失，因此這一幕只接住它明說的抵達、俘獲與抵抗。", "阿特利接著要求寶藏，而貢納爾要先看霍格尼的心。", "Gunnar and Hogni leave home with their followers. On reaching Atli’s side, Gunnar is bound while Hogni still fights for him with his sword.", "The poem is defective here, so this scene holds only its explicit arrival, capture, and resistance.", "Atli then seeks the hoard, and Gunnar first demands to see Hogni’s heart." ) }),
            scene("norse-atlakvitha", ["hearts", "hoard"], { title: { "zh-Hant": "心臟沒有說謊，寶藏沒有交出", en: "The Heart Does Not Lie; the Hoard Is Not Given" }, narrative: sceneNarrative("赫亞利的心仍在盤上顫動，貢納爾因而識破試探。霍格尼死後，他把尼弗隆格的寶藏祕密帶往萊茵河與蛇穴。", "赫亞利遇害和心臟段落受殘缺文本影響，故事只標示其不確定性，不替它補對話。", "在蛇穴裡，貢納爾仍以豎琴守住不交出寶藏的選擇。", "Hjalli’s heart trembles on the platter, so Gunnar sees through the test. After Hogni dies, he takes the secret of the Niflungs’ hoard toward the Rhine and the serpent den.", "Hjalli’s death and the heart episode are affected by defective text; the story marks the uncertainty without inventing dialogue.", "In the serpent den, Gunnar still uses the harp to keep his choice not to surrender the hoard." ) }),
            scene("norse-atlakvitha", ["feast", "fire"], { title: { "zh-Hant": "古德倫的宴席與火", en: "Guthrun’s Feast and Fire" }, narrative: sceneNarrative("古德倫讓阿特利在宴席上得知自己吃下兩個兒子的心與肉。阿特利醉而無備，她用劍殺夫，再焚毀殿堂。", "詩給出兩子已死與宴席的結果，卻沒有完整描寫殺害的過程；這裡不把沉默變成細節。", "她先放走無辜的狗和奴僕，復仇才落在仍留在殿中的人身上。", "At the feast Guthrun lets Atli learn that he has eaten the hearts and flesh of his two sons. Drunk and unguarded, he is killed by her sword before she burns the hall.", "The poem gives the sons’ deaths and the feast’s result but not a full killing scene; this timeline does not turn silence into detail.", "She first lets the innocent dogs and slaves go, then revenge falls on those still in the hall." ) }),
          ],
        },
        {
          id: "norse-guthrunarhvot-route",
          order: 20,
          title: { "zh-Hant": "古德倫之催促：女兒之死與最後哀歌", en: "Guthrun’s Inciting: A Daughter’s Death and Final Lament" },
          summary: { "zh-Hant": "斯萬希爾德死於約爾蒙雷克之命；古德倫催促兒子復仇，隨後把失去說成最後的哀歌。", en: "Svanhild dies by Jormunrek’s command; Guthrun urges her sons to vengeance, then makes loss into a final lament." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依引言、開頭散文與第 1–22 節安排；比基的指控、厄爾普母系與詩末求死都保留不確定性。", en: "Ordered by introduction, opening prose, and stanzas 1–22; Bikki’s accusation, Erp’s maternal line, and the ending’s death wish remain uncertain." },
          segments: [
            scene("norse-guthrunarhvot", ["sea", "svanhild"], { title: { "zh-Hant": "渡海後的新家與女兒的婚姻", en: "A New Home after the Sea; a Daughter’s Marriage" }, narrative: sceneNarrative("古德倫在海上未能沉沒，被浪帶到約納克之地，並生下索利、厄爾普與哈姆瑟。斯萬希爾德則嫁給約爾蒙雷克。", "這是開頭散文的家系；厄爾普在相連詩歌有不同母系，不被此時間線裁定。", "兩個家室的並列，準備了下一場由王命造成的失去。", "Unable to drown at sea, Guthrun is carried to Jonak’s land and bears Sorli, Erp, and Hamther. Svanhild, meanwhile, marries Jormunrek.", "This lineage comes from opening prose; a linked poem gives Erp a different maternal line, which this timeline does not decide.", "The two households stand beside each other before the next loss caused by a king’s command." ) }),
            scene("norse-guthrunarhvot", ["accusation", "death"], { title: { "zh-Hant": "一段含混傳話，兩場處死", en: "An Ambiguous Report and Two Killings" }, narrative: sceneNarrative("比基向約爾蒙雷克傳話，國王絞死兒子蘭德維爾，又令馬踩死斯萬希爾德。", "散文沒有說明比基究竟傳了甚麼；註釋只推測是關係指控，因此不把它寫成證實的罪行。", "消息回到古德倫，她不讓兒子沉默地留在家中。", "Bikki reports something to Jormunrek. The king hangs his son Randver and has horses trample Svanhild.", "The prose never says what Bikki reports; the note only infers a relationship accusation, so it is not written as proven guilt.", "When the news reaches Guthrun, she will not let her sons remain silently at home." ) }),
            scene("norse-guthrunarhvot", ["incitement", "arming"], { title: { "zh-Hant": "催促、反問與鎧甲", en: "Incitement, a Challenge, and Armour" }, narrative: sceneNarrative("古德倫要索利與哈姆瑟為姊妹復仇。哈姆瑟反問她殺子報復阿特利的代價；她笑著取出頭盔與鎧甲。", "這是家內的爭論，不把復仇當成無代價的英雄義務。", "哈姆瑟預告兩兄弟不會回來，故事轉向母親獨自留下的哀歌。", "Guthrun tells Sorli and Hamther to avenge their sister. Hamther asks about the cost of her killing her sons to avenge herself on Atli; she laughs and brings out helmets and mail.", "This is an argument inside the family, not a cost-free heroic duty of revenge.", "Hamther foretells that the brothers will not return, and the story turns toward the mother left alone with lament." ) }),
            scene("norse-guthrunarhvot", ["lament", "pyre"], { title: { "zh-Hant": "三個家之後的最後呼喚", en: "A Last Call after Three Homes" }, narrative: sceneNarrative("古德倫回想西古爾德、阿特利、兄弟和斯萬希爾德的死亡，最後呼喚西古爾德並要求柴堆。", "引言認為詩末幾節可能來自另一失傳版本；它表達求死意願，卻不保證後來發生了甚麼。", "本歌在哀歌被說盡之處結束，而不是替〈哈姆瑟之歌〉提前交代復仇結局。", "Guthrun remembers the deaths of Sigurth, Atli, her brothers, and Svanhild, then calls to Sigurth and asks for a pyre.", "The introduction considers the last stanzas possibly drawn from another lost version; they express a wish to die without guaranteeing what follows.", "The lay ends when the lament has been spoken, not by giving the later outcome of Hamthesmol in advance." ) }),
          ],
        },
        {
          id: "norse-hamthesmol-route", order: 21,
          title: { "zh-Hant": "哈姆瑟之歌：復仇的殘缺終局", en: "Hamthesmol: The Fragmented End of Revenge" },
          summary: { "zh-Hant": "兄弟上路復仇，殺害厄爾普後傷及約爾蒙雷克，最終死於石下。", en: "The brothers set out for vengeance, kill Erp, wound Jormunrek, and finally die beneath stones." },
          chronologyMode: "variant", coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依引言與第 1–31 節；文本拼貼、缺行與厄爾普母系異說均不由時間線修補。", en: "Ordered by the introduction and stanzas 1–31; patchwork text, gaps, and Erp’s maternal-line variant are not repaired by the timeline." },
          segments: [
            scene("norse-hamthesmol", ["incitement", "departure"], { title: { "zh-Hant": "再次催促，踏上不歸路", en: "Incited Again, Setting Out" }, narrative: sceneNarrative("古德倫再次催促哈姆瑟與索利為斯萬希爾德復仇。二人知道自己未必能回來，仍然出發。", "這首詩與前一首共享若干行文，但不因此把兩首的家系異說消除。", "路上同行的厄爾普會令復仇變得更破碎。", "Guthrun again urges Hamther and Sorli to avenge Svanhild. Knowing they may not return, they still set out.", "This poem shares wording with the earlier lay but does not erase their lineage variants.", "Their companion Erp will make revenge more fractured." ) }),
            scene("norse-hamthesmol", ["road", "road-killing"], { title: { "zh-Hant": "同行者被殺", en: "The Companion Is Killed" }, narrative: sceneNarrative("厄爾普與兄弟同行，卻在路上被殺。文本與註釋都不能補足動機和對話。", "此處不把殘缺段落擴寫成可確證的衝突。", "哈姆瑟其後承認，厄爾普若還活著，刺殺或許已成功。", "Erp travels with the brothers but is killed on the road. Neither text nor note can fill in motive and dialogue.", "This timeline does not expand defective passage into certain conflict.", "Hamther later admits that the attack might have succeeded had Erp lived." ) }),
            scene("norse-hamthesmol", ["attack", "regret"], { title: { "zh-Hant": "傷及國王，後悔太遲", en: "Wounding the King, Regretting Too Late" }, narrative: sceneNarrative("兄弟傷及約爾蒙雷克，卻沒有完成刺殺。哈姆瑟回想厄爾普的缺席。", "現存詩的編排破碎，因此只保留結果與後悔，不假定完整戰場次序。", "他們的名聲和死亡在同一段殘詩裡被說出。", "The brothers wound Jormunrek but do not finish the killing. Hamther remembers Erp’s absence.", "The surviving poem is fragmented, so only result and regret are retained without assuming a full battle order.", "Their fame and death are spoken in the same damaged passage." ) }),
            scene("norse-hamthesmol", ["death", "aftermath"], { title: { "zh-Hant": "石頭之下的終局", en: "The End beneath Stones" }, narrative: sceneNarrative("索利倒在山牆旁，哈姆瑟倒在屋後。", "詩末只留下這個結局，不能倒推所有失落行文。", "復仇沒有恢復家室，只留下另一層古德倫的失去。", "Sorli falls beside the gable and Hamther behind the house.", "The poem leaves only this ending and cannot reconstruct all lost lines backward.", "Revenge does not restore a household; it leaves Guthrun with another layer of loss." ) }),
          ],
        },
        {
          id: "norse-drap-niflunga-route", order: 22,
          title: { "zh-Hant": "尼弗隆格之死：詩歌之間的橋", en: "Drap Niflunga: A Bridge between Lays" },
          summary: { "zh-Hant": "散文把西古爾德之死後的英雄詩線索連成導航。", en: "Prose joins the heroic-lay threads after Sigurth’s death into navigation." },
          chronologyMode: "variant", coverageStatus: "complete",
          coverageNote: { "zh-Hant": "只作為抄本的敘事橋；各首詩內異本仍保留在原故事中。", en: "A manuscript narrative bridge only; variants remain in their original lays." },
          segments: [
            scene("norse-drap-niflunga", ["bridge-1", "bridge-2"], { title: { "zh-Hant": "西古爾德之後", en: "After Sigurth" }, narrative: sceneNarrative("散文橋從西古爾德死後，帶讀者走向古德倫的下一段故事。", "它不以摘要取代各首詩。", "下一個入口是阿特利。", "The prose bridge leads from Sigurth’s death toward Guthrun’s next story.", "It does not replace the individual lays with a summary.", "The next entrance is Atli." ) }),
            scene("norse-drap-niflunga", ["bridge-3", "bridge-4"], { title: { "zh-Hant": "阿特利與兄弟", en: "Atli and the Brothers" }, narrative: sceneNarrative("阿特利的邀請和古德倫兄弟的死亡，在各首詩中各有細節。", "這裡只標出讀者應轉往哪一首。", "復仇由古德倫承接。", "Atli’s invitation and the brothers’ deaths have distinct details in separate lays.", "This bridge only marks where readers should turn.", "Guthrun carries revenge forward." ) }),
            scene("norse-drap-niflunga", ["bridge-5", "bridge-6"], { title: { "zh-Hant": "復仇之後的女兒", en: "A Daughter after Revenge" }, narrative: sceneNarrative("古德倫的故事沒有在阿特利後停止，斯萬希爾德成為另一條悲劇線。", "散文不裁定詩歌異本。", "哈姆瑟的故事由此接上。", "Guthrun’s story does not stop after Atli; Svanhild begins another tragic thread.", "The prose decides none of the poetic variants.", "Hamther’s lay connects from here." ) }),
            scene("norse-drap-niflunga", ["bridge-7", "bridge-8"], { title: { "zh-Hant": "橋的盡頭", en: "The Bridge’s End" }, narrative: sceneNarrative("散文停在哈姆瑟的遠征之前，讓詩歌自己說出殘缺的結局。", "橋接不是新的正典版本。", "讀者回到〈哈姆瑟之歌〉。", "The prose stops before Hamther’s expedition, leaving the fragmented ending to the lay itself.", "A bridge is not a new canonical version.", "The reader returns to Hamthesmol." ) }),
          ],
        },
        {
          id: "norse-fafnismol-route",
          order: 23,
          title: { "zh-Hant": "法夫尼爾之歌：龍血與鳥語", en: "Fafnismol: Dragon Blood and Bird-Speech" },
          summary: { "zh-Hant": "西古爾德伏殺法夫尼爾；臨終警告、雷金的圖謀與鳥語，令受詛寶藏成為下一段旅程。", en: "Sigurth ambushes Fafnir; dying warnings, Regin's plot, and bird-speech make the cursed hoard the next journey." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依引言、開頭散文與第 1–44 節；編纂層次、疑似插段與鳥語的多重來源都不被時間線抹平。", en: "Ordered by the introduction, opening prose, and stanzas 1–44; compilation layers, probable interpolations, and the bird-speech's multiple sources are not flattened by the timeline." },
          segments: [
            scene("norse-fafnismol", ["approach", "trench"], { title: { "zh-Hant": "足跡、溝與龍血", en: "Track, Trench, and Dragon Blood" }, narrative: sceneNarrative("西古爾德與雷金上格尼塔荒原，看見法夫尼爾從寶藏爬往水邊的足跡。西古爾德挖溝伏下，讓巨龍從上方爬過。", "開頭散文明說毒液流下，而劍刺進心臟；這裡不把它改成後來常見的正面決鬥。", "法夫尼爾仍能說話，刺殺於是轉成一段對話。", "Sigurth and Regin go up to Gnitaheith and find Fafnir's track from the hoard toward water. Sigurth digs a trench and waits as the dragon crawls overhead.", "The opening prose explicitly has venom pouring down and a sword driven to the heart; this is not remade as a familiar later face-to-face duel.", "Fafnir can still speak, so the slaying becomes a conversation." ) }),
            scene("norse-fafnismol", ["name", "warning"], { title: { "zh-Hant": "名字、黃金與背叛", en: "A Name, Gold, and Betrayal" }, narrative: sceneNarrative("法夫尼爾追問誰殺了他。西古爾德先隱名，後承認自己是西格蒙德之子。", "瀕死的龍警告金、財與指環會帶來禍害，又說雷金會背叛兩邊。這些是詩中說話者的警告，不是已完成的命運報告。", "雷金回來時，西古爾德已知道危險不會隨龍死去。", "Fafnir asks who has killed him. Sigurth first hides his name, then acknowledges that he is Sigmund's son.", "The dying dragon warns that gold, wealth, and rings bring ruin, and says Regin will betray both sides. These are a speaker's warnings in the poem, not reports of a fate already completed.", "When Regin returns, Sigurth already knows the danger has not died with the dragon." ) }),
            scene("norse-fafnismol", ["heart", "birds"], { title: { "zh-Hant": "心臟、血與鳥語", en: "Heart, Blood, and Bird-Speech" }, narrative: sceneNarrative("雷金取出兄長的心，叫西古爾德烤熟，自己躺下休息。西古爾德碰到熱血，把手指放進口中，忽然聽懂鳥語。", "鳥先警告雷金正在圖謀，之後又說起不同方向：居基家的女兒，與辛達爾山上的沉睡女戰士。", "引言與註釋指出鳥語段落可能不只一個來源，所以它是指引，不是強迫單一路線。", "Regin takes out his brother's heart, tells Sigurth to cook it, and lies down to rest. Sigurth touches the hot blood, puts his finger in his mouth, and suddenly understands birds.", "The birds first warn that Regin is plotting, then speak of different directions: Gjuki's daughter and a sleeping battle-maid on Hindarfjoll.", "The introduction and notes indicate that the bird passages may have more than one source, so this is guidance rather than one compulsory route." ) }),
            scene("norse-fafnismol", ["departure", "boundary"], { title: { "zh-Hant": "格拉尼背著寶藏離開", en: "Grani Leaves with the Hoard" }, narrative: sceneNarrative("西古爾德在法夫尼爾的巢中裝滿寶箱，取走恐懼之盔和其他寶物，放上格拉尼。馬要等他上背才肯前行。", "詩把寶藏從龍的巢穴帶往西古爾德的旅程，但沒有在這裡把後來所有悲劇演成定局。", "鳥語只開出通往布倫希爾德故事的門；那一首的異本仍需在其自身脈絡讀取。", "Sigurth fills chests in Fafnir's lair, takes the fear-helm and other treasures, and puts them on Grani. The horse will not move until he mounts.", "The poem takes the hoard out of the dragon's lair and into Sigurth's journey without performing every later tragedy as a settled outcome here.", "Bird-speech only opens the door toward Brynhild's story; variants of that lay still need their own context." ) }),
          ],
        },
        {
          id: "norse-sigrdrifumol-route",
          order: 24,
          title: { "zh-Hant": "希格德里法之歌：盾塔與長眠", en: "Sigrdrifumol: The Shield-Tower and the Long Sleep" },
          summary: { "zh-Hant": "西古爾德在辛達爾山喚醒布倫希爾德；她說明長眠，並把蜜酒、符文與殘存箴言交給他。", en: "Sigurth wakes Brynhild on Hindarfjoll; she explains the long sleep and gives him mead, runes, and surviving counsels." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依引言、開頭散文及第 1–37 節；第 2–4 與 20–21 節構成相遇核心，符文與箴言保留為相接的殘篇，不補成完整詩。", en: "Ordered by the introduction, opening prose, and stanzas 1–37; stanzas 2–4 and 20–21 form the meeting’s core, while runes and counsels remain adjoining fragments rather than a completed poem." },
          segments: [
            scene("norse-sigrdrifumol", ["mountain", "awakening"], { title: { "zh-Hant": "盾塔裡的長眠", en: "A Long Sleep in the Shield-Tower" }, narrative: sceneNarrative("西古爾德騎上辛達爾山，看見火光直上天空。盾塔裡睡著一名全副武裝的人；他取下頭盔，才知道那是一名女子。", "鎖子甲像貼著肌膚，他由頭口一路割到雙臂。鎧甲一除，她醒來，問是甚麼割開甲衣、打破睡眠。", "西古爾德以西格蒙德之子的身分回答；相遇由這場喚醒開始。", "Sigurth rides onto Hindarfjoll and sees a fire-like light rising to heaven. A fully armed figure sleeps in the shield-tower; only after he removes the helm does he know she is a woman.", "The mail seems grown to the flesh, so he cuts from the head opening to both arms. When the armour comes away, she wakes and asks what broke her sleep and cut her byrnie.", "Sigurth answers as Sigmund's son; their meeting begins with this awakening." ) }),
            scene("norse-sigrdrifumol", ["punishment", "mead"], { title: { "zh-Hant": "睡眠刺與一角蜜酒", en: "The Sleep-Thorn and a Horn of Mead" }, narrative: sceneNarrative("布倫希爾德說，自己曾殺死奧丁已許諾勝利的赫亞姆貢納爾，因此被睡眠刺刺中；奧丁又說她往後不再為戰士帶來勝利。", "她曾發誓，不嫁給知道恐懼的人。西古爾德問她的名字與智慧，她則把一角蜜酒交到他手上。", "這杯酒把剛醒來的女戰士帶進一段仍未完結的問答。", "Brynhild says that she killed Hjalmgunnar, whom Othin had promised victory, and was pierced with the sleep-thorn; Othin also said she would no longer bring victory to warriors.", "She had vowed not to marry a man who knew fear. Sigurth asks her name and her wisdom, and she gives him a horn of mead.", "The drink carries the newly wakened battle-maid into a dialogue that remains unfinished." ) }),
            scene("norse-sigrdrifumol", ["runes", "choice"], { title: { "zh-Hant": "符文之後，只剩幾句選擇", en: "After the Runes, Only a Few Lines of Choice" }, narrative: sceneNarrative("接在蜜酒後的是勝利、麥酒、出生、海浪與言語等符文教誨。它們被抄在兩人的對話之後，卻未必原本就屬於這場相遇。", "當布倫希爾德再開口，她把說與沉默交給西古爾德選擇，並提到兩人命定的災厄。西古爾德說自己不會逃走，只求她的愛語。", "中間失去的詩節不能由這幾句話補回，這段相遇只留下選擇的重量。", "After the mead come teachings of winning, ale, birth, waves, and speech runes. They are copied after the pair’s dialogue, yet may not originally belong to this meeting.", "When Brynhild speaks again, she gives Sigurth a choice of speech or silence and names their fated trouble. He says he will not flee and asks only for her loving word.", "The missing stanzas cannot be reconstructed from those lines; this meeting leaves the weight of a choice." ) }),
            scene("norse-sigrdrifumol", ["counsels", "fragment"], { title: { "zh-Hant": "殘頁前的十一條箴言", en: "Eleven Counsels before the Missing Leaf" }, narrative: sceneNarrative("隨後是一連串關於親族、誓言、酒後爭吵、敵人與葬禮的箴言。它們在手稿裡接著這段相遇，卻不像從同一個情節自然長出。", "第 29 節後的頁葉已失去，後補詩節也沒有讓結尾重現。這裡不替沉默寫出一個圓滿結局。", "西古爾德與布倫希爾德的話停在殘篇邊緣；後面的故事要由其他詩歌各自說下去。", "What follows is a sequence of counsels about kin, oaths, drunken quarrels, enemies, and burial. They come after the meeting in the manuscript but do not grow naturally from one shared plot.", "The leaf after stanza 29 is lost, and later added verses do not restore the ending. This scene does not write a rounded ending into the silence.", "Sigurth and Brynhild’s words stop at the fragment’s edge; other lays must tell what follows in their own ways." ) }),
          ],
        },
        {
          id: "norse-hyndluljoth-route",
          order: 25,
          title: { "zh-Hant": "海因德拉之歌：奧塔爾的家系賭注", en: "Hyndluljoth: Ottar’s Ancestry Wager" },
          summary: {
            "zh-Hant": "芙蕾雅在夜裡請海因德拉說出奧塔爾的家系；時間線只走第1至29節，停在《短女巫預言》之前。",
            en: "Freyja asks Hyndla at night to name Ottar’s ancestry; this route follows only stanzas 1–29 and stops before the Short Voluspo.",
          },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: {
            "zh-Hant": "按貝洛斯英譯《海因德拉之歌》第1至29節的對話與誦說安排；海因德拉的指控維持為詩中說法，不當作穩定傳記事實。",
            en: "Ordered by the dialogue and recital in Bellows’s Hyndluljoth, stanzas 1–29; Hyndla’s accusation remains a claim inside the poem, not stable biography.",
          },
          segments: [
            scene("norse-hyndluljoth", ["freyja-calls-from-cave", "hyndla-sees-companion"], {
              title: { "zh-Hant": "洞口的召喚與爭執", en: "A Call and a Dispute at the Cave" },
              narrative: sceneNarrative(
                "芙蕾雅在夜裡來到海因德拉的洞前，要她同行。海因德拉一醒來便把目光放到芙蕾雅身旁的坐騎與同行者上。",
                "她說那不是單純的野豬，而是奧塔爾；芙蕾雅則指認金鬃的希爾迪斯維尼，並沒有接受這個說法。",
                "這段爭執不是替讀者解謎的定論。時間線把海因德拉的話留在她夜行時的敵意裡，也讓金鬃野豬作為芙蕾雅明說的坐騎留下。",
                "At night Freyja comes to Hyndla’s cave and asks her to travel. As Hyndla wakes, her attention goes at once to the mount and companion beside Freyja.",
                "She says the figure is not simply a boar but Ottar; Freyja instead identifies the gold-bristled Hildisvini and does not accept the charge.",
                "The dispute is not a final solution to a riddle. This route keeps Hyndla’s words within their hostile night-time setting while retaining the gold-bristled boar as Freyja’s stated mount.",
              ),
            }),
            scene("norse-hyndluljoth", ["wager-needs-ancestry", "paternal-line-recited"], {
              title: { "zh-Hant": "賭注要一條父系", en: "The Wager Requires a Paternal Line" },
              narrative: sceneNarrative(
                "芙蕾雅說奧塔爾正為父親留下的財富與自己的出身同安甘蒂爾打賭，因此要海因德拉替他記起名字。",
                "海因德拉由奧塔爾說到父親因斯坦，再倒數老阿爾夫、烏爾夫、賽法里與紅髮斯萬。名字在這裡不是裝飾，而是一條可被逐代檢查的父系路線。",
                "詩沒有提供一場法庭裁決；它展示的是為了賭注而把家系說出來的過程。這條時間線也不把同名人物同其他英雄故事自動合併。",
                "Freyja says Ottar has wagered with Angantyr over inherited wealth and his descent, so she asks Hyndla to recall names for him.",
                "Hyndla moves from Ottar to his father Instein, then back through Alf the Old, Ulf, Saefari, and Svan the Red. The names are not decoration but a paternal line that can be followed generation by generation.",
                "The poem supplies no courtroom verdict; it shows the act of speaking a genealogy for a wager. This route likewise does not automatically merge homonymous figures with other heroic stories.",
              ),
            }),
            scene("norse-hyndluljoth", ["maternal-line-recited", "heroic-kin-names"], {
              title: { "zh-Hant": "母系與更遠的英雄名字", en: "Maternal Kin and More Distant Heroic Names" },
              narrative: sceneNarrative(
                "家系並未只沿父方而行。海因德拉接著說出赫萊迪絲，以及她的父親弗羅西和母親弗里奧特。",
                "之後她把更長的英雄名單連到奧塔爾的親屬關係。這使奧塔爾的主張顯得更廣，卻不等於詩已逐一證明每一條遙遠連線。",
                "近親關係可以逐代看清；更遠的英雄名字只在吟誦中延展，不必替每一個名字定出唯一的家譜。",
                "The line does not run only through fathers. Hyndla next names Hledis, then Hledis’s father Frothi and mother Friaut.",
                "She then joins a longer list of heroic names to Ottar’s kin. That broadens his claim without showing that every distant connection has been proved one by one.",
                "The close kin can be followed generation by generation; the more distant heroic names remain part of the recital, without forcing each one into a single fixed genealogy.",
              ),
            }),
            scene("norse-hyndluljoth", ["genealogy-closes", "stop-before-short-voluspo"], {
              title: { "zh-Hant": "名字說到可用作證明之處", en: "Names Reach the Point of Proof" },
              narrative: sceneNarrative(
                "父母與祖先被說出口後，奧塔爾的出身便從一個主張變成他能拿去支持賭注的證明。詩在此處仍把壓力放在記憶與背誦，而不是在賭局結果上。",
                "這條夜行至第29節為止。接在後面的《短女巫預言》有自己的材料與傳本問題，並不被收作這個賭注的下一幕。",
                "因此結尾保留一個有意識的界線：我們知道海因德拉替奧塔爾說了甚麼，卻不把緊接的另一首詩，或其他傳統的結果，當成這段對話必然延續。",
                "Once parents and ancestors have been spoken, Ottar’s descent becomes proof he can use to support the wager. The pressure remains on memory and recital rather than on a reported result of the bet.",
                "This night route ends with stanza 29. The following Short Voluspo has its own material and manuscript problems, so it is not treated as the wager’s next scene.",
                "The ending therefore keeps a deliberate boundary: we know what Hyndla says for Ottar, but do not make the following poem or outcomes from other traditions a necessary continuation of this dialogue.",
              ),
            }),
          ],
        },
        {
          id: "norse-reginsmol-route",
          order: 26,
          title: { "zh-Hant": "雷金之歌：黃金、劍與斷裂的徵兆", en: "Reginsmol: Gold, Sword, and Broken Omens" },
          summary: { "zh-Hant": "雷金講述受詛黃金與家族仇怨；詩又插入航海與戰鬥材料，最後才把西格爾德推回法夫尼爾。", en: "Regin recounts cursed gold and family grievance; the poem then breaks into voyage and battle material before returning Sigurth to Fafnir." },
          chronologyMode: "variant",
          coverageStatus: "complete",
          coverageNote: { "zh-Hant": "依〈雷金之歌〉引言、散文與第 1–26 節的編者次序。引言明說數組詩節可能來自不同作品，特別是希尼卡爾與洪丁之戰的材料。", en: "Ordered by the compiler’s sequence in Reginsmol’s introduction, prose, and stanzas 1–26. The introduction says several groups may come from other poems, especially the Hnikar and Hunding battle material." },
          segments: [
            scene("norse-reginsmol", ["regin-remembers", "andvari-captured"], { title: { "zh-Hant": "雷金把黃金的舊事交給西格爾德", en: "Regin Gives Sigurth the Gold’s Old Story" }, narrative: sceneNarrative("雷金告訴西格爾德，奧特爾死後，洛基曾用羅恩的網捕住化作梭魚的安德瓦利。安德瓦利交出黃金，卻想把一枚戒指藏下來。", "洛基連戒指也奪走。雷金講這段話時，法夫尼爾不再只是盤踞荒原的龍，而是同一份黃金牽出的兄弟。", "故事把英雄尚未拔劍以前的危險放在家族記憶裡。", "Regin tells Sigurth that after Otr’s death, Loki caught Andvari in pike form with Ran’s net. Andvari gives up the gold but tries to hide one ring.", "Loki takes the ring as well. As Regin tells it, Fafnir is no longer only a dragon on the heath but a brother bound to the same gold.", "Before the hero draws his sword, danger already lives in family memory." ) }),
            scene("norse-reginsmol", ["andvari-curse", "hreidmar-killed"], { title: { "zh-Hant": "詛咒進入赫雷德瑪爾家", en: "The Curse Enters Hreithmar’s House" }, narrative: sceneNarrative("安德瓦利退回石洞前，咒罵黃金會帶來死亡；這一節在詩中雖由他說出，卻帶着來自別處的痕跡。", "賠償金送到赫雷德瑪爾家後，法夫尼爾與雷金要求分產。赫雷德瑪爾拒絕，法夫尼爾便在父親熟睡時刺死他，獨佔黃金。", "黃金還未落到西格爾德手中，父子之間已先為它流血。", "Andvari withdraws before his rocky cave and curses the gold to bring death; although the poem gives him the line, it bears marks of another source.", "When the ransom reaches Hreithmar’s house, Fafnir and Regin demand shares. Hreithmar refuses, and Fafnir stabs his sleeping father and takes the gold alone.", "Before the gold reaches Sigurth, father and son have already bled for it." ) }),
            scene("norse-reginsmol", ["inheritance-refused", "gram-and-fafnir", "father-vengeance"], { title: { "zh-Hant": "格拉姆已成，西格爾德卻先要復仇", en: "Gram Is Made, but Sigurth Demands Vengeance First" }, narrative: sceneNarrative("雷金向法夫尼爾討不回父產，只能把這份仇怨帶到西格爾德身邊。他造出格拉姆，劍能斷開水上的羊毛，也能劈開鐵砧。", "他催西格爾德去殺法夫尼爾，西格爾德卻說自己不能先追逐紅金；洪丁之子若見他不替父親報仇，必會嘲笑他。", "屠龍被暫時放下，這一串材料轉到海上的風暴與戰鬥徵兆。", "Regin cannot recover his inheritance from Fafnir, so he brings that grievance to Sigurth. He makes Gram, a sword that cuts wool on the water and cleaves an anvil.", "He urges Sigurth to kill Fafnir, but Sigurth says he cannot pursue red gold first; Hunding’s sons would mock him if he did not avenge his father.", "Dragon-slaying is set aside for a time, and the material turns to storm at sea and battle omens." ) }),
            scene("norse-reginsmol", ["hnikar-and-battle"], { title: { "zh-Hant": "希尼卡爾上船，雷金再把他推向龍", en: "Hnikar Boards; Regin Sends Him Back toward the Dragon" }, narrative: sceneNarrative("船在風暴中受困時，山上的人自稱希尼卡爾，登船同行，告訴西格爾德烏鴉、狼與敵人的徵兆。", "隨後的散文和詩節把洪丁之子的死亡歸到西格爾德；但這段用語與赫爾吉故事相近，不能把它當作毫無疑問的一段西格爾德經歷。", "戰鬥一結束，雷金立刻再催他去對付法夫尼爾。詩就在屠龍真正開始之前停下。", "When the ship is caught in storm, a man on the mountain calls himself Hnikar, boards, and tells Sigurth of ravens, wolves, and signs before battle.", "The following prose and stanzas give the deaths of Hunding’s sons to Sigurth, but their language is close to Helgi material and cannot be treated as an unquestioned episode of his life.", "As the battle ends, Regin again urges him toward Fafnir. The poem stops before dragon-slaying truly begins." ) }),
          ],
        },
      ],
    },
  ],
};

export const timelineProgrammeByDataset: Record<string, TimelineProgramme> = {
  "greek-mythology-core": greekCoreEightAgesProgramme,
  "norse-mythology-fenrir": norseStoryModeProgramme,
};

export function timelineProgrammeForDataset(
  dataset: string,
): TimelineProgramme | undefined {
  return timelineProgrammeByDataset[dataset];
}
